export type HelpBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string; level?: 2 | 3 }
  | { type: 'list'; items: string[] }
  | { type: 'ordered-list'; items: string[] }
  | { type: 'note'; text: string; variant?: 'info' | 'warning' | 'tip' }
  | { type: 'image'; src: string; alt: string; caption?: string }
  // Видеоинструкция: файл кладём в frontend/public/help-media/ (в git не коммитим,
  // на прод — rsync), src = "/help-media/имя.mp4". poster — кадр-превью (опц.).
  | { type: 'video'; src: string; caption?: string; poster?: string }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'divider' }
  | { type: 'steps'; steps: { title: string; description: string }[] }

export type HelpArticle = {
  slug: string
  category: string
  title: string
  description: string
  icon: string
  updatedAt: string
  blocks: HelpBlock[]
}

export type HelpCategory = {
  key: string
  title: string
  icon: string
  description: string
  color: string
}

export const HELP_CATEGORIES: HelpCategory[] = [
  { key: 'portal', title: 'Портал', icon: '🏠', description: 'Навигация, настройки, профиль', color: '#8B5CF6' },
  { key: 'creator-reports', title: 'Маркетинг', icon: '🎬', description: 'Отчёты создателей: креаторы, публикации, метрики', color: '#EC4899' },
  { key: 'wb-analytics', title: 'Продажи', icon: '📊', description: 'Отчёты, планирование, финансы, склад', color: '#0EA5E9' },
  { key: 'rd', title: 'R&D', icon: '🔬', description: 'Отбор новинок, заказы, логистика, себестоимость', color: '#14B8A6' },
  { key: 'telegram', title: 'Telegram-платформа', icon: '🤖', description: 'Боты, сценарии, рассылки, розыгрыши', color: '#10B981' },
  { key: 'admin', title: 'Администрирование', icon: '⚙️', description: 'Пользователи, права, отделы', color: '#F59E0B' },
  { key: 'hr', title: 'HR', icon: '🧑‍💼', description: 'Сотрудники, оргструктура, ФОТ, отпуска', color: '#F43F5E' },
]

// Текст всех блоков статьи одной строкой — для полнотекстового поиска и оценки времени чтения
export function articlePlainText(article: HelpArticle): string {
  return article.blocks
    .map((b) => {
      switch (b.type) {
        case 'paragraph':
        case 'note':
        case 'heading':
          return b.text
        case 'list':
        case 'ordered-list':
          return b.items.join(' ')
        case 'table':
          return [...b.headers, ...b.rows.flat()].join(' ')
        case 'steps':
          return b.steps.map((s) => `${s.title} ${s.description}`).join(' ')
        case 'video':
          return b.caption ?? ''
        default:
          return ''
      }
    })
    .join(' ')
}

// Оценка времени чтения (~170 слов/мин), минимум 1 минута
export function readingMinutes(article: HelpArticle): number {
  const words = articlePlainText(article).split(/\s+/).filter(Boolean).length
  return Math.max(1, Math.round(words / 170))
}

export const HELP_ARTICLES: HelpArticle[] = [
  {
    slug: 'calculator-acceptance',
    category: 'rd',
    title: 'Приёмка в калькуляторе прибыли',
    description: 'Почему приёмку приходится вводить руками, чем отличаются ФБС и ФБО и как считается коэффициент слота.',
    icon: '📦',
    updatedAt: '2026-08-12',
    blocks: [
      { type: 'note', variant: 'warning', text: 'До 12.08.2026 калькулятор обнулял приёмку при выборе склада, поэтому во всех расчётах «Поиска новинок» она была нулевой. Себестоимость выходила заниженной на величину приёмки. Если пересчитываете старый расчёт — заполните эти поля заново.' },
      { type: 'heading', text: 'Почему вручную', level: 2 },
      { type: 'paragraph', text: 'Логистику и хранение портал берёт из тарифов WB автоматически, а приёмки в этих тарифах просто нет — WB её там не публикует. Поэтому два поля приёмки заполняются руками, как и в «Себестоимости».' },
      { type: 'heading', text: 'Два вида приёмки', level: 2 },
      { type: 'list', items: [
        '«Приёмка ФБС, ₽/шт» — когда товар едет со своего склада. Ставка фиксированная за единицу, у нас это 10 ₽.',
        '«Приёмка ФБО, ₽/л» — когда товар сдаётся на склад WB. Считается за литр, поэтому итог зависит от габаритов упаковки.',
      ] },
      { type: 'heading', text: 'Коэффициент слота', level: 2 },
      { type: 'paragraph', text: 'Множитель к приёмке ФБО, который WB меняет каждые несколько часов в зависимости от загрузки склада. Смотреть его нужно в кабинете WB при планировании поставки.' },
      { type: 'list', items: [
        '0 — приёмка бесплатная (WB сам заинтересован в поставке на этот склад).',
        '1 — обычная стоимость без наценки.',
        '2 и выше — во столько раз дороже.',
      ] },
      { type: 'paragraph', text: 'Итог считается как «ставка за литр × объём × коэффициент» и показан рядом, менять его напрямую не нужно. Пример: упаковка 30×20×10 см — это 6 литров; при ставке 5 ₽ и коэффициенте 2 приёмка ФБО составит 60 ₽.' },
      { type: 'note', variant: 'info', text: 'В блоке «Структура» приёмка показана двумя отдельными строками — ФБС и ФБО, — чтобы было видно, за что именно платим. Строка с нулём не показывается.' },
    ],
  },
  {
    slug: 'passwords-vault',
    category: 'admin',
    title: 'Пароли и доступы — общие учётки компании',
    description: 'Где хранятся общие логины, пароли и токены, как выдать доступ сотруднику и как посмотреть, кто чем пользовался.',
    icon: '🔑',
    updatedAt: '2026-08-11',
    blocks: [
      { type: 'paragraph', text: 'Раздел заменяет отдельный сервис для хранения паролей: общие учётки компании лежат прямо в портале. Открывается из бокового меню: «Настройки» → «Пароли и доступы».' },
      { type: 'note', variant: 'tip', text: 'Главное отличие от прежнего сервиса: доступ невозможно потерять. Раньше человек забывал свой PIN и терял всё, что ему выдали. Теперь, если сотрудник забыл пароль от портала, он просто восстанавливает вход — все доступы остаются на месте.' },
      { type: 'heading', text: 'Как устроен экран', level: 2 },
      { type: 'list', items: [
        'Слева — папки. Можно вкладывать одну в другую: например «Кабинеты WB», внутри — по каждому кабинету. Счётчик справа показывает, сколько записей внутри.',
        'Справа — записи выбранной папки. Поиск сверху ищет по названию, логину и ссылке.',
        'Значение всегда закрыто точками. Показать его — глазик, скопировать — кнопка рядом; копировать можно, не показывая.',
        'Показанное значение прячется само через 30 секунд, а скопированное стирается из буфера обмена, чтобы не всплыть при следующей вставке.',
      ] },
      { type: 'heading', text: 'Добавить запись', level: 2 },
      { type: 'steps', steps: [
        { title: 'Нажмите «Запись»', description: 'Кнопка справа сверху. Если сначала выбрать папку, запись сразу попадёт в неё.' },
        { title: 'Заполните название и значение', description: 'Название — то, по чему запись будут искать («Кабинет WB — Титкова»). Тип выбирайте по смыслу: логин с паролем или API-токен.' },
        { title: 'Пароль можно не придумывать', description: 'Кнопка с палочкой справа от поля создаёт случайный пароль из 20 символов.' },
        { title: 'Сохраните', description: 'При изменении записи поле пароля можно оставить пустым — тогда значение не поменяется.' },
      ] },
      { type: 'heading', text: 'Коды двухфакторки', level: 2 },
      { type: 'paragraph', text: 'Если при входе куда-то, кроме логина и пароля, просят шестизначный код с телефона, портал может показывать этот код сам — всем, у кого есть доступ к записи. Тогда за кодом не нужно бегать к одному коллеге, на чьём телефоне он настроен.' },
      { type: 'paragraph', text: 'В поле «Коды двухфакторки» вставьте либо сам секрет из приложения-аутентификатора, либо ссылку вида otpauth://, которая прячется за QR-кодом. Дальше портал показывает код и кружок обратного отсчёта: код меняется каждые 30 секунд.' },
      { type: 'note', variant: 'warning', text: 'Заводите коды только для ОБЩИХ кабинетов. Когда пароль и код лежат в одном месте, второй фактор перестаёт быть вторым — кто получил доступ к записи, проходит вход целиком. Для личных учёток сотрудников код должен оставаться на телефоне.' },
      { type: 'heading', text: 'Выдать доступ сотруднику', level: 2 },
      { type: 'paragraph', text: 'Внизу каждой записи — строка «выдать доступ». Выбранный сотрудник увидит запись у себя в профиле, в блоке «Мои доступы». Крестик рядом с именем забирает доступ обратно.' },
      { type: 'note', variant: 'info', text: 'Доступ можно выдать не на запись, а на целую папку — тогда он распространяется и на вложенные папки. Удобно, когда отделу нужен весь набор учёток сразу.' },
      { type: 'heading', text: 'Журнал', level: 2 },
      { type: 'paragraph', text: 'Кнопка «Журнал» сверху показывает, кто и когда открывал значения, кому выдавали и у кого забирали доступ. Каждый показ пароля попадает туда — это и есть способ понять, кто пользовался учёткой перед тем, как с ней что-то случилось.' },
    ],
  },
  {
    slug: 'my-access',
    category: 'portal',
    title: 'Мои доступы — пароли, выданные лично вам',
    description: 'Где посмотреть логины и пароли от рабочих сервисов и что делать, если доступ перестал открываться.',
    icon: '🔐',
    updatedAt: '2026-08-11',
    blocks: [
      { type: 'paragraph', text: 'Общие рабочие учётки — кабинеты, сервисы, почты — хранятся в портале. То, что выдали лично вам, лежит в вашем профиле: нажмите на своё имя или фото в углу и выберите «Профиль», блок «Мои доступы» — в правой колонке.' },
      { type: 'note', variant: 'info', text: 'Если блока нет — значит вам пока ничего не выдали. Напишите администратору, какой доступ нужен для работы.' },
      { type: 'heading', text: 'Как пользоваться', level: 2 },
      { type: 'list', items: [
        'Пароль закрыт точками. Глазик показывает его, соседняя кнопка копирует — копировать можно, не показывая, чтобы пароль не светился на экране рядом с коллегами.',
        'Показанный пароль прячется сам через 30 секунд.',
        'Скопированный пароль стирается из буфера обмена, чтобы случайно не вставиться в переписку.',
        'Если у записи есть шестизначный код для входа, он показывается там же с кружком обратного отсчёта. Код меняется каждые 30 секунд — берите свежий, если осталось меньше пяти.',
      ] },
      { type: 'heading', text: 'Забыли пароль от портала', level: 2 },
      { type: 'paragraph', text: 'Ничего страшного: доступы к этому не привязаны. Восстановите вход в портал обычным способом — все выданные учётки останутся на месте.' },
    ],
  },
  {
    slug: 'kb-settings',
    category: 'admin',
    title: 'База знаний — регламенты и инструкции компании',
    description: 'Где лежат внутренние документы компании, как их читать, создавать и редактировать. Раздел доступен только администраторам платформы.',
    icon: '📚',
    updatedAt: '2026-09-02',
    blocks: [
      { type: 'paragraph', text: 'База знаний — это внутренние документы компании: регламенты работы, должностные инструкции, процессы управления доступами, профайлы сотрудников и инструкции по сервисам. Открывается из бокового меню: «Настройки» → «База знаний».' },
      { type: 'paragraph', text: 'Базу знаний можно открыть прямо из «Кабинета»: в блоке «Быстрые действия» есть кнопка «База знаний» — откроется тот же экран с разделами и читалкой, окном поверх кабинета. Рядом кнопка «Добавить документ» — то же окно создания, документ попадает в командную базу.' },
      { type: 'note', variant: 'warning', text: 'Раздел закрытый: и вкладка, и сами документы доступны только администратору платформы. У остальных сотрудников «Настройки» не открываются, а прямой запрос к документам ничего не возвращает.' },
      { type: 'heading', text: 'Как устроен экран', level: 2 },
      { type: 'list', items: [
        'Слева — разделы (например «Должностные инструкции»), внутри раздела документы деревом: документ может быть и статьёй, и папкой с вложенными документами одновременно.',
        'Стрелка слева от названия разворачивает вложенные документы, счётчик справа показывает, сколько их внутри.',
        'Справа — читалка: полный текст выбранного документа со всеми картинками, заголовками, списками и таблицами.',
        'На телефоне список и текст показываются по очереди: выбрали документ — открылся текст, стрелка «назад» слева от заголовка возвращает к списку.',
      ] },
      { type: 'heading', text: 'Создать и отредактировать', level: 2 },
      { type: 'steps', steps: [
        { title: 'Новый документ', description: 'Кнопка «Документ» в шапке — создаёт документ на верхнем уровне. Раздел можно вписать вручную или выбрать из уже существующих.' },
        { title: 'Документ внутри раздела', description: '«+» справа от названия раздела при наведении — раздел подставится сам.' },
        { title: 'Вложенный документ', description: '«+» справа от документа в дереве — новый документ станет вложенным, область и раздел унаследуются от родителя.' },
        { title: 'Правка', description: 'Кнопка «Редактировать» над текстом открывает редактор: заголовки, списки, чек-листы, цвет, выделение, ссылки и картинки. Картинка вставляется кнопкой на панели и хранится в портале — снаружи по ссылке она не откроется.' },
        { title: 'Удаление', description: 'Кнопка «Удалить» спрашивает подтверждение. Документ, внутри которого есть вложенные, удалить нельзя — сначала перенесите или удалите вложенные.' },
      ] },
      { type: 'note', variant: 'tip', text: 'Иконку документа (эмодзи или картинку) можно задать в редакторе — квадрат слева от заголовка. С иконками дерево читается заметно быстрее.' },
    ],
  },
  {
    slug: 'hr-module',
    category: 'hr',
    title: 'HR — справочник, оргструктура и отпуска',
    description: 'Единый модуль HR: сотрудники, оргструктура и ФОТ, найм, онбординг и офбординг, документы, отпуска.',
    icon: '🧑‍💼',
    updatedAt: '2026-08-25',
    blocks: [
      { type: 'paragraph', text: 'Модуль HR собирает в одном месте то, что раньше жило в отдельных Google-таблицах: единый справочник сотрудников, оргструктуру с ФОТ и график отпусков. Открывается из отдела «HR» и содержит пять вкладок.' },
      { type: 'note', variant: 'tip', text: 'Дальше по статье к каждой вкладке есть короткое видео с голосовым пояснением — можно просто посмотреть их подряд и начать работать. Суммы, телефоны и почты на видео и скриншотах размыты специально.' },
      { type: 'video', src: '/help-media/hr-dashboard.mp4', poster: '/help-media/hr-dashboard-poster.jpg', caption: 'Дашборд: ключевые цифры, ФОТ по направлениям, ближайшие отпуска и «Люди в движении»' },
      { type: 'heading', text: 'Вкладки', level: 2 },
      { type: 'list', items: [
        'Дашборд — ключевые цифры: штат, число отделов, ФОТ за месяц, кто в отпуске сейчас, сколько дней рождения в ближайшие 30 дней, плюс блоки «Ближайшие отпуска», «Люди в движении» и «Дни рождения».',
        'Оргструктура — схема компании сверху вниз: операционное управление → направление → отдел → сотрудники. Карточка открывается кликом, кружок под карточкой сворачивает ветку. Сверху — сводка по компании: штат, направления, отделы, ФОТ и налог.',
        'Найм — четыре среза: «Вакансии» (карточки с мини-воронкой и статусом + тумблер «Архив»), «Кандидаты» (канбан — перетаскивание по этапам Отклик → Собес HR → ТЗ → Собес ЛПР → Оффер → Нанят/Отказ, с AI-скорингом резюме), «Воронка» (конверсии по этапам и таблица стоимости найма) и «Аналитика» (сводка по всем вакансиям за период: воронка, сроки, источники кандидатов).',
        'Оформление — вход и выход человека в одной вкладке, переключатель справа: «Приём» (договор → доступы → акт → КЭДО) и «Увольнение» (заявление → передача дел → отзыв доступов → возврат техники → расчёт → выдача документов → exit-интервью → закрытие карточки). Это пульт: видно, у кого что застряло, и галочки ставятся в один клик, а правится всё в карточке человека — она открывается кликом по имени.',
        'Отпуска — годовой график «сотрудник × 12 месяцев»: полоски отпусков в цвете отдела, вертикаль «сегодня», счётчик дней у каждого имени и подсветка нарушения правила «минимум 3 месяца перерыв». Клик по имени открывает карточку человека.',
      ] },
      { type: 'heading', text: 'Оргструктура: схема компании', level: 2 },
      { type: 'paragraph', text: 'Вкладка «Оргструктура» открывается схемой сверху вниз, как рабочая орг-схема компании: «Операционное управление» → направление (Front office / Back office) → отдел → сотрудники. Внутри отдела люди вложены по полю «Руководитель» из карточки сотрудника: подчинённые встают под своим руководителем. Схема статична и занимает всю ширину страницы: она сама подбирает масштаб под экран, а если не помещается по ширине (телефон, много отделов) — прокручивается вбок.' },
      { type: 'list', items: [
        'Строка над схемой — сводка: сколько в штате и кандидатов, сколько направлений, отделов и руководителей, месячный ФОТ и налог (суммы — только с правом «HR · ФОТ и оклады»).',
        'Тумблер «Подробно» разворачивает карточки людей: ЦКП, задачи роли, ключевая метрика и ссылка на должностную инструкцию. Выбор запоминается до следующего раза.',
        'Кружок под карточкой сворачивает ветку — на нём остаётся число спрятанных людей. Кнопки справа сверху разворачивают всю схему или сворачивают её до отделов.',
        'С правом «HR · редактирование» на верхней грани карточки при наведении появляются две кнопки: «+» заводит нового сотрудника сразу в эту ветку (направление, отдел и руководитель подставляются), корзина упраздняет должность — удаляет карточку из справочника. Если человек увольняется, вместо удаления заведите офбординг: карточка сохранится в истории.',
        'Поиск ищет по имени, должности, отделу, направлению, ЦКП и задачам: находка и путь к ней остаются яркими, остальное приглушается, а холст сам наводится на первое совпадение.',
        'Клик по карточке РАСКРЫВАЕТ её прямо на схеме (как нода в конструкторе ботов): руководитель, оформление, график, контакты, психометрика, ЦКП, задачи, метрика, ссылка на инструкцию, а по праву — ФОТ и мотивация. Соседи раздвигаются, ничего не перекрывается; повторный клик сворачивает карточку, кнопка «Изменить карточку» открывает форму сотрудника.',
        'У пилюли отдела и направления справа — численность ветки и её ФОТ.',
        'Справа в шапке — две кнопки уровня модуля: «Архив» открывает уволенных (их нет на схеме, чтобы не поехали состав и ФОТ) и «Шаблоны договоров» — файлы .docx, по которым генерируются договоры при оформлении.',
      ] },
      { type: 'note', variant: 'tip', text: 'Чтобы в схеме появилась иерархия подчинения, заполняйте в карточке сотрудника поле «Руководитель» — ФИО ровно так, как оно записано у руководителя в справочнике. Сотрудник без направления и отдела считается руководством компании и встаёт над направлениями.' },
      { type: 'note', variant: 'tip', text: 'ЦКП, задачи, ключевую метрику и ссылку на должностную инструкцию заполняют в карточке сотрудника (клик по человеку на схеме → «Изменить карточку» → вкладка «Роль») — они же показываются на схеме в режиме «Подробно».' },
      { type: 'video', src: '/help-media/hr-org.mp4', poster: '/help-media/hr-org-poster.jpg', caption: 'Оргструктура: схема компании и карточка сотрудника' },
      { type: 'heading', text: 'Люди в движении', level: 2 },
      { type: 'paragraph', text: 'Блок на дашборде собирает все текущие переходы людей в одном месте: кто вышел на работу и ещё оформляется, кто в отпуске или уходит в него, у кого идёт увольнение и кто уже принял оффер. Переключатель справа сверху (7 / 30 / 90 дней) задаёт глубину сразу в обе стороны: назад — сколько человек пришло и ушло за период, вперёд — какие события ждут впереди. Рядом кнопка обновления.' },
      { type: 'list', items: [
        'Шесть плиток слева — движение за период: «Оформляется» (незакрытые чек-листы онбординга), «Пришло», «Ушло», «Нетто» (пришло минус ушло), «Текучесть» (доля уволившихся от средней численности) и «В движении» (сколько разных людей во всех потоках сразу).',
        'Ряд вкладок «Все · Новички · Отпуска · Уходят · Офферы» переключает поток; число рядом с названием — сколько там карточек. «Все» — общая лента, отсортированная по дате события.',
        'На карточке человека — имя, должность и отдел, готовая подпись состояния («Выход через 3 дня», «В отпуске, вернётся через 5 дней», «Последний день был 2 дня назад»), дата события и прогресс чек-листа приёма или увольнения. Клик по карточке открывает карточку сотрудника (у кандидата с оффером её ещё нет — открывать нечего).',
        'Красная карточка со значком ⚠ — зависший процесс: дата прошла, а чек-лист онбординга или увольнения так и не закрыт. Сколько таких всего — в строке-предупреждении над потоками.',
      ] },
      { type: 'note', variant: 'tip', text: 'Блок виден всему HR-отделу по обычному праву «HR»: в нём только оргданные — имя, должность, отдел и даты событий. Ни ФОТ, ни личное дело сюда не попадают.' },
      { type: 'heading', text: 'Дни рождения', level: 2 },
      { type: 'paragraph', text: 'На дашборде есть блок «Дни рождения» — кого поздравлять в ближайшее время. Карточки отсортированы «кто ближе»: именинник сегодняшнего дня подсвечивается розовым и значком торта, ближайшая неделя — оранжевым, остальные — фиолетовым. Переключатель справа меняет горизонт: 30 дней / 90 дней / год (по умолчанию открывается наименьший непустой). Рядом в KPI-строке — кубик «ДР в 30 дней».' },
      { type: 'list', items: [
        'Берутся только активные сотрудники: дата из карточки сотрудника, а если там пусто — из профиля пользователя портала (такие подписаны «из профиля»).',
        'Год рождения на дашборд не передаётся: день и месяц видит весь HR, а «сколько исполняется лет» — только с правом «HR · Личные дела».',
        'Если список пуст — значит дата рождения ещё нигде не заполнена: добавьте её в карточке сотрудника или попросите людей указать её в своём профиле.',
      ] },
      { type: 'heading', text: 'Аналитика воронки найма', level: 2 },
      { type: 'heading', text: 'Стоимость найма', level: 3 },
      { type: 'paragraph', text: 'Блок под воронкой считает, во что обошёлся подбор. Строка — позиция; «Разм.» — сколько раз её публиковали на hh (каждая публикация приходит из hh отдельной записью), «Просмотры» и «Отклики» — суммарно по этим публикациям, «Нанято» — сколько кандидатов дошло до статуса «Нанят», «Цена найма» — стоимость, делённая на нанятых. Просмотры рядом с откликами показывают, окупалось ли размещение: много показов при малом отклике — вопрос к тексту вакансии или вилке.' },
      { type: 'note', variant: 'info', text: 'Сумму размещения hh в API не отдаёт — ни за прошлые публикации, ни за текущие. Поэтому цену задаёте вы: кнопка «Цены размещений» открывает прайс по типам публикации («Стандарт», «Стандарт плюс», «Премиум»), напротив каждого — сколько таких размещений уже было. Стоимость позиции = цена типа × число публикаций. Кнопка «Взять из hh» подтягивает то немногое, что hh показывает — цены доступных апгрейдов активных вакансий; остальные тарифы вносятся руками. Если цена типа не задана, его размещения в сумму не идут, и рядом со стоимостью показывается «сколько из скольких» учтено.' },
      { type: 'note', variant: 'warning', text: 'Прайс и суммы видны только с правом «Финансы HR». Без него остаются размещения, просмотры, отклики и нанятые.' },
      { type: 'paragraph', text: 'Под-вкладка «Аналитика» на вкладке «Найм» показывает подбор цифрами: сколько кандидатов на каждом этапе, где теряем людей, как быстро доходим до оффера и какие каналы реально приводят сотрудников. Сверху — окно отчёта (30 дней / 90 дней / год / всё время) и фильтр по вакансии (по умолчанию — все).' },
      { type: 'list', items: [
        'Пять кубиков-метрик: активные кандидаты, конверсия отклика в оффер, среднее время «отклик → оффер», средний срок закрытия вакансии и число открытых вакансий. У каждой — значок «?» с формулой и источником.',
        'Воронка по этапам: столько-то «дошли» до этапа (включая ушедших дальше), «сейчас» на полосе — кто стоит на нём в данный момент, справа конверсия от предыдущего этапа и среднее время на этапе.',
        'Источники кандидатов — пончик и список «сколько пришло / сколько из них нанято». Источник указывается в карточке кандидата (поле «Источник»); отклики с hh.ru синхронизация помечает сама.',
        'Таблица «Вакансии периода» — кандидаты, офферы, найм, срок закрытия и просрочка по плановой дате; со стандартными сортировками, фильтрами и итогами.',
      ] },
      { type: 'video', src: '/help-media/hr-hiring.mp4', poster: '/help-media/hr-hiring-poster.jpg', caption: 'Найм: срезы вкладки, канбан кандидатов и карточка с полным резюме' },
      { type: 'image', src: '/help-media/hr-hiring-kanban.jpg', alt: 'Канбан кандидатов на вкладке «Найм»', caption: 'Канбан: кандидаты перетаскиваются между этапами, счётчик над колонкой — сколько человек стоит на этапе' },
      { type: 'note', variant: 'warning', text: 'Метрики ВРЕМЕНИ (время на этапе, «отклик → оффер») считаются по журналу смен стадии, который включился вместе с этой вкладкой. Задним числом истории нет: сразу после запуска они пустые и наполняются по мере того, как вы двигаете кандидатов по канбану. Счётчики, конверсии и источники работают сразу.' },
      { type: 'heading', text: 'hh.ru и архив вакансий', level: 2 },
      { type: 'paragraph', text: 'Подключение кабинета работодателя живёт в «Настройки → Настройки модулей → Найм: подключение hh.ru» (только администратору портала); частота автоматической синхронизации — там же, в разделе «Синхронизации». Из вкладки «Найм» кнопку убрали: реквизиты приложения hh — настройка платформы, а не повседневная работа рекрутёра. Синхронизация тянет из hh активные вакансии вместе с откликами по ним — отклики становятся карточками кандидатов в канбане на стадии «Отклик» — и отдельно забирает архив кабинета: снятые с публикации вакансии с датой ухода в архив и числом откликов. Вакансии собираются по ВСЕМ менеджерам кабинета, а не только по тому, чьей учётной записью подключён портал: hh отдаёт список по одному менеджеру, и раньше вакансия, заведённая коллегой, в портал не приезжала.' },
      { type: 'list', items: [
        'Тумблер «Архив» справа над карточками вакансий переключает список: выключен — текущие вакансии, включён — архивные. Рядом с тумблером число архивных карточек.',
        'Архивные карточки показывают, когда вакансия ушла в архив и сколько было откликов. Внутрь них не проваливаются: карточек кандидатов по архиву нет — у одной такой вакансии бывает больше тысячи откликов, выгружать их целиком нет смысла.',
        'В аналитику найма, воронку и выбор вакансии архив не попадает — иначе сотня старых позиций перекосила бы счётчики и средний срок закрытия.',
        'Убрать в архив вручную можно любую вакансию: откройте её карандашом и включите тумблер «Архив» в форме. Вернуть обратно — тем же тумблером.',
      ] },
      { type: 'note', variant: 'tip', text: 'Если вакансия из портала снова опубликована на hh, ближайшая синхронизация сама вернёт её из архива в текущие. Дата отклика берётся из hh, поэтому импортированная история не «схлопывается» в день синхронизации.' },
      { type: 'heading', text: 'Резюме кандидата', level: 2 },
      { type: 'paragraph', text: 'В карточке кандидата, пришедшего с hh.ru, есть сворачиваемый блок «Резюме с hh.ru». Он показывает полное резюме прямо в портале: фото, ФИО, должность, город и возраст, стаж, зарплатные ожидания, гражданство, контакты, весь опыт работы с описаниями, навыки, образование, языки и ссылку на оригинал.' },
      { type: 'list', items: [
        'Резюме подтягивается при первом раскрытии блока, а не на синхронизации: у активной вакансии сотни откликов, и тянуть все резюме сразу — сотни лишних обращений к hh.',
        'Загруженное резюме сохраняется в портале: повторные открытия мгновенные и работают, даже если hh недоступен. Ссылка «обновить» перечитывает его заново.',
        'Резюме видно всем, у кого есть доступ к вкладке «Найм» — отдельного права не требуется.',
      ] },
      { type: 'image', src: '/help-media/hr-candidate-resume.jpg', alt: 'Развёрнутый блок «Резюме с hh.ru» в карточке кандидата', caption: 'Блок «Резюме с hh.ru» разворачивается по клику: опыт работы, навыки, образование и языки — прямо в портале' },
      { type: 'heading', text: 'Оформление: приём и увольнение', level: 2 },
      { type: 'paragraph', text: 'Раньше это были две соседние вкладки, «Онбординг» и «Офбординг». Теперь одна — «Оформление», с переключателем «Приём / Увольнение» справа сверху: вход человека в компанию и выход из неё — один процесс, просто в разные стороны. Оба режима устроены одинаково: строка на человека, колонки — шаги, клик по ячейке отмечает шаг выполненным, справа прогресс. Сразу видно, у кого оформление или увольнение зависло.' },
      { type: 'note', variant: 'tip', text: 'Вкладка — пульт наблюдения: смотреть и быстро проставлять галочки. Всё, что касается конкретного человека — причина увольнения, даты, преемник, документы — правится в его карточке: клик по имени в строке открывает её сразу на вкладке «Оформление».' },
      { type: 'note', variant: 'warning', text: 'Видео ниже сняты до объединения вкладок: на них «Онбординг» и «Офбординг» ещё показаны отдельными пунктами. Сами шаги и работа с таблицей не изменились — изменилось только то, что обе таблицы теперь живут под одной вкладкой «Оформление».' },
      { type: 'video', src: '/help-media/hr-onboarding.mp4', poster: '/help-media/hr-onboarding-poster.jpg', caption: 'Приём: чек-лист оформления новичка' },
      { type: 'video', src: '/help-media/hr-offboarding.mp4', poster: '/help-media/hr-offboarding-poster.jpg', caption: 'Увольнение: зеркальный чек-лист — от заявления до закрытия карточки' },
      { type: 'heading', text: 'Увольнение и документы сотрудника', level: 2 },
      { type: 'paragraph', text: 'Процесс увольнения заводится двумя способами: кнопкой «Увольнение» на вкладке «Оформление» или в карточке сотрудника — вкладка «Оформление», кнопка «Оформить увольнение». Сотрудник выбирается только из справочника — увольнять «на имя», без карточки, больше нельзя (иначе шаг «Карточка закрыта» некому переводить в статус «Уволен»). В форме указываются причина, дата заявления, последний рабочий день и кому передаются дела, а также итоги exit-интервью. Дальше строка живёт как чек-лист: клик по ячейке отмечает шаг, справа виден прогресс, а в строке подсвечивается, сколько осталось до последнего рабочего дня (просроченный — красным).' },
      { type: 'list', items: [
        'Последний шаг «Карточка закрыта» переводит сотрудника в статус «Уволен» в справочнике; снятие галочки возвращает его в штат.',
        'По одному сотруднику может быть только один незавершённый офбординг — повторное «Уволить» из карточки не создаст дубль.',
        'Скрепка в строке открывает документы этого сотрудника — туда загружаются заявление, акт передачи дел и обходной лист.',
        'Уволенный уходит из оргструктуры и из ФОТ, но не пропадает: его карточка со всей историей лежит в «Архиве» — кнопка справа в шапке оргструктуры. Оттуда же его можно вернуть в штат, это снимет шаг «Карточка закрыта».',
        'Процесс, заведённый по ошибке или не на того человека, удаляется в карточке сотрудника — корзина в шапке блока на вкладке «Оформление». Закрытый процесс удалить нельзя: он единственный держит статус «Уволен», поэтому сначала вернуть в штат из архива, потом удалять.',
      ] },
      { type: 'heading', text: 'Карточка сотрудника — всё о человеке в одном окне', level: 2 },
      { type: 'paragraph', text: 'Карточка открывается кликом по человеку в оргструктуре и разложена по внутренним вкладкам, чтобы не быть бесконечной простынёй. Вкладки появляются по смыслу: у новой, ещё не сохранённой карточки «Оформления» нет — чек-листам не к чему прицепиться, пока у человека нет id.' },
      { type: 'list', items: [
        'Основное — ФИО, должность, направление, отдел, статус, руководитель, тип оформления, график, дата выхода и рабочие контакты.',
        'Роль — ЦКП, задачи, ключевая метрика, ссылка на должностную инструкцию, психометрика и мотивация. Это же наполнение видно на схеме в режиме «Подробно».',
        'Оформление — чек-листы приёма и увольнения этого человека: семь шагов новичка и восемь шагов выхода, причина увольнения, даты, преемник и итоги exit-интервью.',
        'Финансы — ФОТ и налог, только с правом «HR · ФОТ и оклады».',
        'Личное дело — паспорт, ИНН, СНИЛС, адрес и реквизиты, плюс сканы документов и договоры. Поля PII требуют права «HR · Личные дела», рабочие бумаги видны всему HR-отделу.',
      ] },
      { type: 'note', variant: 'warning', text: 'Вкладка «Оформление» сохраняет свои галочки и поля СРАЗУ по клику — она правит чек-листы, а не карточку. Кнопка «Сохранить» внизу относится только к остальным вкладкам. Шаг «Карточка закрыта» на этой вкладке переводит человека в статус «Уволен»: он уходит из оргструктуры и ФОТ, но остаётся в истории.' },
      { type: 'paragraph', text: 'В карточке сотрудника есть блок «Документы»: сканы и файлы вместо текстовых полей. Тип документа выбирается из справочника (паспорт, ИНН, СНИЛС, реквизиты, диплом, договор, акт, заявление, акт передачи дел, обходной лист, расчётный лист). Чипсы сверху показывают обязательный комплект — зелёные уже загружены, пунктирные ещё нет. Принимаются PDF, изображения и офисные документы до 25 МБ.' },
      { type: 'note', variant: 'warning', text: 'На видео ниже карточка человека показана на отдельной вкладке «Сотрудники» — её больше нет. Карточка открывается кликом по человеку в оргструктуре (а уволенные — через «Архив» там же); блок документов и генерация договора внутри неё работают ровно так, как на записи, только лежат на вкладке «Личное дело».' },
      { type: 'video', src: '/help-media/hr-staff.mp4', poster: '/help-media/hr-staff-poster.jpg', caption: 'Карточка человека, блок документов и генерация договора по шаблону' },
      { type: 'note', variant: 'warning', text: 'Документы личного дела (паспорт, прописка, ИНН, СНИЛС, реквизиты, договор, акт, расчётный лист) доступны только с правом «HR · Личные дела»: без него они не показываются в списке, не скачиваются и не загружаются — файл вообще не покидает сервер. Рабочие бумаги процесса (заявление, акт передачи дел, обходной лист, NDA, диплом) видны всему HR-отделу.' },
      { type: 'heading', text: 'AI-скоринг резюме', level: 2 },
      { type: 'paragraph', text: 'В канбане кандидатов встроена AI-оценка резюме под вакансию. У каждой вакансии есть поле «Требования к кандидату» (эталон) — по нему модель сравнивает профиль кандидата и выставляет балл соответствия 0–100, рекомендацию (явно подходит / подходит / под вопросом / не подходит), сильные стороны, риски и разбивку по критериям.' },
      { type: 'list', items: [
        'Балл виден прямо на карточке канбана (цветной значок ✦) рядом с ручной оценкой HR.',
        'Открыв карточку кандидата, в блоке «AI-скоринг резюме» можно нажать «Оценить резюме» (или «Переоценить») и увидеть полный разбор.',
        'Кнопка «Скорить всех» в тулбаре «Кандидаты» оценивает разом все ещё не оценённые резюме по выбранной вакансии.',
      ] },
      { type: 'note', variant: 'tip', text: 'AI-оценка — подсказка, а не приговор: финальное решение всегда за HR и ЛПР. Если AI-модель недоступна, показывается упрощённая оценка по совпадению ключевых слов.' },
      { type: 'heading', text: 'График отпусков', level: 2 },
      { type: 'paragraph', text: 'Вкладка «Отпуска» — годовой график: строка на сотрудника, 12 колонок-месяцев, полоска = отпуск по дням (она может переходить из месяца в месяц). Сотрудники сгруппированы по отделам — отдел написан в строке-заголовке группы; цвет аватара и полоски совпадает с цветом отдела, поэтому отделы различаются взглядом.' },
      { type: 'list', items: [
        'Вертикальная линия через весь график — сегодняшний день (ставится точно по дате, а не по середине месяца).',
        'Числа у имени — «X дн · Y ост»: сколько дней отпуска запланировано за год и сколько из них ещё не отгуляно на сегодня. На узком экране те же числа показываются короче — «X/Y».',
        'Наведение на полоску показывает даты начала и конца, длительность и заметку; клик по ней открывает редактирование отпуска (при праве на изменение).',
        'На самой полоске подписаны числа отпуска (например «10–21») — если она достаточно широкая; у совсем коротких отпусков подписи нет, даты видно по наведению.',
        'Значок ⚠ у имени — между двумя отпусками человека меньше 3 месяцев, это нарушение регламента планирования.',
        'Сотрудник в форме отпуска выбирается из справочника — вписать произвольное ФИО нельзя, отдел подставляется из карточки. Отпуск всегда принадлежит конкретному человеку: строк «ничьих» отпусков в графике не бывает.',
      ] },
      { type: 'video', src: '/help-media/hr-vacations.mp4', poster: '/help-media/hr-vacations-poster.jpg', caption: 'Отпуска: годовой график, даты прямо на полоске и линия «сегодня»' },
      { type: 'note', variant: 'warning', text: 'Норма отпуска (сколько дней сотруднику положено за год) в портале не хранится, поэтому «остаток» здесь — это НЕ остаток от нормы, а сколько дней из уже запланированного графика ещё впереди. Всё считается по датам самих отпусков.' },
      { type: 'heading', text: 'Права доступа', level: 2 },
      { type: 'paragraph', text: 'Доступ к модулю и его вкладкам выдаётся как обычно — по вкладкам отдела (просмотр / редактирование / удаление) в «Управлении пользователями». AI-скоринг доступен при праве на редактирование вкладки «Найм».' },
      { type: 'note', variant: 'warning', text: 'Два чувствительных блока закрыты отдельными именными правами (группа «Системные», выдаются поимённо): «HR · ФОТ и оклады» открывает суммы ФОТ и налогов, «HR · Личные дела» — паспорт, ИНН, СНИЛС, реквизиты и адрес. Без права эти поля не показываются и не покидают сервер — по той же логике, что право «Реальная себестоимость» в продажах.' },
    ],
  },

  {
    slug: 'ai-assistant',
    category: 'portal',
    title: 'AI-ассистент — персональный помощник',
    description: 'Чат с AI, который знает вас, вашу базу знаний и умеет объяснять показатели',
    icon: '✨',
    updatedAt: '2026-09-04',
    blocks: [
      { type: 'paragraph', text: 'AI-ассистент доступен всем сотрудникам во вкладке «AI» плавающей круглой кнопки в углу экрана (рядом с чатом, задачами и заявками). Это чат с искусственным интеллектом, но, в отличие от обычного ChatGPT, он знает контекст компании.' },
      { type: 'paragraph', text: 'Прошлые беседы доступны и из чата: в списке разговоров есть кнопка «AI» рядом с «Все», «Каналы» и «Личные» — она показывает все ваши беседы с ассистентом, самые свежие сверху. Нажали на беседу — она открывается там же, где и была; плюс справа заводит новую.' },
      { type: 'note', variant: 'tip', text: 'Не знаете, с чего начать? В новом чате есть готовые подсказки-кнопки («С чего начать работу в портале?», «Как считается маржа?», «Мои задачи на сегодня» и др.) — нажмите любую, и ассистент сразу ответит.' },
      { type: 'heading', text: 'Что он умеет', level: 2 },
      { type: 'list', items: [
        'Знает, кто вы: обращается по имени, учитывает ваш отдел, должность и доступные вам модули.',
        'Понимает, где вы сейчас — вплоть до конкретной вкладки: открыли ассистента на вкладке «Отчёт» отдела Продажи и спросили «что тут показывается / как это устроено» — он поймёт, о каком разделе и вкладке речь, и ответит по ней, не переспрашивая.',
        'Приводит прямо к нужному разделу: когда уместно, под ответом появляется кнопка-переход (например, «Открыть отдел „Продажи“») — нажали и сразу оказались там, без поиска по меню.',
        'Отвечает по базе знаний компании — находит нужную статью и ссылается на неё как на источник.',
        'Объясняет бизнес-показатели: как считается тот или иной показатель и откуда берутся данные.',
        'Показывает ваши личные данные по запросу — активные задачи, счётчики непрочитанного, а на вопрос «что нового за сегодня?» собирает сводку: непрочитанные сообщения (от кого), незакрытые задачи со сроками и (для админов) новые заявки на доработку.',
        'Рассказывает о конкретном товаре: спросите «что с t_denim_klesh/синие?» — ассистент соберёт цену и СПП, себестоимость, комиссию, логистику, хранение и приёмку, маржу с рекламой и без, ДРР, продажи за месяц и остаток (учётный и реально доступный), а также что изменилось между двумя последними закрытыми месяцами.',
        'Считает «а что если»: «что будет с маржой, если поднять цену на 10 %», «если снизить ДРР до 3 %», «если себестоимость станет 950 ₽». Пересчёт идёт по формуле портала — той же, что стоит за целевой ценой в юнит-экономике, — и ассистент показывает, из чего сложился ответ. Спрос, процент выкупа и позицию в выдаче портал не предсказывает, о чём честно предупреждает.',
        'Кратко пересказывает переписку: в открытом диалоге чата есть кнопка «✨ Кратко» — ассистент за секунды выжимает из длинной переписки, о чём договорились и что от вас ждут.',
        'Подсказывает, к кому обратиться: знает команду с должностями и отделами (спросите «кто отвечает за закупки?»), а также помнит статус ваших заявок на доработку.',
        'Видит прикреплённые скрины: можно приложить изображение (кнопкой 📎 или вставить скрин через Ctrl+V) и спросить, что на нём — ассистент разберёт содержимое.',
        'Помогает оформить доработку: когда вы просите составить ТЗ на правку или новую функцию, ответ оформляется по разделам, и под ним появляются кнопки «В доработки» и «Задачей человеку» (на обычных ответах их нет). Клик открывает окно предпросмотра с уже заполненными полями, где вы всё проверяете и правите, и только по кнопке отправляете.',
      ] },
      { type: 'note', variant: 'info', text: 'Мелочи для удобства: ответ можно остановить на полуслове кнопкой ⏹, беседу — переименовать (карандаш в её заголовке), а саму панель — развернуть шире кнопкой в углу, чтобы удобнее читать длинные ответы. Поиск вверху панели работает в списках (люди, задачи, беседы) и прячется, когда открыт конкретный диалог. Недописанное сообщение сохраняется как черновик — вернётесь позже и продолжите с того же места. В браузерах с поддержкой распознавания речи (Chrome/Edge) в поле ввода есть кнопка микрофона — можно надиктовать вопрос голосом.' },
      { type: 'note', variant: 'info', text: 'Ассистент сам работает только на чтение — он отвечает, ищет и показывает, но ничего не создаёт и не меняет в портале. Заявку на доработку или задачу создаёте вы сами кнопкой под ответом, с предпросмотром и подтверждением. Ассистент видит только те данные, к которым у вас есть доступ.' },
      { type: 'heading', text: 'Про расчёты показателей', level: 2 },
      { type: 'paragraph', text: 'Для выверенных показателей ассистент приводит точную формулу и источники данных. Если формула ещё не выверена окончательно, он честно предупредит об этом и посоветует уточнить у аналитика — чтобы вы не приняли решение по неподтверждённым цифрам.' },
      { type: 'note', variant: 'tip', text: 'Если ответа в базе знаний нет, ассистент так и скажет, а не будет выдумывать. Ему можно доверять в том смысле, что он не сочиняет внутренние факты компании.' },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // ОТЗЫВЫ WILDBERRIES
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'wb-reviews',
    category: 'wb-analytics',
    title: 'Отзывы Wildberries — ответы покупателям',
    description: 'Все отзывы обоих кабинетов в одном списке: фильтры, генерация ответа, массовая обработка и история действий',
    icon: '💬',
    updatedAt: '2026-08-24',
    blocks: [
      { type: 'paragraph', text: 'Вкладка **«Отзывы»** в отделе «Продажи» собирает отзывы покупателей из всех подключённых кабинетов Wildberries. Смысл экрана — отвечать быстро и не терять неотвеченные: WB учитывает скорость ответа, а список в личном кабинете WB неудобно фильтровать.' },
      { type: 'heading', text: 'Список и фильтры', level: 2 },
      { type: 'list', items: [
        'Фильтр по кабинету, по оценке (звёзды), по наличию фото и по тому, отвечен отзыв или нет.',
        'Поиск по тексту — если нужно найти конкретную жалобу или упоминание артикула.',
        'Карточка отзыва показывает исходный текст целиком, отдельно «Плюсы» и «Минусы», фото покупателя и товар, к которому отзыв относится.',
      ] },
      { type: 'heading', text: 'Ответ на отзыв', level: 2 },
      { type: 'paragraph', text: 'Ответ можно написать руками или сгенерировать: портал предлагает вариант, вы правите его под себя и отправляете. Сгенерированный текст никуда не уходит без вашего подтверждения — до отправки это черновик.' },
      { type: 'note', variant: 'tip', text: 'Для однотипных отзывов есть массовая обработка: выбираете несколько и отвечаете одним заходом. Всё, что отправлено пачкой, попадает в «Журнал массовых операций» — видно, что именно ушло и когда.' },
      { type: 'heading', text: 'История и разбор ошибок', level: 2 },
      { type: 'paragraph', text: 'У каждого отзыва есть «История действий» — кто и когда отвечал, какие были черновики. Если генерация ответа сработала не так, как ожидалось, «Диагностика генерации» показывает, из чего складывался запрос: это помогает понять, чего не хватило — данных о товаре или настроек тона.' },
      { type: 'note', variant: 'info', text: 'Настройки модуля (тон ответов, шаблоны, подключение кабинетов) — в «Настройки → Отзывы WB», доступны администратору. Синхронизация отзывов идёт фоновой задачей; если отзывы перестали приходить, посмотрите её состояние в «Синхронизациях».' },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────────
  // ЗАПУСК ПРОДУКТА И ТЕПЛОВАЯ КАРТА
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'product-launch',
    category: 'rd',
    title: 'Запуск продукта — тепловая карта готовности',
    description: 'Видно, на каком этапе стоит каждый продукт и что мешает запуску: строки — продукты, колонки — этапы, ячейка — конкретный вопрос',
    icon: '🚦',
    updatedAt: '2026-08-24',
    blocks: [
      { type: 'paragraph', text: 'Модуль «Запуск продукта» отвечает на вопрос «почему товар всё ещё не запущен». Главный экран — **тепловая карта**: строки это продукты категории, колонки — этапы запуска, а ячейка — конкретный вопрос по этапу («есть ли фото», «согласована ли цена»). Ячейка отмечена галочкой или крестом, клик раскрывает подробности и срок.' },
      { type: 'list', items: [
        'Этапы сворачиваются: можно смотреть карту крупно, по этапам, или разворачивать вопросы внутри нужного.',
        'Слева — ABC по **оставшейся скорости запуска**: сумма сроков по невыполненным ячейкам. Наверху оказываются продукты, до запуска которых дальше всего.',
        'У этапа может стоять отметка «Согласовано» — финальная проверка ответственного. Она НЕ блокирует другие этапы: ТЗ уходят фабрикам параллельно, а согласование это штамп проверки, а не шлагбаум.',
      ] },
      { type: 'heading', text: 'Карточка продукта', level: 2 },
      { type: 'paragraph', text: 'Клик по строке открывает карточку: описание, референсы, фото, конкуренты и комментарии по этапам. Здесь же видно, кто отвечает за следующий шаг.' },
      { type: 'note', variant: 'tip', text: 'Набор этапов и вопросов настраивается: если процесс изменился, карту не нужно переделывать заново — достаточно поправить состав ячеек.' },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────────
  // ЭТИКЕТКИ И ЧЕСТНЫЙ ЗНАК
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'label-maker',
    category: 'rd',
    title: 'Этикетки и «Честный знак» — печать',
    description: 'Печать этикеток на товары по данным кабинета: выбор товара, шаблон, быстрый режим для повторной печати',
    icon: '🏷️',
    updatedAt: '2026-08-24',
    blocks: [
      { type: 'paragraph', text: 'Модуль «Этикетки (ЧЗ)» готовит этикетки к печати по товарам кабинета: артикул WB, наименование, штрихкод. Данные берутся из портала, поэтому не нужно переносить их руками из личного кабинета Wildberries.' },
      { type: 'list', items: [
        'Вкладка **«Товары»** — список позиций кабинета с поиском; отмечаете нужные и отправляете на печать.',
        'Вкладка **«Печать»** — предпросмотр и сама печать: видно, как этикетка ляжет на лист.',
        'Режим **«Быстро»** — для повторной печати того же набора без повторного отбора.',
      ] },
      { type: 'note', variant: 'info', text: 'Подключение кабинета и параметры шаблонов — в «Настройки → Этикетки (ЧЗ)». Коды «Честного знака» портал не выпускает: он печатает то, что уже получено, поэтому маркировку заказывайте своим обычным порядком.' },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────────
  // ТРЕКЕР РАЗРАБОТОК
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'dev-tracker',
    category: 'rd',
    title: 'Трекер разработок — что в работе по каждому продукту',
    description: 'Одна таблица: продукт, следующий шаг, срок, что мешает и референсы. Чтобы на планёрке не собирать статусы по чатам',
    icon: '📌',
    updatedAt: '2026-08-24',
    blocks: [
      { type: 'paragraph', text: 'Трекер разработок держит статус по каждому продукту в одной таблице, чтобы не собирать его по переписке. Строка — продукт, и рядом всё, что нужно для планёрки.' },
      { type: 'list', items: [
        '**Следующий шаг** — что делаем прямо сейчас, одной фразой.',
        '**Дедлайн** — к какому числу шаг должен быть закрыт.',
        '**Блокер** — что мешает двигаться: без этого поля трекер превращается в список благих намерений.',
        '**Референсы** — ссылки и картинки-образцы, прямо в строке.',
        '**Комментарий** — что обсудили, чтобы контекст не терялся между встречами.',
      ] },
      { type: 'note', variant: 'tip', text: 'Поля правятся прямо в таблице, без открытия карточки: кликнули по ячейке, написали, ушли с неё — сохранилось.' },
      { type: 'note', variant: 'info', text: 'Это не то же самое, что «Доработки» в командной панели: там заявки на правки ПОРТАЛА, а здесь — работа по товарам компании.' },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────────
  // РАБОТА В КОМАНДЕ: ЧАТ, ЗАДАЧИ, ДОРАБОТКИ
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'teamwork',
    category: 'portal',
    title: 'Работа в команде — чат, задачи и доработки',
    description: 'Круглая кнопка в углу экрана: переписка с коллегами, задачи друг другу, заявки на доработку портала и AI-ассистент — не выходя с рабочего экрана',
    icon: '👥',
    updatedAt: '2026-08-24',
    blocks: [
      { type: 'paragraph', text: 'Всё общение внутри компании живёт в одной панели — круглой кнопке в правом нижнем углу. Она открывается поверх любого экрана: не нужно уходить с отчёта, чтобы написать коллеге или поставить задачу. На кнопке видно число непрочитанного, а при наведении — из чего оно сложилось: сообщения, задачи, доработки.' },
      { type: 'image', src: '/help-media/teamwork-panel.jpg', alt: 'Командная панель со списком коллег', caption: 'Панель открывается поверх рабочего экрана — отчёт остаётся на месте' },
      { type: 'heading', text: 'Пять вкладок панели', level: 2 },
      { type: 'list', items: [
        '**Чат** — переписка один на один с любым сотрудником. Список коллег с должностями, поиск по имени. В диалоге есть кнопка «✨ Кратко» — ассистент за секунды пересказывает длинную переписку: о чём договорились и что от вас ждут.',
        '**Задачи** — поручения между сотрудниками: заголовок, описание, срок, исполнитель. Видно и то, что поставили вам, и то, что поставили вы. Задача закрывается тем, кто её выполнил.',
        '**Доработки** — заявки на правку или новую функцию портала. Опишите, что не так и как должно быть; заявка попадает администратору, а вы видите её статус.',
        '**AI** — встроенный ассистент: знает контекст компании, отвечает по показателям и товарам, показывает ваши задачи и сводку «что нового». Подробно — в статье про AI-ассистента.',
        '**Чаты** — архив переписок из Telegram, если он подключён.',
      ] },
      { type: 'heading', text: 'Кто сейчас в портале', level: 2 },
      { type: 'paragraph', text: 'Рядом с именем коллеги виден кружок присутствия: **зелёный** — человек сейчас в портале, **серый** — был здесь недавно (наведите, чтобы увидеть когда). Это помогает понять, ждать ли быстрый ответ или лучше поставить задачу со сроком.' },
      { type: 'heading', text: 'Командное меню ⌘K', level: 2 },
      { type: 'paragraph', text: 'Сочетание **Ctrl+K** (на Mac — ⌘K) открывает быстрый поиск по порталу: разделы, вкладки, коллеги и частые действия. Начните печатать название — и переходите к нужному экрану, не разыскивая его в меню.' },
      { type: 'note', variant: 'tip', text: 'Панель можно расширить кнопкой в её углу — так удобнее читать длинные ответы ассистента и большие переписки. Выбранная вкладка запоминается: в следующий раз панель откроется там же, где вы её закрыли.' },
      { type: 'note', variant: 'info', text: 'Задачи из этой панели — это поручения между людьми. Их не надо путать с «Трекером разработок» (что делает разработчик по портале) и с задачами внутри отделов.' },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────────
  // ВКЛАДКА «АНАЛИТИК» — РАЗБОР МЕСЯЦА
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'sales-analyst',
    category: 'wb-analytics',
    title: 'Аналитик — разбор месяца',
    description: 'Что происходит с месяцем, куда придём к его концу и что сделать сегодня. Цифры считает портал, текст пишет модель — и чисел она не пишет вовсе',
    icon: '🧠',
    updatedAt: '2026-08-23',
    blocks: [
      { type: 'paragraph', text: 'Экран «Аналитик» отвечает на три вопроса: что происходит с месяцем прямо сейчас, чем месяц закончится при нынешнем темпе и что сделать сегодня. Это не ещё один дашборд: те же цифры собраны в разбор, где рядом с каждым числом сказано, хорошо это или плохо и что с этим делать.' },
      { type: 'note', variant: 'info', text: 'Разделение жёсткое: ВСЕ цифры считает портал — теми же функциями, что рисуют дашборд и финансовый отчёт по SKU. Модель получает готовые цифры и только формулирует текст; писать числа она не может физически — в текст они не передаются. Поэтому выдуманное число на экран не попадёт.' },
      { type: 'image', src: '/help-media/sales-analyst-screen.jpg', alt: 'Вкладка «Аналитик»: шкалы месяца, карточки «Что происходит», вердикт', caption: 'Разбор собирается каждое утро сам: вердикт одной фразой, три шкалы «пульса месяца» и карточки с цифрами' },
      { type: 'heading', text: 'Как читать экран', level: 2 },
      { type: 'list', items: [
        'Три шкалы сверху — «пульс месяца». Заполнение — сколько сделано, вертикальная риска — где мы должны быть сегодня по календарю. Если заливка не дотянула до риски, рядом написано «отстаём».',
        'У «Продаж» и «Маржи» крупная цифра — процент выполнения плана. У «ДРР» крупная цифра — сам ДРР, а рядом сказано, сколько процентов лимита уже израсходовано: у рекламы хорошо быть НИЖЕ планки, поэтому шкала читается наоборот.',
        '«Что происходит» — карточки по темам: продажи и план, маржа, реклама, товары, склад. Клик по карточке открывает расшифровку: из чего сложилось число и какие артикулы за ним стоят.',
        '«Что сделать» — решения с меткой срочности. Каждое привязано к конкретной цифре, а не к общему впечатлению.',
        'Клик по числу или артикулу ведёт на тот экран, где это видно подробно.',
      ] },
      { type: 'heading', text: 'Откуда берётся текст', level: 2 },
      { type: 'paragraph', text: 'Разбор собирается автоматически каждое утро, после ночной синхронизации с Wildberries. Кнопка «Пересобрать» делает то же самое вручную — например, если днём подгрузили финансовый отчёт и хочется свежий вывод. Если модель почему-то не ответила, экран продолжает работать на одних цифрах и честно пишет, что текста нет.' },
      { type: 'note', variant: 'tip', text: 'Расписание сборки настраивается в разделе «Фоновые задачи» — задача «Аналитик: разбор месяца». Там же видно, когда разбор собирался последний раз и чем закончился.' },
      { type: 'heading', text: 'Почему прибыль изменилась', level: 2 },
      { type: 'paragraph', text: 'Блок раскладывает сдвиг прибыли между двумя последними ЗАКРЫТЫМИ месяцами по факторам: цена продажи, объём, себестоимость, комиссия, логистика, реклама, налог и остальные статьи отчёта. Зелёная полоса вправо — фактор поднял прибыль, красная влево — уронил. Сумма всех вкладов равна разнице прибылей точно, строки «прочее» нет.' },
      { type: 'list', items: [
        'Всё нормировано на выкупленную штуку: рост расхода вслед за ростом продаж — это не ухудшение, и такая нормировка отделяет «продали больше» от «стало дороже».',
        'Строка «Продали больше/меньше» показывает вклад одного лишь объёма — сколько бы дал прирост штук, если бы экономика осталась прежней.',
        'Сравниваются закрытые месяцы, а не текущий с прошлым: в текущем прошла часть дней, и «продали меньше» показывало бы календарь, а не результат.',
      ] },
      { type: 'paragraph', text: 'Тот же разбор есть и по отдельному товару: раскройте артикул в списке «Из чего сложилось» — под расшифровкой появится строка «было → стало» и четыре главных фактора именно этого SKU. Итог «39 убыточных артикулов» показывает, что болит, а разбор по артикулу — почему: у одного просела цена, у другого выросла комиссия, третьего съела реклама.' },
      { type: 'heading', text: 'Спросить ассистента про цифру', level: 2 },
      { type: 'paragraph', text: 'В окне любой карточки разбора есть кнопка «Спросить ассистента». Она открывает AI-чат с уже собранным вопросом: название показателя, его значение, расшифровка «из чего сложилось» и способ расчёта. Пересказывать цифру руками не нужно — ассистент отвечает по ней и подсказывает, что стоит проверить.' },
      { type: 'note', variant: 'info', text: 'Ассистент отвечает только по переданным числам и не придумывает новых. Если данных для вывода мало, он прямо об этом говорит.' },
      { type: 'heading', text: 'Сбывались ли прогнозы', level: 2 },
      { type: 'paragraph', text: 'Каждый разбор сохраняется целиком — вместе с цифрами, какими они были в тот день. Блок «Сбывались ли прогнозы» показывает, что обещал ранний разбор месяца и чем месяц закончился: прогноз берётся из самого раннего сохранённого разбора, итог — из последнего. Ничего не пересчитывается задним числом, сравниваются сохранённые снимки.' },
      { type: 'list', items: [
        'Расхождение показывается только у ЗАКРЫТОГО месяца: у текущего прогноз на конец месяца сравнивать не с чем, там стоит «месяц идёт».',
        '«Переоценили» — прогноз был выше факта, «недооценили» — ниже. Промах больше 10 % подсвечивается: значит строке «придём к концу месяца» пока верить рано.',
      ] },
      { type: 'heading', text: 'Чего разбор не знает', level: 2 },
      { type: 'paragraph', text: 'Внизу экрана — честный список того, что искажает цифры сегодня: бонусы Wildberries в рекламе, которые WB не разделяет по артикулам; дата, по которую посчитана маржа реальным финансовым отчётом; хранение на складе фулфилмента, которое в марже пока не учтено вовсе; упрощённый расчёт налога у «ИП Титкова». Список меняется вместе с данными — если строка исчезла, значит дыру закрыли.' },
      { type: 'note', variant: 'warning', text: 'Экран виден тем же, кому виден дашборд отдела «Продажи» — отдельного права у разбора нет. Он не показывает ничего, чего нет на дашборде и в финансовом отчёте по SKU.' },
    ],
  },
  // ─────────────────────────────────────────────────────────────────────────────
  // ОТЧЁТ ОТДЕЛА ПРОДАЖИ (ГПР)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'sales-report',
    category: 'wb-analytics',
    title: 'Отчёт по кабинетам (ГПР) — дневной срез',
    description: 'Дневной отчёт отдела «Продажи»: заказы, продажи, % выкупа, логистика, реклама и маржа за день по кабинетам Feevi / Enmoone и суммарно, плюс таблица по дням выбранного периода с динамикой. Месяц — на «Дашборде»',
    icon: '📊',
    updatedAt: '2026-08-13',
    blocks: [
      {
        type: 'paragraph',
        text: 'Вкладка «Отчёт» в отделе «Продажи» (первая, рядом с «Дашборд») — это ДНЕВНОЙ отчёт (ГПР) по обоим кабинетам сразу: Feevi (ИП Бабаев) и Enmoone (ИП Титкова), плюс блок «Все кабинеты» (сумма). Показывает результат за вчерашний день и таблицу по дням за выбранный период (по умолчанию — вчера и позавчера). Все цифры — реальные данные WB.',
      },
      {
        type: 'note',
        text: 'Отчёт отвечает на вопрос «как отработали вчера». Как идут дела за месяц — на вкладке «Дашборд»: это разные вопросы, поэтому и разные экраны.',
        variant: 'info',
      },
      { type: 'heading', text: 'Из чего состоит блок кабинета', level: 2 },
      {
        type: 'list',
        items: [
          'Шапка: бренд + юр-лицо, справа — итог дня одной фразой: «план дня выполнен» / «почти выполнен» / «не выполнен» и рядом проценты выполнения по заказам и по продажам.',
          '«За день» (крупные плашки): заказы ₽, продажи ₽ и маржа ₽. В каждой — процент выполнения плана цветным чипом, под цифрой сам план, полоса выполнения и разрыв с планом словами («не хватает 3,9 млн ₽ до плана» / «220 тыс ₽ сверх плана»).',
          'Детали дня: % выкупа, реклама ₽, ДРР от заказов, ДРР от выкупов, логистика ₽, % логистики. Плашки устроены ровно так же, как крупные: чип с процентом, под цифрой ориентир, полоса и разрыв словами.',
          'Таблица по дням: строка на каждый день выбранного периода (свежий сверху), между днями — строка «дин.» с изменением к предыдущему дню по каждой колонке. По умолчанию в ней два дня — вчера и позавчера.',
        ],
      },
      { type: 'heading', text: 'Выполнен план дня или нет', level: 2 },
      {
        type: 'paragraph',
        text: 'Отчёт показывает ЗАКРЫТЫЙ день, поэтому по плану тут не прогноз и не темп, а окончательный счёт: факт против плана. Считать в уме ничего не нужно — портал сам показывает процент выполнения, полосу и разрыв в рублях, а цветом отвечает на главный вопрос: зелёный — на плане и выше, янтарный — 90–100% (почти), красный — ниже 90%. Итоговая фраза в шапке кабинета говорит «план дня выполнен» только когда выполнены ОБА плана — и по заказам, и по продажам (берётся худший из двух процентов).',
      },
      {
        type: 'note',
        text: 'Дневного плана по марже в ГПР не бывает — есть только плановая маржа в процентах на месяц. Чтобы плашка говорила на одном языке с соседними (заказы и продажи), портал переводит этот процент в рубли: плановая маржа % × выручку выбранного дня. Поэтому под цифрой стоит «план 120 212 ₽», а разрыв подписан рублями: «не хватает 86 тыс ₽ до плана». Если за выбранный день финансовый отчёт ещё не доехал, плашка откатывается на последний посчитанный день и подписывает его дату.',
        variant: 'info',
      },
      {
        type: 'note',
        text: 'Если выбран день, за который WB отдал данные не полностью, сравнение с планом гасится: чипы, полосы и итоговая фраза становятся серыми, а в шапке пишется «день не досчитан — с планом не сравниваем». Так недосчитанные 1% не читаются как провал.',
        variant: 'warning',
      },
      { type: 'heading', text: 'Детали дня: с чем сравнивается % выкупа, ДРР и логистика', level: 2 },
      {
        type: 'paragraph',
        text: 'У этих показателей дневного плана в ГПР нет, но одна голая цифра ничего не говорит: 6,8% ДРР — это хорошо или плохо? Поэтому каждая плашка деталей дня показывает факт ПРОТИВ ОРИЕНТИРА и читается так же, как крупные плашки сверху: чип с процентом, под цифрой сам ориентир, полоса и разрыв словами («5,5% до плана», «6 442 ₽ хуже нормы»).',
      },
      {
        type: 'table',
        headers: ['Показатель', 'Ориентир, с которым сравнивается'],
        rows: [
          ['% выкупа', 'План ГПР на месяц: плановые штуки выкупов ÷ плановые штуки заказов. Если в плане нет штук — норма за последние 7 полных дней.'],
          ['ДРР от заказов / от выкупов', 'Собственная норма кабинета: средневзвешенный ДРР за последние 7 ПОЛНЫХ дней до отчётного (недосчитанные дни в норму не входят).'],
          ['Реклама ₽', 'Сколько рекламы ожидалось при ТАКОМ объёме заказов: норма ДРР × заказы дня. Со «средним рублём» сравнивать нельзя — объём дня скачет, и плашка краснела бы случайно.'],
          ['Логистика ₽', 'Сколько ожидалось при таком объёме продаж: норма % логистики × продажи дня.'],
          ['% логистики', 'Норма по последним дням с РЕАЛЬНЫМ финансовым отчётом (не по оценкам) — поэтому её окно может быть глубже семи дней.'],
        ],
      },
      {
        type: 'note',
        text: 'У расходных показателей (ДРР, логистика, реклама) «меньше — лучше», поэтому выполнение считается наоборот: ориентир ÷ факт. Пороги светофора те же — 100% и выше зелёный, 90–100% янтарный, ниже красный. То есть зелёный всегда значит «хорошо», в какую бы сторону ни считалось. Разрыв подписан по-человечески: «0,6% лучше нормы» / «6 442 ₽ хуже нормы». У процентных показателей это разница двух процентов с плашки (факт против ориентира), а не процент от процента — никаких «процентных пунктов» читать не нужно.',
        variant: 'info',
      },
      {
        type: 'note',
        text: 'Пока за отчётный день не доехал финансовый отчёт, логистика ОЦЕНИВАЕТСЯ по среднему проценту от продаж — тогда факт и ориентир считаются от близкой базы и сходятся примерно к 100%. Такая цифра помечена словом «оценка» янтарным. Если ориентира ещё нет (не набралось полных дней), плашка честно пишет «ориентира нет» и не рисует чип.',
        variant: 'warning',
      },
      { type: 'heading', text: 'Как отправить отчёт в чат', level: 2 },
      {
        type: 'paragraph',
        text: 'Кнопка «PDF за день» вверху справа скачивает отчёт ОДНИМ листом: все кабинеты подряд, тот же набор колонок и те же дни, что на экране (по умолчанию вчера и позавчера). Лист не A4 — его размер подгоняется под содержимое: ширина под колонки, высота под количество строк. Никакого перелистывания страниц и никаких «а где остальные показатели». Выполнение дневного плана в PDF показано так же, как на экране: процент, полоса и разрыв с планом в рублях, а под каждым показателем деталей дня — его ориентир и отклонение от него. Дальше отправляете файл в чат и подписываете свои комментарии: почему план выполнен или не выполнен, что повлияло. Открывать портал, чтобы прочитать отчёт, при этом не нужно — в этом и смысл.',
      },
      { type: 'heading', text: 'Как читать таблицу по дням', level: 2 },
      {
        type: 'table',
        headers: ['Колонка', 'Что означает'],
        rows: [
          ['План/день', 'Догоняющий план: (план месяца − факт с начала месяца) ÷ оставшиеся дни. Растёт, когда отстаём — показывает, сколько нужно делать в день, чтобы всё-таки выйти на план месяца. Плоского «плана ÷ 31» больше нет. Под суммой — выполнение этого плана за тот же день: «зак» по заказам, «прод» по продажам, тем же светофором.'],
          ['Заказы, ₽ / шт', 'Заказы за день из воронки WB: цена до СПП, без отменённых.'],
          ['Продажи, ₽ / шт', 'Выручка от выкупленных товаров за день, посчитанная «до СПП» — до скидки, которую даёт сам Wildberries (без скидки Wildberries). Берём середину между ценой, что заплатил покупатель, и ценой без этой скидки: WB возмещает продавцу примерно половину СПП. Штуки — за вычетом возвратов.'],
          ['% выкупа', 'Выкуплено штук ÷ заказано штук за этот день.'],
          ['Логистика, ₽ и % логистики', 'Логистика за день и её доля от продаж. Отстоявшиеся дни — по финансовому отчёту WB; свежие дни (пока финансовый отчёт не доехал) ОЦЕНИВАЮТСЯ — средний % от продаж по последним посчитанным дням. Оценка отмечена янтарным.'],
          ['Реклама, ₽ и ДРР', 'Расход на рекламу за день и его доля — от заказов и от выкупов. Крупная цифра — только то, что WB реально удержал деньгами. Если рядом в скобках стоит «+N ₽ бонусами» — столько откручено бонусами Wildberries: они показаны справочно и в расход не идут, поэтому в марже их нет.'],
          ['Маржа, ₽ и %', 'Маржа ₽ за день (методика ОПиУ): к выплате − себестоимость (закупка + маркировка) − налог − эквайринг − логистика − реклама − доп. расходы. Маржа % = маржа ₽ ÷ выручку до СПП (база — выручка без скидки WB, поэтому процент ниже). За свежие дни, пока не доехал финансовый отчёт, ОЦЕНИВАЕТСЯ (янтарным).'],
          ['дин.', 'Изменение к предыдущему дню — считается у ВСЕХ колонок, пустых клеток в этой строке больше нет (прочерк означает, что сравнивать не с чем: за один из двух дней цифры нет). У рублёвых и штучных колонок это прирост в процентах ((сегодня − вчера) ÷ вчера), у процентных (% выкупа, ДРР, % логистики, маржа %) — РАЗНИЦА двух процентов, иначе получился бы «процент от процента». Цвет — по смыслу показателя, а не по знаку: у заказов, продаж, % выкупа и маржи зелёный значит рост, у рекламы, логистики и ДРР зелёный значит снижение. У колонки «План/день» динамика серая — догоняющий план растёт просто оттого, что мы отстаём. Если один из двух дней недосчитан, динамика тоже серая: сравнивать нельзя.'],
        ],
      },
      {
        type: 'note',
        text: 'Финансовый отчёт WB (логистика, маржа) запаздывает примерно на неделю. Чтобы отчёт за вчера был живым, за свежие дни логистика и маржа ОЦЕНИВАЮТСЯ — берётся средний процент от продаж по последним посчитанным дням (тем же способом, что и в эталонном расчёте — расхождение около 4%). Оценка отмечена янтарным цветом и словом «оценка»; когда доедет финансовый отчёт, цифра сама встанет на фактическую. Продажи и реклама приходят без задержки.',
        variant: 'warning',
      },
      {
        type: 'note',
        text: 'Отчёт открывается на последнем дне, за который данные точно полные. Обычно это вчера, но до утренней синхронизации — позавчера: WB обновляет аналитику по заказам раз в сутки, поэтому ночью за вчера приходит лишь часть заказов (выкупы при этом полные, и % выкупа с ДРР тоже оказываются занижены). Если выбрать такой день вручную, вверху появится жёлтое предупреждение, а строка в таблице станет серой курсивной — отправлять такой отчёт не нужно, к утру цифры встанут на место.',
        variant: 'warning',
      },
      {
        type: 'paragraph',
        text: 'Сверху два переключателя дат: «День» (по умолчанию — последний день с данными) и «Период» — он задаёт, какие дни попадут в таблицу «По дням» и в PDF. По умолчанию это ДВА дня: отчётный день и день перед ним, чтобы сразу видеть вчера и позавчера без лишней простыни. Нужен более длинный срез — раздвиньте период руками, месяц целиком тоже можно. Рядом с показателями, где смысл неочевиден, есть значок «?» с пояснением.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // ДАШБОРД ОТДЕЛА ПРОДАЖИ (месячный срез тех же показателей)
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'sales-dashboard',
    category: 'wb-analytics',
    title: 'Дашборд отдела «Продажи» — месячный срез',
    description: 'Те же показатели, что во вкладке «Отчёт», но за месяц: заказы, продажи, маржа, % выкупа, логистика, реклама, ДРР и возвраты — с планом ГПР, светофором и разрывом словами. Плюс разрез по кабинетам и проекция «идём на»',
    icon: '📈',
    updatedAt: '2026-08-19',
    blocks: [
      {
        type: 'paragraph',
        text: 'Вкладка «Дашборд» отвечает на вопрос «как идут дела за месяц». Состав показателей теперь ровно такой же, как во вкладке «Отчёт» (там они дневные), и цифры сходятся между вкладками до рубля: заказы берутся из воронки WB, продажи — из финансового отчёта, маржа считается по методике ОПиУ, % выкупа — «выкуплено штук ÷ заказано штук».',
      },
      {
        type: 'note',
        text: '«Отчёт» — про вчерашний день, «Дашборд» — про месяц. Разные вопросы, поэтому разные экраны, но одни и те же формулы: если цифра разошлась между вкладками, это ошибка, а не разные методики.',
        variant: 'info',
      },
      { type: 'heading', text: 'Переключатели вверху', level: 2 },
      {
        type: 'list',
        items: [
          '«Месяц ↔ 30 дней» — окно расчёта. «Месяц» — календарный, с 1-го числа: именно так задаётся план ГПР, поэтому проценты выполнения сходятся с «Отчётом». «30 дней» — скользящее окно назад от сегодня, удобно, когда важна не календарная граница, а последние четыре недели.',
          'Выпадашка кабинетов — «Все кабинеты» (сумма) или конкретный кабинет: Feevi (ИП Бабаев) / Enmoone (ИП Титкова). Переключается мгновенно, без перезагрузки: портал считает все разрезы сразу.',
          '«Реальная себестоимость» — тумблер виден тем, у кого есть право: переключает себестоимость между публичной (из кабинета WB) и реальной. Влияет на маржу.',
          'Выпадашка месяца (справа, со значком пульса) — какой месяц смотрим. По умолчанию текущий, доступны 12 последних: так можно вернуться и посмотреть, чем закончился прошлый период. В режиме «30 дней» окно отсчитывается назад от последнего дня выбранного месяца, и это подписано над карточками («30 дн. по 30.06»).',
        ],
      },
      {
        type: 'note',
        text: 'Если рядом с выпадашкой месяца появилась янтарная точка и подпись «данные по такое-то число» — синхронизация кабинета отстала, и свежих дней в портале ещё нет. Наведите на подпись, там сказано на сколько. Пропущенные дни не показываются нулями: на большом графике линия обрывается, а из мини-графиков карточек такие дни просто исключены. Проценты и проекция при этом посчитаны по неполному месяцу.',
        variant: 'warning',
      },
      { type: 'heading', text: 'План и факт: что сравнивается с планом ГПР', level: 2 },
      {
        type: 'paragraph',
        text: 'Справа вверху, сразу под выпадашками кабинета и месяца, одной фразой сказан вывод: «Идём на 56 % плана продаж», а под ним — сколько нужно делать в день, чтобы всё-таки выйти на план, и сколько делаем сейчас. Это то же число, что в блоке «Идём на» внизу экрана, просто вынесенное наверх, чтобы не листать. Цвет фразы — тот же светофор: зелёная на плане и выше, янтарная 90–100 %, красная ниже. Если выбран закрытый месяц, фраза говорит не про проекцию, а про итог.',
      },
      {
        type: 'paragraph',
        text: 'Первый ряд карточек — показатели, у которых есть план в ГПР (тот же файл плана, что у «Отчёта»). Под мини-графиком каждой карточки — светофор: сам план, процент выполнения цветным чипом, полоса и разрыв словами («не хватает 74 млн ₽ до плана», «0,3% сверх плана»). Зелёный — на плане и выше, янтарный — 90–100%, красный — ниже 90%.',
      },
      {
        type: 'table',
        headers: ['Карточка', 'Что показывает и с чем сравнивается'],
        rows: [
          ['Заказы ₽ / шт', 'Заказы за период из воронки WB (цена до СПП, отменённые считаются размещёнными) — тот же источник, что в «Отчёте». План — плановая сумма и штуки заказов на месяц.'],
          ['Продажи ₽ / шт', 'Выручка от выкупов «до СПП» (без скидки Wildberries) и штуки за вычетом возвратов. План — плановые выкупы месяца.'],
          ['Маржа ₽', 'Маржа по методике ОПиУ: к выплате − себестоимость − налог − эквайринг − логистика − реклама − доп. расходы. Планового рубля маржи в ГПР нет — есть плановый процент, поэтому план в подписи переведён в рубли: плановая маржа % × выручку периода. Процент выполнения от этого не меняется.'],
          ['% выкупа', 'Сколько заказов дошло до выкупа, в штуках. Ориентир — тот же процент из плана на месяц, а если штук в плане нет — прошлый период. Рядом справочно — тот же процент, посчитанный по одним и тем же заказам, а не по календарю: он точнее, но с отчётной цифрой не совпадает.'],
        ],
      },
      {
        type: 'note',
        text: 'План ГПР всегда задаётся на МЕСЯЦ. Поэтому в режиме «30 дней» плановые карточки продолжают сравнивать план с фактом месяца (это подписано в карточке) — иначе процент выполнения врал бы: тридцать дней «внахлёст» через границу месяца планом не покрыты.',
        variant: 'info',
      },
      { type: 'heading', text: 'Расходы и эффективность: ориентир — прошлый период', level: 2 },
      {
        type: 'paragraph',
        text: 'У логистики, рекламы, ДРР и возвратов планового значения в ГПР нет. Ориентиром служит СОБСТВЕННАЯ норма за предыдущий сопоставимый период — прошлый месяц (или предыдущие 30 дней). Даты этой нормы подписаны над рядом карточек. Дневной «Отчёт» берёт норму за 7 дней, месячный дашборд — за прошлый период: сравнивать месяц с недельной нормой было бы подменой базы.',
      },
      {
        type: 'table',
        headers: ['Показатель', 'Ориентир'],
        rows: [
          ['Логистика ₽', 'Сколько ожидалось при ТАКОМ объёме продаж: норма % логистики × выручку периода. Со «средним рублём» сравнивать нельзя — объём месяца скачет.'],
          ['% логистики', 'Та же доля за прошлый период, посчитанная только по дням с РЕАЛЬНЫМ финансовым отчётом — иначе оценка сравнивалась бы сама с собой и всегда давала ровно 100%.'],
          ['Реклама ₽', 'Сколько рекламы ожидалось при таком объёме заказов: норма ДРР × заказы периода. Крупная цифра — только удержанные деньги; бонусы Wildberries, если были, стоят рядом в скобках и в расход не входят.'],
          ['ДРР от заказов / от выкупов', 'Та же доля за прошлый период.'],
          ['Маржа %', 'План ГПР по марже, а если его нет — прошлый период. Считается от розничной выручки финансового отчёта: это та же база, что у маржи % в «Отчёте».'],
          ['Возвраты, шт', 'Доля возвратов (вернули ÷ отгруженное) за прошлый период, пересчитанная в ШТУКИ по объёму этого периода: «столько вернулось бы при прошлой доле». Со «средним числом возвратов» сравнивать нельзя — объём месяца скачет.'],
        ],
      },
      {
        type: 'note',
        text: 'У расходных показателей «меньше — лучше», поэтому выполнение считается наоборот: ориентир ÷ факт. Зелёный всегда значит «хорошо», в какую бы сторону ни считалось. Разрыв подписан по-человечески: «1,2 млн ₽ лучше нормы» / «2,7% хуже нормы».',
        variant: 'info',
      },
      {
        type: 'paragraph',
        text: 'На полосе выполнения плана есть тонкая вертикальная риска — сколько ожидалось бы к сегодняшнему дню при ровном темпе (доля прошедших дней месяца). Заливка левее риски означает «отстаём», правее — «идём с опережением». Без неё «32 %» на 17-е число и на 3-е выглядели одинаково красными.',
      },
      { type: 'heading', text: '«Идём на»: выйдем ли на план', level: 2 },
      {
        type: 'paragraph',
        text: 'Блок «Идём на» — это проекция месяца по текущему темпу: факт × дней в месяце ÷ прошедших дней. В строках заказы, продажи и маржа, в колонках — план, факт, «сделано %», проекция, «идём на %» и «нужно в день». Последняя колонка — догоняющий темп: (план − факт) ÷ оставшиеся дни, то есть сколько надо делать ежедневно, чтобы всё-таки закрыть план (та же логика, что у колонки «План/день» во вкладке «Отчёт»). Блок отвечает на главный вопрос месяца: если работать так же, план будет выполнен или нет. Он всегда месячный, даже в режиме «30 дней», — план задаётся на месяц. Если выбран уже закрытый месяц, проецировать нечего: блок называется «Итог», колонки проекции пустые, а факт и есть результат.',
      },
      {
        type: 'paragraph',
        text: 'У строки «Маржа» план считается не так, как у заказов и продаж: в плане на месяц маржа задана процентом, а не рублями. Поэтому рубли берутся из плана в штуках: маржа на единицу по текущей юнит-экономике × плановые выкупы каждого артикула, а из суммы вычитается реклама по плановому ДРР. ДРР — один на все артикулы, задаётся вручную в «Настройки → Продажи · WB → План ГПР на месяц», считается ОТ ВЫКУПОВ (ориентир 3–4 %). У артикулов, по которым не заведена себестоимость, маржа достраивается средней по остальным — иначе их план молча считался бы бесприбыльным.',
      },
      { type: 'heading', text: 'График, кабинеты и остатки', level: 2 },
      {
        type: 'list',
        items: [
          '«Динамика по дням» — график за выбранный период и выбранный кабинет. Кнопки переключают метрику: продажи, заказы, маржа, реклама, логистика. Пунктирный хвост — предварительная оценка, финансовый отчёт WB ещё не пришёл.',
          '«Выкупы по кабинетам WB» — пончик распределения выручки. Он всегда про ВСЕ кабинеты, независимо от выбранного разреза.',
          '«План-факт месяца» — шкала выполнения плана по выкупам за календарный месяц, по выбранному разрезу.',
          '«Остатки» — сколько товара сейчас на складах WB, в пути к клиенту и в пути обратно, штуки и оценка в рублях по себестоимости. Снимок общий по обоим кабинетам.',
          'Остатки и индекс локализации — снимки НА СЕЙЧАС, они не перематываются вместе с выбранным месяцем: у WB нет истории остатков по дням. Дата снимка подписана в самой карточке.',
        ],
      },
      {
        type: 'note',
        text: 'Финансовый отчёт WB (логистика, маржа) запаздывает примерно на неделю, поэтому за свежие дни они ОЦЕНИВАЮТСЯ. В карточках это подписано («3 дн. оценка»), у маржи рядом показано значение без оценки и дата, по которую посчитан реальный финансовый отчёт. Заказы и реклама приходят без задержки.',
        variant: 'warning',
      },
    ],
  },

  {
    slug: 'refdata-history',
    category: 'wb-analytics',
    title: 'История справочников — что и когда менялось под отчётами',
    description: 'Снимки тарифов и комиссий WB, галочек «какие склады учитываем» и ставок кабинетов: когда изменилось, что именно и на какую дату что действовало',
    icon: '🕔',
    updatedAt: '2026-08-17',
    blocks: [
      {
        type: 'paragraph',
        text: 'Экономика в отчётах опирается на справочники: тарифы складов и комиссии WB, галочки «какие склады учитываем», ставки кабинета (налог, эквайринг, приёмка за литр, маркировка). Все они меняются — WB переписывает свою сетку, а ставки правят руками. Раньше портал хранил только «как сейчас», поэтому вопрос «почему в июле оборачиваемость была другой» оставался без ответа. Теперь каждое изменение попадает в историю, и её видно на экране: «Настройки» → «Продажи · WB» → «История справочников».',
      },
      {
        type: 'note',
        text: 'Снимок отвечает на вопрос «что источник ГОВОРИЛ на эту дату» — какой тариф публиковал WB, какая ставка стояла в кабинете, какие склады были включены. Это НЕ отчёт о расходах: сколько мы фактически заплатили, видно в финансовом отчёте WB, и расхождение между ними законно. Пример: ставка приёмки 1,7 ₽/л стоит в кабинете с 28.05.2026, а WB до конца июля приёмку не брал вовсе — и начал 28.07.2026, уже по-другому: 10 ₽ за штуку за обработку товара.',
        variant: 'warning',
      },
      {
        type: 'heading',
        text: 'Как читать список',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Каждая строка — одно реальное изменение, а не ежедневная копия. Колонки «Действует с» и «По» задают период, в течение которого справочник был именно таким: чтобы узнать, что действовало 3 августа, найдите строку, чей период накрывает эту дату. В колонке «Что изменилось» написано, что именно поменяли («выключили «Коледино»», «приёмка, ₽/л: 1,7 → 2,5»). Нажатие на строку открывает панель со сравнением с предыдущим снимком и, по кнопке, полным содержимым.',
      },
      {
        type: 'list',
        items: [
          'Тарифы складов WB — коэффициенты логистики и хранения по каждому складу. Приёмки в этом ответе WB нет.',
          'Комиссии WB по категориям — ставка комиссии на каждую категорию товара.',
          'Склады: какие учитываем — галочки на вкладке «Остатки по складам». От них зависят оборачиваемость и хранение.',
          'Ставки кабинетов — налог, режим налогообложения, НДС, эквайринг, коррекция комиссии, приёмка за литр, маркировка, доп. расходы.',
          'Коэффициенты приёмки WB — заготовка: WB пока не отдаёт эти данные, снимки начнут копиться сами, как только отдаст.',
        ],
      },
      {
        type: 'heading',
        text: 'Почему записей меньше, чем дней',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Снимок создаётся ТОЛЬКО когда содержимое действительно изменилось. Синхронизация тарифов идёт каждый день, но сами тарифы стоят месяцами, поэтому ежедневных копий нет — вместо этого у последней записи растёт дата «По». Так одна строка означает одно изменение, и найти момент, когда цифра поехала, можно глазами.',
      },
      {
        type: 'note',
        text: 'Если источник ответил пусто, снимок НЕ создаётся и предыдущий остаётся действующим — пустой ответ считается сбоем, а не «данных больше нет». Такой случай виден в «Синхронизациях» как ошибка. Именно так 16.08.2026 WB отдал пустой список складов, и портал остался бы вообще без тарифов.',
        variant: 'info',
      },
      {
        type: 'paragraph',
        text: 'Внизу экрана — «Глубина хранения». Подённая история держится за последний год, глубже остаётся один снимок на месяц. Кнопка «Посмотреть, что будет удалено» показывает список без удаления; само схлопывание требует отдельного подтверждения, потому что вернуть удалённые снимки неоткуда.',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // ПЛАНИРОВАНИЕ YUC
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'plan-yuc',
    category: 'wb-analytics',
    title: 'Планирование YUC — сезон, распродажа, план по SKU',
    description: 'Пошаговая инструкция по всем вкладкам: импорт тренда MPSTATS с сезонностью, планирование по SKU с юнит-экономикой (калькулятор/кабинет), планом/фактом/прогнозом, режим распродажи, планирование по месяцам с ROI/GMROI',
    icon: '📈',
    updatedAt: '2026-08-18',
    blocks: [
      {
        type: 'paragraph',
        text: 'Модуль «Планирование YUC» помогает заранее ответить на главные вопросы продаж: сколько товара мы продадим в каждом месяце, сколько нужно закупить и когда, и сколько на этом заработаем. Он находится в отделе «Продажи» — верхняя вкладка «Планирование YUC».',
      },
      {
        type: 'paragraph',
        text: 'Идея простая. У любого товара есть свой сезон: платья лучше продаются весной, куртки — осенью. Чтобы понять этот сезон, модуль смотрит не на один ваш артикул (по нему данных мало), а на спрос КОНКУРЕНТОВ в вашей нише — эти данные выгружаются из сервиса MPSTATS. По ним строится «форма сезона» (в каком месяце спрос выше, в каком ниже), а дальше на неё накладывается ваша реальная скорость продаж, ваша цена, процент выкупа и себестоимость — и получается помесячный план с прибылью.',
      },
      {
        type: 'note',
        text: 'Для кого: менеджер по продажам / закупкам, который планирует сезон и закуп. Никаких формул вручную считать не нужно — вы загружаете выгрузку, привязываете её к артикулу, а расчёт и графики строятся сами.',
        variant: 'info',
      },
      { type: 'heading', text: 'Весь цикл за 6 шагов', level: 2 },
      {
        type: 'paragraph',
        text: 'Ниже — короткая карта: что за чем делать. Каждый шаг подробно разобран дальше в статье.',
      },
      {
        type: 'ordered-list',
        items: [
          'Собрать в MPSTATS группу конкурентов вашего товара и скачать выгрузку файлом (делается один раз на группу артикулов одного фасона).',
          'Во вкладке «Импорт MPSTATS» выбрать категорию (тип изделия) и отметить артикулы, к которым относится тренд, затем «Загрузить MPSTATS» — сразу увидите форму сезона (в каком месяце спрос выше). Артикулы к тренду привязываются автоматически.',
          'При желании перейти на «Планирование по SKU» и уточнить/сменить тренд у артикула вручную.',
          'Задать параметры: с какого месяца планируем, как оцениваем рост рынка. Получить помесячный план с прибылью, поставками и остатком.',
          'При необходимости включить режим «Распродажа остатков» (когда цель — распродать склад к определённому сроку) и при желании поправить план вручную.',
          'Открыть «Планирование по месяцам» — там сводная картина по всем артикулам: сколько заработаем в каждом месяце и какие товары самые выгодные.',
        ],
      },
      {
        type: 'note',
        text: 'Подсказки везде. Рядом почти с каждым параметром, показателем и заголовком столбца есть значок «?». Наведите на него курсор — во всплывающей подсказке простыми словами написано, что это и как считается. Если что-то непонятно на экране — сначала посмотрите туда.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Словарь простыми словами', level: 2 },
      {
        type: 'table',
        headers: ['Термин', 'Что это простыми словами'],
        rows: [
          ['Тренд (MPSTATS)', 'Выгрузка спроса по группе конкурентов вашего товара за год. Из неё берётся форма сезона.'],
          ['Сезонность', 'Насколько каждый месяц выше или ниже среднего. Среднее по году = 1. Больше 1 — сезон (продаём много), меньше 1 — спад.'],
          ['SKU / артикул', 'Один конкретный товар (например «поло белое»). План строится по каждому отдельно.'],
          ['Заказы', 'Сколько штук заказали покупатели (ещё не факт, что выкупят).'],
          ['Выкупы', 'Сколько штук реально выкупили и оставили себе (заказы минус возвраты). Именно с них прибыль.'],
          ['% выкупа', 'Какая доля заказов доходит до выкупа. Берётся из вашей юнит-экономики.'],
          ['Прибыль', 'Сколько заработали после всех расходов (себестоимость, комиссии, налог, реклама).'],
          ['ROI / GMROI', 'Отдача на вложенные деньги: сколько прибыли приносит каждый вложенный (или замороженный в товаре) рубль.'],
          ['Юнит-экономика', 'Расчёт «сколько зарабатываем с одной единицы товара» — цена, себестоимость, комиссии, % выкупа. Берётся из вашего кабинета WB.'],
        ],
      },

      { type: 'divider' },

      { type: 'heading', text: 'Шаг 0. Подготовка данных в MPSTATS', level: 2 },
      {
        type: 'paragraph',
        text: 'Основа расчёта — тренд спроса КОНКУРЕНТОВ вашего товара, а не одного артикула. Его нужно один раз собрать в MPSTATS и выгрузить файлом.',
      },
      {
        type: 'ordered-list',
        items: [
          'В MPSTATS найдите ваш товар и откройте его предмет/категорию, соберите группу максимально похожих конкурентов: по цене (±15–20% от вашей цены с СПП), по полу, по цвету (по корню слова, напр. «беж»), исключите неподходящие фасоны по ключевым словам. Цель — чтобы 80–90% группы были вашими прямыми конкурентами.',
          'Задайте период минимум 1 год (лучше «с 7-го по 7-е», чтобы обойти искажение остатков MPSTATS с 8 сентября).',
          'Откройте вкладку «По дням» и скачайте выгрузку в XLSX. Подойдёт и месячная, и дневная выгрузка — дневная даёт более чистую сезонную кривую.',
        ],
      },

      { type: 'heading', text: 'Вкладка 1. Импорт MPSTATS', level: 2 },
      {
        type: 'paragraph',
        text: 'Здесь вы загружаете выгрузки MPSTATS и сразу видите сезонность выбранного тренда. При загрузке выбираете артикулы (SKU), к которым тренд относится — один тренд можно привязать к нескольким артикулам одного фасона.',
      },
      {
        type: 'ordered-list',
        items: [
          'Выберите «Категорию» — тип изделия (Рубашки, Платья): по ней тренды группируются. Категория выбирается из списка; новую заводят кнопкой «+ Добавить категорию»; удалить категорию — наведя на неё в списке, корзинкой (тренды при этом сохраняются, уходят в «Прочее»).',
          'В поле «Выберите SKU» отметьте один или несколько артикулов, к которым относится этот тренд.',
          'Дальше — два пути. «Загрузить MPSTATS» — выбрать скачанный XLSX-файл: создаётся тренд и сразу привязывается к выбранным SKU (имя берётся из них). «Привязать к «…»» (без файла) — у выбранных SKU, у кого уже есть свой одноимённый тренд, берётся он (и относится к этой категории), а у остальных — средний тренд категории; если своего тренда нет и в категории ещё нет трендов, такой SKU нужно сначала загрузить файлом.',
          'Тренды сгруппированы по категориям-пилюлям (Рубашки, Платья…). Клик по категории показывает СРЕДНИЙ тренд по всем её подкатегориям; клик по конкретной карточке — её сезонность. Наведение на карточку → корзина удаляет тренд (SKU, что висели на нём, автоматически перецепятся на средний тренд категории).',
          'Строка «Поиск тренда…» ищет по имени во всех категориях.',
          'Сырые месячные строки выгрузки спрятаны в раскрывающийся блок под графиком — нажмите «Месячные строки выгрузки», чтобы их посмотреть.',
        ],
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-import.png',
        alt: 'Вкладка «Импорт MPSTATS»: поле имени, кнопка загрузки, карточки трендов',
        caption: 'Импорт MPSTATS: сверху — компактная строка загрузки, ниже — карточки загруженных трендов и сезонность выбранного.',
      },
      { type: 'heading', text: 'Сезонность выбранного тренда', level: 3 },
      {
        type: 'paragraph',
        text: 'Ниже списка трендов — форма сезонности ниши: насколько каждый месяц выше или ниже среднего (среднее по году = 1). Зелёные столбцы выше 1 — сезон, серые ниже — спад. Это отвечает на вопрос «что лучше продаётся в июле, а что в августе».',
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-seasonality.png',
        alt: 'Сезонность тренда: столбцы коэффициентов по месяцам, линия заказов, переключатели метода и показателя',
        caption: 'Сезонность: коэффициент по каждому месяцу (пик в мае, спад зимой). Сверху — метод расчёта и показатель (заказы/выкупы).',
      },
      {
        type: 'list',
        items: [
          'Метод «Мес. доля» — распределение спроса по месяцам как есть (значение по умолчанию).',
          'Метод «Снять тренд» — убирает годовой рост/падение ниши, оставляя чистую сезонность (надёжнее на дневных данных).',
          'Показатель — по чему строить форму: «Заказы, шт» (по умолчанию), «Выкупы, шт» или «Заказы, ₽».',
        ],
      },
      {
        type: 'note',
        text: 'Свежий (неполный) месяц помечен полупрозрачным — он не участвует в расчёте сезонности, чтобы не искажать картину.',
        variant: 'info',
      },

      { type: 'heading', text: 'Вкладка 2. Планирование по SKU', level: 2 },
      {
        type: 'paragraph',
        text: 'Главная вкладка — помесячный план по одному артикулу с прибылью, поставками и сравнением с фактом. Выберите артикул в списке сверху.',
      },
      { type: 'heading', text: 'Параметры и KPI', level: 3 },
      {
        type: 'ordered-list',
        items: [
          'Выберите артикул в списке сверху — список сгруппирован по категориям и ищется по подстроке.',
          'Кнопка «Артикулы» справа — какие артикулы участвуют в планировании: тумблер выключен → артикул скрыт из списка и сводов (план и ручные правки при этом сохраняются). Так чистят список от того, чего нет в продаже.',
          'Привяжите тренд в поле «Тренд» (с поиском): либо конкретный тренд, либо «Средний тренд · Категория» — усреднённая сезонность всех трендов этой категории. Если имя тренда совпадает с артикулом, он подтягивается автоматически (регистр букв не важен). Обычно привязка уже сделана при импорте.',
          'Задайте «Старт» — с какого месяца строить план (планируете осень — ставьте сентябрь; старт в прошлом включает план/факт/прогноз).',
          'Выберите «Юнитку» (источник экономики): «Калькулятор» — плановая маржа из «Просчёта прибыли» (реклама уже в марже); «Кабинет» — фактическая юнит-экономика по продажам за 4 недели; «Авто» (по умолчанию) — калькулятор, если товар в нём есть, иначе кабинет. Бейдж рядом («план»/«факт») показывает, что реально применилось.',
          'Выберите «Динамику рынка»: Без роста (только сезонность), Низкая/Умеренная/Сильная (закладываем 33/66/100% от годового роста ниши).',
          'При необходимости поправьте «Триггер поставки» (при каком остатке в днях заказывать новую партию) и «Потолок оборачиваемости» (на сколько дней максимум завозить).',
        ],
      },
      {
        type: 'paragraph',
        text: 'Строка KPI показывает итоги: прибыль за год, ROI на инвестиции, GMROI (отдача на замороженный в товаре капитал), выкупы, поставки и инвестиции. Ниже — графики заказов/выкупов по месяцам с линией сезонности и график остатка с поставками.',
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-pnl-top.png',
        alt: 'Верх вкладки «Планирование по SKU»: выбор SKU, параметры, KPI-карточки и графики',
        caption: 'Планирование по SKU: выбор артикула, панель параметров, карточки KPI и графики (заказы/выкупы + сезонность; остаток + поставки).',
      },

      { type: 'heading', text: 'Юнит-экономика: план или факт', level: 3 },
      {
        type: 'paragraph',
        text: 'Вся прибыль, маржа и ROI зависят от юнит-экономики артикула (цена, себестоимость, комиссии, налог, % выкупа). Её можно брать из двух источников — переключатель «Юнитка» в панели параметров:',
      },
      {
        type: 'list',
        items: [
          '«Калькулятор» — плановая юнитка из «Просчёта прибыли» (та, что раньше была в R&D). Реклама в ней уже учтена внутри маржи, поэтому ДРР показывается 0.',
          '«Кабинет» — фактическая юнит-экономика по вашим продажам за последние 4 недели. У новых или пока убыточных товаров она бывает отрицательной — это реальная картина, а не ошибка.',
          '«Авто» (по умолчанию) — берёт калькулятор, если товар в нём заведён, иначе кабинет.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Рядом с переключателем — бейдж «план» (зелёный, применён калькулятор) или «факт» (синий, применён кабинет), чтобы всегда было видно, откуда взяты цифры.',
      },
      {
        type: 'note',
        text: 'Если товара ещё нет в калькуляторе (или нужно поправить его экономику) — нажмите «Заполнить» рядом с переключателем. Прямо здесь откроется компактный калькулятор прибыли: закупка, логистика, цена, комиссии, налог. Заполните и нажмите «Сохранить в таблицу» — план тут же пересчитается по новой марже, а товар появится в калькуляторе и для других инструментов.',
        variant: 'tip',
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-eco.png',
        alt: 'Компактный калькулятор прибыли по кнопке «Заполнить»: цена, комиссии, логистика, налог, себестоимость, маржа/ROI',
        caption: 'Калькулятор «Заполнить»: вносим закупку, логистику, цену и комиссии — внизу сразу маржа, ROI и чистая прибыль; «Сохранить в таблицу» обновляет план.',
      },

      { type: 'heading', text: 'Помесячная таблица', level: 3 },
      {
        type: 'paragraph',
        text: 'Каждая строка — месяц плана. Цветные ячейки можно редактировать вручную (клик по ячейке) — ручной ввод приоритетнее расчёта; чтобы вернуть авторасчёт, очистите ячейку.',
      },
      {
        type: 'paragraph',
        text: 'План ведётся в ВЫКУПАХ — ориентируемся на то, сколько реально выкупят, а не сколько закажут. Над таблицей есть тумблер «Сохранять объём сезона»: когда он включён, правка выкупов месяца не меняет общий объём сезона — разница расходится по следующим месяцам пропорционально их сезонному коэффициенту (убрали 10 штук из пикового месяца — они распределятся по остальным, больше туда, где сезон выше). Выключите тумблер, если нужно именно увеличить или уменьшить объём всего сезона.',
      },
      {
        type: 'table',
        headers: ['Столбец', 'Что показывает'],
        rows: [
          ['Коэф', 'Коэффициент сезонности месяца (среднее = 1). Редактируется.'],
          ['Заказы', 'План заказов = уровень × сезонность × динамика рынка. Редактируется.'],
          ['Выкупы', 'ГЛАВНЫЙ показатель плана — сколько штук планируем продать за месяц. Редактируется: заказы пересчитаются через % выкупа, а поставка подстроится под план. Зелёным снизу — факт; оранжевым «нет товара» — сколько штук плана не обеспечено остатком и поставкой.'],
          ['%вык / Цена СПП / ДРР', 'Из выбранной юнит-экономики (калькулятор/кабинет); можно задать плановые вручную.'],
          ['Маржа сДРР', 'Маржинальность после рекламы (прибыль ÷ выручка выкупов).'],
          ['Прибыль', 'Операционная прибыль месяца: выкупы × маржа до ДРР − реклама.'],
          ['Поставка / Остаток', 'Рекомендованная (или ручная) поставка и прогноз остатка на конец месяца.'],
        ],
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-pnl-table.png',
        alt: 'Помесячная таблица P&L с редактируемыми ячейками и фактом',
        caption: 'Помесячная таблица: план по каждому месяцу; под планом зелёным — факт (в столбцах Выкупы и Прибыль).',
      },
      { type: 'heading', text: 'Режим распродажи остатков', level: 3 },
      {
        type: 'paragraph',
        text: 'Включите тумблер «Распродажа остатков», задайте целевой выкуп (сколько штук нужно продать за сезон — это может быть вся закупка, а не только текущий остаток) и срок. Если поле цели оставить пустым, берётся весь остаток плюс товар в пути (и едущий к покупателям, и возвраты/примерки от них) — то, что реально предстоит распродать. Система развернёт расчёт наоборот: распределит эту цель по сезонности (в пик месяцы больше, в спад меньше) и по проценту выкупа посчитает нужные заказы — так, чтобы за срок продать ровно заданное количество. Распределяется именно ваша цель, а не текущий остаток кабинета. Новые поставки при этом не рекомендуются.',
      },
      { type: 'heading', text: 'План / Факт / Прогноз', level: 3 },
      {
        type: 'paragraph',
        text: 'Блок сравнения плана с фактом. По завершённым месяцам подтягивается факт из финансового отчёта WB; прогноз проецирует итог года по текущему темпу (план × процент выполнения). Переключатель сверху меняет вид: «Прибыль (мес)» — накопленная прибыль (план пунктиром, факт зелёной, прогноз фиолетовой), «Заказы (день)» и «Выкупы (день)» — план и факт по дням.',
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-pnl-fact.png',
        alt: 'Блок План/Факт/Прогноз: накопленная прибыль и полоса выполнения плана',
        caption: 'План/Факт/Прогноз: факт (зелёная) выше плана (пунктир) — выполнение 217%, прогноз пересчитывает итог года.',
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-daily.png',
        alt: 'План/факт по дням: дневной план ступенькой и фактические продажи',
        caption: 'План/факт по дням: серый пунктир — дневной план, зелёная — фактические продажи (тонкая по дням, жирная — среднее за 7 дней), вертикаль «Сегодня».',
      },
      {
        type: 'note',
        text: 'Факт и прогноз появляются только для завершённых месяцев. Если план стартует в будущем (по умолчанию — с текущего месяца), факта ещё нет. Чтобы сравнить с фактом, задайте дату старта плана в прошлом (начало сезона).',
        variant: 'info',
      },

      { type: 'heading', text: 'Вкладка 3. Планирование по месяцам', level: 2 },
      {
        type: 'paragraph',
        text: 'Сверху — «План по месяцам»: месяцы идут по столбцам, показатели — по строкам (как в финансовой декомпозиции). Блок «Итого по всем артикулам» сводит на год вперёд заказы, выкупы, поставки, выручку, прибыль и маржу; справа столбец «Год» — сумма за горизонт. Ниже — блоки по каждому артикулу с теми же показателями по месяцам; клик по названию артикула раскрывает/сворачивает его строки (кнопка «Развернуть всё» — сразу все). План выкупов артикула на месяц редактируется прямо в ячейке (клик по числу) — заказы пересчитаются через % выкупа; очистите значение, чтобы вернуть авторасчёт. Тумблер «Сохранять объём сезона» вверху задаёт, что делать с разницей: распределить её по следующим месяцам по тренду (включён) или изменить объём сезона (выключен). Строка «Реклама» — плановый бюджет (заказы × ДРР), поэтому правка ДРР сразу меняет и рекламу, и маржу, и прибыль.',
      },
      {
        type: 'paragraph',
        text: 'Ниже — «План на месяц»: выберите месяц в списке, и откроется таблица со всеми артикулами, у которых есть план на этот месяц (строки — артикулы). Колонки сгруппированы: Товар (предмет, дней в наличии), Факт за месяц (заказы/выкупы WB), Рынок (реклама, ДРР, рост рынка), План (заказы/выкупы шт и ₽, динамика к пред. месяцу), Себестоимость (себес/комиссия/логистика/налог из калькулятора), Маржа (цена, % выкупа, маржа до/с ДРР), Прибыль. Внизу — «Итого за месяц». Чекбокс «Согласован» отмечает, что план по артикулу на месяц утверждён (сохраняется).',
      },
      {
        type: 'paragraph',
        text: 'Если план на месяц уже посчитан в экселе, его не нужно вбивать руками: кнопка «Загрузить из файла» справа от заголовка «План на месяц» принимает файл xlsx, где в первой колонке артикул продавца, а во второй — план в штуках. Строки «ИТОГО» не нужны, но если они есть — по ним сверится, что файл прочитан целиком. Кабинет искать не надо: каждый артикул сам находит свой, поэтому в одном файле могут лежать сразу все.',
      },
      {
        type: 'paragraph',
        text: 'Сначала нажмите «Проверить файл» — портал покажет, что попадёт в план: сколько штук и артикулов по каждому кабинету, сколько планов заведётся заново, а у скольких настройки сохранятся. Отдельно перечисляются проблемы: артикул повторяется в файле (числа сложатся — если это опечатка, поправьте файл), артикула нет в портале (строку записать некуда, будет пропущена), выбранный месяц не попадает в горизонт плана, план нулевой. Ничего не записывается, пока вы не нажмёте «Записать план». Загрузка не трогает настройки уже заведённых планов — режим, горизонт и цели остаются вашими, меняется только план выкупов на выбранный месяц.',
      },
      {
        type: 'paragraph',
        text: 'Ниже — «Свод по артикулам»: прибыль за год, выполнение плана, прогноз, ROI на инвестиции и на поставки, GMROI, инвестиции. Клик по заголовку столбца сортирует. Убыточные артикулы подсвечены красным — сразу видно, куда эффективнее вложить капитал и от чего лучше отказаться.',
      },
      {
        type: 'image',
        src: '/help-media/plan-yuc-summary.png',
        alt: 'Вкладка «Планирование по месяцам»: месяцы по столбцам, показатели и артикулы по строкам, ниже свод по SKU',
        caption: 'План по месяцам: месяцы — столбцы, показатели/артикулы — строки, столбец «Год»; ниже — свод по SKU с ROI/GMROI.',
      },

      { type: 'divider' },

      { type: 'heading', text: 'Частые вопросы и что делать, если…', level: 2 },
      {
        type: 'table',
        headers: ['Вопрос / проблема', 'Что делать'],
        rows: [
          ['Загрузил план из файла, а сумма в модуле не сходится с файлом', 'Скорее всего, у части артикулов план уже был заведён раньше, а в файле по ним стоит ноль или их нет вовсе. Ноль из файла перекрывает старый план, а вот отсутствующие в файле артикулы модуль продолжает считать сам. Проверьте предпросмотр загрузки — там перечислено всё, что будет пропущено.'],
          ['В списке артикулов слишком много лишнего', 'На вкладке «Планирование по SKU» нажмите кнопку «Артикулы» и выключите тумблеры у тех, что не в продаже — они исчезнут из списка и сводов. План при этом не удаляется, включите обратно — всё вернётся.'],
          ['Тренд не привязался автоматически', 'Автопривязка срабатывает, когда имя тренда совпадает с артикулом (регистр букв не важен). Если не совпало — выберите тренд вручную в поле «Тренд» (там есть поиск).'],
          ['Прибыль/маржа считается отрицательной', 'По умолчанию берётся фактическая юнит-экономика кабинета (продажи за 4 недели) — у новых/убыточных сейчас товаров она в минусе. Переключите «Юнитку» на «Калькулятор» (или «Авто») — план посчитается по плановой марже из «Просчёта прибыли».'],
          ['Товара нет в калькуляторе / хочу поправить его юнитку', 'Нажмите «Заполнить» рядом с переключателем «Юнитка» — прямо в этой вкладке откроется компактный калькулятор прибыли (закупка, логистика, цена, комиссии, налог). Заполните и нажмите «Сохранить в таблицу» — план сразу пересчитается по новой марже.'],
          ['Сентябрь и осень «по нулям»', 'В выгрузке MPSTATS выкупы конкурентов не показываются до 09.09.2025 (WB сменил правила). Модуль сам достраивает такие месяцы из заказов, но для чистой картины лучше строить сезон по показателю «Заказы, шт» (стоит по умолчанию).'],
          ['Хочу планировать не с текущего месяца, а с осени', 'В параметрах артикула поле «Старт» — выберите нужный месяц (например сентябрь). Старт в прошлом дополнительно включает сравнение план/факт/прогноз.'],
          ['Нет блока «Факт/Прогноз»', 'Факт по WB подтягивается только за завершённые месяцы. Если план стартует в будущем — фактов ещё нет. Поставьте «Старт» в прошлом (начало сезона).'],
          ['Остаток показывает мало, хотя товар есть', 'Наведите на «?» рядом с остатком — там разбивка: сколько на складе WB и сколько «в пути» (едет на склад / возвращается от покупателей). В обычном плане в расчёт берётся остаток из юнит-экономики; в режиме распродажи цель по умолчанию = остаток + весь товар в пути.'],
        ],
      },

      {
        type: 'note',
        text: 'Важно: юнит-экономика (цена, % выкупа, себестоимость, комиссия) берётся не из MPSTATS, а из выбранного источника — калькулятора «Просчёта прибыли» или вашего кабинета WB (переключатель «Юнитка»). Из MPSTATS берётся только форма сезонности. На месячных данных оценка годового тренда ниши приблизительна; для точной сезонной кривой грузите дневную выгрузку.',
        variant: 'warning',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // PORTAL
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'getting-started',
    category: 'portal',
    title: 'Начало работы с порталом',
    description: 'Обзор интерфейса, навигация по боковой панели, профиль, переключение темы и как найти нужный раздел',
    icon: '🚀',
    updatedAt: '2026-09-30',
    blocks: [
      {
        type: 'paragraph',
        text: 'YUC Portal — единая внутренняя платформа компании, объединяющая все рабочие инструменты: аналитику продаж WB, маркетинг и работу с блогерами, R&D и закупки, ассортиментную матрицу, базу знаний, доску задач и справочные материалы. Все данные доступны в одном месте — без переключения между сторонними сервисами.',
      },
      {
        type: 'video',
        src: '/help-media/getting-started-navigation.mp4',
        poster: '/help-media/getting-started-navigation.jpg',
        caption: 'Видео с озвучкой: навигация по порталу — раздел «Отделы», страница отдела, поиск по инструкциям (1 мин)',
      },

      { type: 'heading', text: 'Вход в систему', level: 2 },
      {
        type: 'paragraph',
        text: 'Откройте портал в браузере. Если вы не авторизованы, система автоматически перенаправит вас на страницу входа. Введите свой корпоративный email и пароль, затем нажмите «Войти».',
      },
      {
        type: 'note',
        text: 'Если вы забыли пароль или не можете войти — обратитесь к администратору платформы. Самостоятельный сброс пароля через email пока недоступен.',
        variant: 'info',
      },

      { type: 'heading', text: 'Боковая панель навигации', level: 2 },
      {
        type: 'paragraph',
        text: 'Слева расположена главная навигационная панель. Она присутствует на всех страницах портала и даёт доступ ко всем разделам. Набор пунктов меню зависит от ваших прав доступа — часть разделов может быть скрыта.',
      },
      {
        type: 'image',
        src: '/screenshots/portal-sidebar.png',
        alt: 'Боковая панель навигации',
        caption: 'Боковая панель с основными разделами портала',
      },
      {
        type: 'table',
        headers: ['Раздел', 'Назначение'],
        rows: [
          ['Главная', 'Стартовая страница портала: входная группа компании, а под ней — дорожная карта развития (что сделано, что в планах, кнопка «Предложить идею в YUC»)'],
          ['Отделы', 'Страницы подразделений (Продажи, Маркетинг, R&D, IT и другие) — здесь живут все рабочие вкладки и модули'],
          ['Инструкции', 'Этот раздел — руководства и справочные материалы'],
        ],
      },
      {
        type: 'note',
        text: 'Разделы «Пользователи» и «Настройки» открываются из меню профиля (карточка с вашим именем внизу боковой панели). Там же — кнопка уведомлений. Плавающая круглая кнопка в углу экрана — командная панель: чат с коллегами, задачи между сотрудниками, заявки на доработку портала и AI-чат. Значок непрочитанного на кнопке подсвечивается цветом того раздела, где есть новое. Долгое нажатие (или правый клик) на кнопке раскрывает быстрые действия: сразу спросить AI, поставить задачу или написать коллеге. А сочетание Ctrl+K (⌘K на Mac) открывает командную палитру — быстрый поиск по действиям, разделам портала и коллегам с клавиатуры.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Свернуть и развернуть панель', level: 3 },
      {
        type: 'paragraph',
        text: 'Нажмите кнопку со стрелкой в правом верхнем углу боковой панели, чтобы свернуть её до иконок. Это освобождает рабочее пространство. Повторное нажатие разворачивает панель. Состояние сохраняется между сессиями браузера. В свёрнутой панели значок со зданием — это «Отделы»: нажатие выкидывает список вправо, а нажатие на отдел в нём раскрывает его разделы — так же, как в широкой панели, разворачивать её для этого не нужно.',
      },

      { type: 'heading', text: 'Отделы, разделы и подразделы', level: 2 },
      {
        type: 'paragraph',
        text: 'Вся навигация живёт в боковой панели одним деревом: отделы списком, а разделы отдела («Дашборд», «Ассортиментная матрица», «Закуп/Доставка» и другие) раскрываются прямо под своим отделом. Набор разделов зависит от ваших прав — то, к чему доступа нет, в меню не появится.',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Раскройте отдел в боковой панели',
            description: 'Нажмите на название отдела — под ним раскроется список его разделов. Чтобы панель осталась широкой, нажмите «Закрепить» внизу; состояние запоминается.',
          },
          {
            title: 'Выберите раздел',
            description: 'Клик по разделу открывает его сразу — с первым доступным вам подразделом. Отмеченное в меню и открытое на экране всегда совпадают.',
          },
          {
            title: 'Переключайте подразделы строкой над содержимым',
            description: 'Под названием раздела идёт строка подразделов («Ассортимент · Поиск новинок · Воронка · Просчёт заказа»). Портал помнит, на каком подразделе вы остановились в КАЖДОМ разделе: ушли в другой раздел и вернулись — увидите то же, что оставили. Перезагрузка страницы это тоже сохраняет.',
          },
        ],
      },

      { type: 'heading', text: 'Приложения: Figma, Miro и Google прямо в портале', level: 2 },
      {
        type: 'paragraph',
        text: 'ВРЕМЕННО СКРЫТО (август 2026): правая панель отдела убрана с экрана, добавленные приложения сохранены и вернутся вместе с ней. Ниже — как это работает. Правая панель на странице отдела — это «Приложения»: ссылки на файлы во внешних сервисах, которые открываются внутри портала, без переключения вкладок браузера. Удобно для того, на что смотрят регулярно: оргсхема в Miro, макеты в Figma, рабочая таблица в Google, календарь совещаний. Поддерживаются Figma, Miro, Google Документы/Таблицы/Презентации/Формы/Календарь/Диск, YouTube и публичные страницы Notion.',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Возьмите ссылку в самом сервисе',
            description: 'В Figma: Share → Get embed code → Copy. В Miro: Share → Embed. Для Google-документа, таблицы, презентации или файла на Диске достаточно обычной ссылки из адресной строки. Для Google Календаря нужен именно код для вставки: «Настройки календаря → Интеграция календаря → Код для вставки». Портал понимает и код, и просто ссылку.',
          },
          {
            title: 'Нажмите «+ Добавить» внизу правой панели',
            description: 'Введите название, вставьте ссылку или код и выберите, кому это видно.',
          },
          {
            title: 'Откройте кликом по названию',
            description: 'Файл покажется в рабочей области. Кнопка «Открыть в сервисе» рядом — если нужно редактировать.',
          },
          {
            title: 'Свернули панель — наведите на значок',
            description: 'В свёрнутой полоске приложения показаны кружками. При наведении влево выезжает подпись с названием и сервисом, клик открывает приложение. Полоска прокручивается, если приложений много.',
          },
          {
            title: 'Правый клик по значку — быстрое меню',
            description: 'Клик правой кнопкой по приложению (в списке или в свёрнутой полоске) открывает своё меню: «Открыть здесь», «Открыть в сервисе», «Изменить», «Удалить». Изменить и удалить видны тем, кто может править приложение.',
          },
        ],
      },
      {
        type: 'table',
        headers: ['Кому видно', 'Что это значит'],
        rows: [
          ['Общие', 'Видят все сотрудники портала. Добавлять в этот список может только администратор — его видит вся компания.'],
          ['Отдел', 'Видят сотрудники этого отдела. Добавить может любой, у кого есть доступ к отделу.'],
          ['Мои', 'Видите только вы. Личные ссылки не показываются никому, включая администратора.'],
        ],
      },
      {
        type: 'note',
        text: 'Приложения работают ТОЛЬКО на просмотр — редактируют в самом сервисе, это его ограничение, а не портала. Второе: файл откроется, лишь если у вас есть доступ к нему в самом сервисе (Figma, Miro, Google) — портал не подставляет свою авторизацию, и без доступа вы увидите внутри рамки экран входа этого сервиса. Третье: часть сервисов запрещает встраивание — например, обычные страницы Notion; из Notion работают только публичные страницы вида notion.site.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Профиль и выход из системы', level: 2 },
      {
        type: 'paragraph',
        text: 'В нижней части боковой панели расположена карточка вашего профиля — имя и должность. Нажмите на неё, чтобы открыть меню.',
      },
      {
        type: 'list',
        items: [
          '**Профиль** — просмотр и редактирование личных данных',
          '**Настройки** — параметры отображения и персонализации',
          '**Выйти** — завершение сеанса и возврат на страницу входа',
        ],
      },

      { type: 'heading', text: 'Переключение светлой и тёмной темы', level: 2 },
      {
        type: 'paragraph',
        text: 'Кнопка с иконкой луны (тёмная тема) или солнца (светлая тема) расположена в верхней части боковой панели. Одно нажатие переключает между режимами. Выбранная тема сохраняется автоматически.',
      },

      { type: 'heading', text: 'Страница настроек', level: 2 },
      {
        type: 'paragraph',
        text: 'Раздел «Настройки» (из меню профиля) — это платформенные настройки для администраторов: **Конструктор темы**, **Модули по отделам** (какие инструменты видны в каждом отделе), **Продажи · WB** (кабинеты, налоги, синхронизация, себестоимость), Разделы перечислены списком слева — сетки карточек больше нет, переход в один клик. **Настройки модулей** — «Синхронизации и фоновые задачи» (частота всех синков и их состояние), «Найм: подключение hh.ru», а также настройки Отзывов WB, Отчётов креаторов и Этикеток и **Клиенты, кабинеты и токены** (кому принадлежат данные и какими ключами портал ходит наружу). С 05.08 настройки модулей больше НЕ живут внутри рабочих экранов — ни шестерёнкой, ни ссылкой. Единственный вход — эта страница.',
      },

      { type: 'heading', text: 'Синхронизации и фоновые задачи', level: 3 },
      {
        type: 'paragraph',
        text: 'Раздел **«Настройки модулей → Синхронизации»** собирает все фоновые задачи портала: суточный синк WB-Аналитики, остатки, поиск и округа, комиссии WB, скидку «WB Клуба», логистику по габаритам, отзывы, МойСклад, hh.ru, тарифы складов, рассылки Telegram и cron сценариев. У каждой видно, **как часто она ходит, когда отработала в последний раз и чем кончилось** — успешно, с ошибкой или пропущена (и почему: «кабинет не подключён», «данные ещё свежие»). Правка частоты применяется без перезапуска портала.',
      },
      {
        type: 'note',
        variant: 'warning',
        text: 'Заходить сюда каждый день не нужно: если задача упала или давно молчит, портал сам скажет об этом в **колокольчике уведомлений** (слева внизу). Строка «Фоновая задача не отработала» означает ошибку — данные по этой части не обновляются; «Синхронизация давно молчит» — задача не отрабатывала дольше трёх своих периодов. Клик ведёт прямо сюда. Уведомление видят только администраторы. Появилось 23.08.2026 после случая, когда три задачи — в том числе расчёт логистики для юнит-экономики — лежали в ошибке больше суток и этого никто не заметил.',
      },
      {
        type: 'note',
        variant: 'warning',
        text: 'Задача, чей последний прогон старше двух её интервалов, подсвечивается: «похоже, не идёт». Это главное, ради чего раздел появился — раньше молча не отработавший ночной синк обнаруживался только на следующий день по неверным цифрам в отчёте.',
      },
      {
        type: 'list',
        items: [
          'Выключатель есть не у всех. Рассылки Telegram уходят живым людям, cron гасит в том числе оповещения о падениях, а тик планировщика — это частота опроса, а не задача: у них можно менять только частоту.',
          'Расписание ночного автосинка WB (включён, час запуска, глубина, тянуть ли рекламу) задаётся тут же, отдельно для каждого кабинета.',
          'Расписание отзывов по кабинетам и метрики креаторов настраиваются в своих разделах — здесь только их состояние и ссылка, чтобы одно значение не правилось в двух местах.',
        ],
      },

      { type: 'heading', text: 'Найм: подключение hh.ru', level: 3 },
      {
        type: 'paragraph',
        text: 'Реквизиты приложения hh.ru (Client ID, Client Secret, Redirect URI, User-Agent), авторизация кабинета работодателя и ручная синхронизация. Раньше это открывалось кнопкой «hh.ru» в шапке вкладки «Найм» — теперь только здесь. «Цены размещений hh.ru» и «Стоимость найма» остались в HR: они по правам отдела, а не платформы.',
      },

      { type: 'heading', text: 'Клиенты, кабинеты и токены', level: 3 },
      {
        type: 'paragraph',
        text: 'Страница **«Клиенты, кабинеты и токены»** показывает, кому принадлежат данные портала и какими ключами он ходит во внешние сервисы: клиент → ИП → кабинет площадки → его токены со сроками. Нужна она потому, что один и тот же кабинет Wildberries заведён в трёх модулях отдельно и под разными названиями: в аналитике и отзывах он «ИП Титкова», а в этикетках — «Enmoone». Здесь видно, что за всеми тремя записями стоит один ИП одного клиента — и сразу же, какие у него ключи и когда они заканчиваются.',
      },
      {
        type: 'list',
        items: [
          '**Три счётчика наверху** — «истекают», «не заданы», «кабинетов ничьих». Это не украшение: клик по счётчику переключает страницу в плоский список только этих ключей, и заменить нужный можно в один клик.',
          '**ИП — плитка с логотипом и кольцом**: кольцо показывает долю живых ключей, цвет — по худшему, число внутри — сколько всего ключей. Клик открывает попап прямо у плитки — со всеми кабинетами, ключами и настройками владения.',
          '**Правка структуры — только в попапе ИП.** Бренд, клиент, привязка кабинета к ИП, переименование, логотип — всё там. В списках селектов нет намеренно: они нужны раз в квартал, а место занимали всегда.',
          '**«Общие ключи портала»** — то, что кабинету не принадлежит: Telegram-боты, Apify, hh.ru, МойСклад, ключ ИИ. Там же каталог «что ещё можно подключить».',
          '**Мелочи:** Esc закрывает попап, клик по id кабинета копирует его, у срока в подсказке полная дата.',
        ],
      },
      {
        type: 'list',
        items: [
          '**Связи расставляются сами** — портал сличает кабинеты по продавцу Wildberries, зашитому внутрь токена. Такие связи помечены «определено автоматически». Кнопка «Определить автоматически» пересчитывает их заново.',
          '**Выбор ИП в строке кабинета** закрепляет связь вручную — после этого автоматика её не переигрывает.',
          '**Блок «Не привязаны к ИП»** внизу — кабинеты, заведённые в модуле, про которые неизвестно, чьи они. Их стоит разобрать: пока кабинет ничей, портал не знает, в чей отчёт попадут его продажи.',
          '**Кабинет площадки заводится только здесь** — кнопкой «+ Кабинет» в карточке ИП: выбираете модуль (аналитика, отзывы, этикетки), вставляете токен. Кабинет создаётся в нужном модуле, сразу привязывается к ИП, а токен уходит в шифрованное хранилище. В самих модулях формы создания больше нет.',
          '**Название кабинета правится только здесь.** В модулях (WB-Аналитика, Отзывы, Этикетки) при добавлении кабинета выбирается ИП, а название подставляется из справочника — придумать своё там больше нельзя. Переименуете ИП — название поменяется сразу во всех трёх модулях и в реестре токенов.',
          '**Клиент** — верхний уровень. Пока клиент один (Young Unicorn), но справочник рассчитан на несколько: у клиента может быть сколько угодно ИП, у ИП — свой бренд.',
          '**Добавить клиента и ИП** можно прямо здесь: кнопка «+ Клиент» вверху и «+ ИП» внутри карточки клиента. Спрашивается только название — служебный код портал придумывает сам.',
          '**Бренды** ведутся внизу этой же страницы: добавить, переименовать по клику, загрузить логотип. Бренд, на который ссылаются заказы, ассортимент или ИП, удалить нельзя — иначе данные остались бы с битой ссылкой. Раньше ИП и бренды жили в R&D → «Справочники»; с 04.08 они здесь, а там остались справочники закупа — подрядчики, ткани, логистика.',
          '**Названия правятся по клику** — и у ИП, и у бренда. Переименование ИП меняет название кабинета сразу во всех трёх модулях и в реестре токенов: они читают его отсюда.',
        ],
      },
      {
        type: 'note',
        text: 'На работу модулей справочник пока не влияет: кабинеты по-прежнему заводятся внутри своих модулей. Он нужен, чтобы связь «чей это кабинет» существовала и была видна — восстанавливать её задним числом, когда клиентов станет несколько, будет уже не по чему.',
        variant: 'info',
      },

      { type: 'heading', text: 'Все токены портала в одном списке', level: 3 },
      {
        type: 'paragraph',
        text: 'Внизу страницы **«Интеграции и токены»** — реестр всех ключей, которыми портал ходит во внешние сервисы: кабинеты Wildberries, отзывы, этикетки, Telegram-боты, Apify, hh.ru, МойСклад. По каждому видно: настроен ли, **когда заканчивается**, и его можно **сразу заменить кнопкой «Изменить»** — не уходя в модуль. Сами значения не показываются: только последние 4 символа, чтобы отличать один ключ от другого.',
      },
      {
        type: 'paragraph',
        text: 'При замене форма сначала **разбирает вставленный токен**: показывает, до какого числа он действует и даёт ли он только чтение. Так видно, что вставлен нужный ключ, ещё до сохранения. Если этот же токен записан в нескольких местах, форма предложит **обновить их все разом** и перечислит, какие именно — для Wildberries это три записи, и пропустить одну означает оставить часть портала со старым ключом.',
      },
      {
        type: 'note',
        text: 'После сохранения портал сам доделывает то, что обычно забывают: переустанавливает вебхук Telegram-бота (его секрет зависит от токена), перезапускает бота отчётов и проверяет кабинет отзывов новым ключом. Что именно сделано — написано в зелёной плашке после сохранения.',
        variant: 'info',
      },
      {
        type: 'list',
        items: [
          '**Сверху — предупреждение** о ключах, которым осталось меньше двух недель. Токены Wildberries живут ограниченное время, и когда срок выходит, тихо перестают работать все выгрузки — теперь это видно заранее.',
          '**Метка «копия»** означает, что этот же ключ записан ещё в одном месте. У WB так и есть: один токен кабинета используется и в аналитике, и в рекламе, и в этикетках — при замене поменять нужно везде, иначе часть портала останется со старым.',
          '**Метка «без шифрования»** — значение лежит в базе открытым текстом. Ключи, которыми управляет сама эта страница, шифруются всегда; у токенов из старых модулей так исторически не было.',
          '**«Только чтение»** у токена Wildberries означает, что им можно забирать данные, но не менять что-либо в кабинете.',
        ],
      },
      {
        type: 'note',
        text: 'Реестр видят только администраторы платформы. Он ничего не меняет — это карта: где искать нужный ключ и сколько ему осталось жить.',
        variant: 'info',
      },
      {
        type: 'note',
        text: 'Изменение email или пароля выполняется через администратора платформы.',
        variant: 'info',
      },

      { type: 'heading', text: 'Часто задаваемые вопросы', level: 2 },
      {
        type: 'table',
        headers: ['Вопрос', 'Ответ'],
        rows: [
          ['Не вижу нужный раздел в меню', 'Доступ к разделу может быть закрыт. Обратитесь к администратору для выдачи прав.'],
          ['Портал работает медленно', 'Попробуйте обновить страницу (F5). Если проблема повторяется — сообщите администратору.'],
          ['Как изменить пароль?', 'Обратитесь к администратору платформы — самостоятельная смена пароля недоступна.'],
          ['Могу ли я работать с мобильного?', 'Да, портал адаптирован для мобильных устройств. Боковая навигация скрыта — откройте её кнопкой меню в левом верхнем углу.'],
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // CREATOR REPORTS
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'marketing-full-cycle',
    category: 'creator-reports',
    title: 'Отдел Маркетинг: полный цикл работы',
    description: 'Простыми словами обо всём отделе: креаторы и Telegram-бот, блогеры и бартеры, интеграции и ROMI, атрибуция продаж WB по подменным артикулам, выплаты. Весь путь от заведения партнёра до выплаты и оценки окупаемости.',
    icon: '📣',
    updatedAt: '2026-08-04',
    blocks: [
      {
        type: 'paragraph',
        text: 'Отдел «Маркетинг» ведёт всю рекламу товаров: публикации наших штатных креаторов, закупку размещений у внешних блогеров, бартеры «товар за рекламу» — и считает, окупается ли каждое размещение. Всё, что размещено, автоматически собирает метрики (просмотры, лайки) и, если привязать подменный артикул, показывает реальные продажи Wildberries с этой рекламы.',
      },
      {
        type: 'note',
        text: 'Эта статья — обзорная карта всего отдела и порядок работы «от и до». По отдельным инструментам есть более детальные статьи: «Отчёты создателей: полное руководство» и «Telegram-бот для создателей».',
        variant: 'tip',
      },

      { type: 'heading', text: 'Пять вкладок отдела', level: 2 },
      {
        type: 'paragraph',
        text: 'Отдел открывается по кнопке «Маркетинг» в левом меню. Сверху — пять вкладок, они идут в логике «сначала общая картина и деньги, потом конкретная работа»:',
      },
      {
        type: 'table',
        headers: ['Вкладка', 'Для чего'],
        rows: [
          ['Дашборд', 'Общая сводка отдела за период: сколько публикаций, просмотров, средняя вовлечённость, топ креаторов и постов.'],
          ['Интеграции', 'Все закупки размещений (у блогеров и бартеров) в одном месте: план, факт, окупаемость (ROMI). Это «пульт руководителя».'],
          ['Креаторы', 'Публикации наших штатных креаторов: рейтинг по просмотрам, журнал публикаций, план, выплаты.'],
          ['Блогеры', 'Справочник внешних блогеров (закупка рекламы за деньги): контакты, подписчики, охваты, стоимость.'],
          ['Бартеры', 'То же самое, но для партнёров, работающих по бартеру (товар за рекламу).'],
        ],
      },
      {
        type: 'image',
        src: '/help-media/mkt-dashboard.png',
        alt: 'Дашборд отдела Маркетинг: активных креаторов, публикаций, просмотров, средний ER, графики по платформам',
        caption: 'Дашборд: быстрая сводка за последние 30 дней — публикации и просмотры по платформам, сколько метрик уже собрано.',
      },
      {
        type: 'note',
        text: 'Значок «?» есть почти у каждого показателя и столбца. Наведите на него — во всплывающей подсказке простыми словами написано, что это и как считается.',
        variant: 'tip',
      },

      { type: 'divider' },

      { type: 'heading', text: 'Часть 1. Штатные креаторы (вкладка «Креаторы»)', level: 2 },
      {
        type: 'paragraph',
        text: 'Штатные креаторы — это наши сотрудники, которые снимают видео (Reels, TikTok, Shorts). Они не заходят в портал: публикацию заводит Telegram-бот. Ваша задача — один раз добавить креатора и следить за результатами.',
      },
      {
        type: 'image',
        src: '/help-media/mkt-creators.png',
        alt: 'Вкладка Креаторы: рейтинг просмотров по креаторам и панель кнопок',
        caption: 'Креаторы: сверху рейтинг «Просмотры по креаторам» за выбранный период, справа — кнопки работы (Список креаторов, Выплаты, Импорт и др.).',
      },
      { type: 'heading', text: 'Как завести креатора', level: 3 },
      {
        type: 'steps',
        steps: [
          { title: 'Откройте «Список креаторов»', description: 'Кнопка в панели справа над таблицей. Это справочник всех штатных креаторов.' },
          { title: 'Нажмите «Добавить» и заполните ФИО', description: 'Имя показывается в рейтинге, таблицах и выплатах.' },
          { title: 'Получите Telegram ID креатора', description: 'Попросите креатора написать нашему боту команду /myid — бот пришлёт число. Впишите его в поле Telegram ID (без него бот не примет публикации именно от этого человека).' },
          { title: 'Сохраните', description: 'После сохранения креатор может присылать видео боту. Старые публикации задним числом не появятся.' },
        ],
      },
      { type: 'heading', text: 'Как креатор присылает видео (через бота)', level: 3 },
      {
        type: 'ordered-list',
        items: [
          'Креатор пишет боту команду /start — бот здоровается и объясняет, что делать.',
          'Креатор отправляет одним сообщением ссылку на видео и через пробел артикул WB (например: ссылка на Reels и рядом 987654321).',
          'Бот определяет площадку (Instagram / TikTok / YouTube / VK), создаёт публикацию за сегодняшнюю дату и отвечает «✅ Принято».',
          'Через несколько минут система сама подтягивает метрики (просмотры, лайки, комментарии) — строка появляется в журнале публикаций.',
        ],
      },
      {
        type: 'note',
        text: 'Почему просмотры не появились сразу — это нормально. Метрики собираются в фоне через сервис Apify (для Instagram — специальным актором). Данные подтянутся в течение нескольких минут; если долго нет — см. раздел про Настройки в конце.',
        variant: 'info',
      },
      { type: 'heading', text: 'Кнопки на вкладке «Креаторы»', level: 3 },
      {
        type: 'table',
        headers: ['Кнопка', 'Что делает'],
        rows: [
          ['Список креаторов', 'Справочник штатных креаторов (ФИО, Telegram, даты работы).'],
          ['Выплаты', 'Расчёт зарплаты креаторов за месяц (подробно — в Части 4).'],
          ['Подменные', 'Пул подменных артикулов и загрузка отчёта ANS (подробно — в Части 5).'],
          ['Импорт XLSX', 'Массово загрузить публикации из Excel (удобно перенести историю). Новые креаторы создаются сами.'],
          ['Экспорт', 'Выгрузить то, что сейчас в таблице (по фильтрам), в Excel.'],
          ['Дозабрать новые', 'Собрать метрики только для публикаций, у которых их ещё нет (бережёт лимит Apify).'],
          ['Обновить все', 'Пересобрать метрики по всем публикациям — тратит лимит Apify, спросит подтверждение.'],
          ['План (в рейтинге)', 'Задать каждому креатору план просмотров на месяц — полоса рейтинга покажет факт относительно плана.'],
        ],
      },
      {
        type: 'paragraph',
        text: 'Над таблицей — фильтр по датам и поиск (по имени креатора, его @nick или артикулу). Рейтинг «Просмотры по креаторам» разбивает полосу каждого креатора по площадкам; при наведении видны просмотры, лайки и комментарии.',
      },
      {
        type: 'heading', text: 'Журнал публикаций — что в столбцах', level: 3,
      },
      {
        type: 'table',
        headers: ['Столбец', 'Что показывает'],
        rows: [
          ['Дата', 'Дата публикации.'],
          ['Креатор', 'Кто автор (из справочника).'],
          ['Артикул', 'Артикул WB, к которому относится видео (можно вписать/поправить в строке).'],
          ['Подменный', 'Подменный артикул для атрибуции — при наведении показывает реальные метрики WB (см. Часть 5).'],
          ['Статус', 'Опубликовано / Черновик.'],
          ['Просмотры / Лайки / Комм. / Сохр.', 'Метрики поста (собираются автоматически).'],
          ['ER%', 'Вовлечённость = (лайки + комментарии) ÷ просмотры × 100%. Чем выше, тем живее реагирует аудитория.'],
        ],
      },

      { type: 'divider' },

      { type: 'heading', text: 'Часть 2. Внешние блогеры и бартеры (справочники)', level: 2 },
      {
        type: 'paragraph',
        text: 'Внешние блогеры — это те, у кого мы покупаем рекламу за деньги (вкладка «Блогеры») или обмениваем товар на рекламу (вкладка «Бартеры»). Обе вкладки устроены одинаково и делятся на три раздела (переключатель сверху): База · Закупки · Подменные.',
      },
      {
        type: 'table',
        headers: ['Раздел', 'Что внутри'],
        rows: [
          ['База', 'Справочник партнёров: ник, ФИО, платформа, подписчики, охват, средние просмотры, стоимость, вирусность, топ товаров, аватар.'],
          ['Закупки', 'Размещения именно с этими партнёрами (это те же интеграции, отфильтрованные по типу).'],
          ['Подменные', 'Подменные артикулы, выданные этим партнёрам (для атрибуции продаж WB).'],
        ],
      },
      {
        type: 'image',
        src: '/help-media/mkt-bloggers.png',
        alt: 'Вкладка Блогеры, раздел База: таблица блогеров с подписчиками, охватом, стоимостью, вирусностью',
        caption: 'База блогеров: подписчики, средние просмотры, стоимость и прочее. Часть цифр подтягивается автоматически, часть заполняется вручную.',
      },
      { type: 'heading', text: 'Как наполнить базу', level: 3 },
      {
        type: 'ordered-list',
        items: [
          '«Добавить» — завести блогера вручную: платформа, ник, ФИО, ссылка на профиль, подписчики, охват, стоимость и т.д. Можно загрузить аватар.',
          '«Импорт XLSX» — загрузить сразу многих из Excel (ссылка, подписчики, средние просмотры, вирусность, топ товаров). Повторы по нику пропускаются.',
          'Кнопка обновления статистики (⟳ у строки или массовая) — сама подтянет из открытых источников подписчиков, средние просмотры и аватар. Стоимость и охват вносятся руками (это договорённость с блогером).',
        ],
      },
      {
        type: 'note',
        text: 'Главное действие в базе — кнопка «Согласовать» у партнёра. Она создаёт по нему размещение (интеграцию) и переносит его во вкладку «Интеграции» / «Закупки», где вы дальше вносите условия и результат. То есть база — это «телефонная книга», а согласование — старт конкретной сделки.',
        variant: 'tip',
      },

      { type: 'divider' },

      { type: 'heading', text: 'Часть 3. Интеграции — размещения и окупаемость', level: 2 },
      {
        type: 'paragraph',
        text: 'Вкладка «Интеграции» — сердце отдела. Здесь в одной таблице все размещения (и коммерческие, и бартерные), их план и факт, и главное — окупаемость ROMI. Наверху — сводка: средний ROMI, сколько потрачено, выручка с заказов, чистая прибыль, число заказов и интеграций, разбивка «по типу» (Коммерция/Бартер) и топ партнёров по окупаемости.',
      },
      {
        type: 'image',
        src: '/help-media/mkt-integrations.png',
        alt: 'Вкладка Интеграции: KPI ROMI/потрачено/выручка/прибыль, разбивка по типу, топ по ROMI, таблица размещений',
        caption: 'Интеграции: сверху деньги и окупаемость, ниже — таблица всех размещений с планом, фактом и ROMI.',
      },
      { type: 'heading', text: 'Как завести и провести размещение', level: 3 },
      {
        type: 'steps',
        steps: [
          { title: 'Создайте интеграцию', description: 'Проще всего — кнопкой «Согласовать» у партнёра в базе (Часть 2). Либо на вкладке «Интеграции» нажмите «Добавить из базы» и выберите партнёра.' },
          { title: 'Заполните договорённость', description: 'Откройте размещение (клик по строке). Блок «Договорённость»: площадка, формат (пост/reels/…), дата выхода, стоимость (для бартера — себестоимость товара + логистика), подписчики, промокод, артикулы.' },
          { title: 'После выхода — внесите факт', description: 'Блок «Контент (факт)»: вставьте ссылку на вышедший пост и нажмите «Обновить метрики» — система подтянет просмотры/лайки/комментарии. Впишите фактический охват.' },
          { title: 'Привяжите продажи', description: 'Блок «Продажи · атрибуция (WB)»: выберите подменный артикул (Часть 5) — тогда портал сам покажет заказы, корзину и просмотры карточки WB именно с этого размещения. Или впишите заказы/выручку вручную по промокоду.' },
          { title: 'Смотрите ROMI', description: 'Блок «Экономика» и колонка ROMI покажут, окупилось ли размещение.' },
        ],
      },
      { type: 'heading', text: 'Откуда берутся плановые цифры и ROMI', level: 3 },
      {
        type: 'paragraph',
        text: 'Пока нет фактических продаж, портал прикидывает воронку по средним коэффициентам — чтобы заранее понимать, стоит ли размещение своих денег:',
      },
      {
        type: 'table',
        headers: ['Показатель', 'Как считается'],
        rows: [
          ['CPV (цена за просмотр)', 'Стоимость ÷ охват. Ориентир: до 2 ₽ — хорошо, до 3 ₽ — терпимо, дороже — красный.'],
          ['Клики (план)', 'Охват × 5% (столько людей в среднем переходят).'],
          ['Продажи (план)', 'Клики × 3% (столько из перешедших покупают).'],
          ['Выручка (план)', 'Продажи × 1500 ₽ (средний чек).'],
          ['Маржа', 'Выручка × 20% (сколько остаётся после расходов на товар).'],
          ['ROMI', '(Выручка × маржа − стоимость) ÷ стоимость × 100%. Больше 0 — реклама отбилась; ≥100% зелёный, <0 красный.'],
        ],
      },
      {
        type: 'note',
        text: 'Коэффициенты 5% / 3% / 1500 ₽ / 20% — усреднённые ориентиры для плана. Как только появляются реальные заказы (через подменный артикул или промокод), ROMI пересчитывается по факту, а не по прикидке.',
        variant: 'info',
      },

      { type: 'divider' },

      { type: 'heading', text: 'Часть 4. Выплаты креаторам', level: 2 },
      {
        type: 'paragraph',
        text: 'Кнопка «Выплаты» на вкладке «Креаторы» открывает расчёт зарплаты за месяц. Считается всё автоматически из публикаций и их просмотров — руками вводить количество видео не нужно.',
      },
      {
        type: 'paragraph',
        text: 'Формула простая: Зарплата = Оклад + Бонусы за просмотры (KPI) ± ручная корректировка. Оклад = число видео за месяц × ставка категории креатора. Бонус KPI начисляется за каждое видео по тому порогу просмотров, который оно взяло (10 000 / 100 000 / 500 000 / 1 000 000). «На карту» — это сумма за вычетом налога (−13% для ГПХ, −6% для самозанятых).',
      },
      {
        type: 'steps',
        steps: [
          { title: 'Выберите месяц', description: 'Стрелками ‹ ›. При открытии портал сам покажет прошлый месяц, если текущий ещё пустой.' },
          { title: 'Проверьте категорию каждого креатора', description: 'От неё зависят ставка за видео и налог. Видео и KPI-пороги проставляются автоматически из метрик.' },
          { title: 'При необходимости — корректировка', description: 'Столбец «±Корр.» и комментарий: разовая надбавка или вычет с пояснением.' },
          { title: 'Отметьте «Выплачено»', description: 'Тумблер фиксирует расчёт (снапшот). Даже если метрики видео потом подрастут, выплаченная строка не изменится.' },
        ],
      },

      { type: 'divider' },

      { type: 'heading', text: 'Часть 5. Подменные артикулы — как считаются продажи с рекламы', level: 2 },
      {
        type: 'paragraph',
        text: 'Подменный артикул — это временный «дублёр» вашего товара на WB, который выдают под конкретную рекламу. Когда покупатель переходит по рекламе и попадает на подменный артикул, Wildberries отдельно считает по нему просмотры карточки, добавления в корзину, заказы и избранное. Так мы точно знаем, сколько продаж принесло именно это видео или размещение.',
      },
      {
        type: 'ordered-list',
        items: [
          'В кабинете WB выгрузите отчёт ANS (файл Excel) с данными по подменным артикулам.',
          'Откройте раздел «Подменные» (кнопка на вкладке «Креаторы», либо раздел «Подменные» у Блогеров/Бартеров) и нажмите «Импорт XLSX».',
          'Привяжите подменный артикул к публикации креатора или к интеграции (поле «Подменный»).',
          'Готово: в строке автоматически появятся метрики WB (просмотры карточки, в корзину, заказы, избранное), а в интеграции — реальный ROMI по факту продаж.',
        ],
      },

      { type: 'divider' },

      { type: 'heading', text: 'Весь цикл коротко', level: 2 },
      {
        type: 'steps',
        steps: [
          { title: '1. Завели участника', description: 'Штатного креатора — в «Список креаторов» (+ Telegram ID). Внешнего блогера/бартера — в «Базу».' },
          { title: '2. Разместили рекламу', description: 'Креатор шлёт видео боту. По блогеру — «Согласовать» → в «Интеграциях» вносим условия и после выхода ссылку/охват.' },
          { title: '3. Собрали метрики', description: 'Просмотры/лайки подтягиваются сами. По размещению — «Обновить метрики» по ссылке поста.' },
          { title: '4. Привязали продажи WB', description: 'Загрузили отчёт ANS и привязали подменный артикул — появились реальные заказы и ROMI.' },
          { title: '5. Оценили и заплатили', description: 'На «Интеграциях» — окупаемость размещений, на «Дашборде» — общая картина, в «Выплатах» — зарплата креаторов.' },
        ],
      },

      { type: 'divider' },

      { type: 'heading', text: 'Настройки (шестерёнка — только администраторам)', level: 2 },
      {
        type: 'paragraph',
        text: 'Кнопка-шестерёнка на вкладке «Креаторы» открывает настройки. Они нужны, чтобы всё работало автоматически, и доступны только администраторам платформы:',
      },
      {
        type: 'table',
        headers: ['Настройка', 'Зачем'],
        rows: [
          ['Токен Telegram-бота', 'Чтобы бот принимал видео от креаторов (берётся у @BotFather).'],
          ['Токены Apify', 'Ключи для сбора метрик из соцсетей. Можно несколько — когда у одного кончается лимит, берётся следующий.'],
          ['Токен ВКонтакте', 'Для метрик видео из ВК (официальный доступ VK).'],
          ['Авто-синхронизация', 'Расписание автоматического обновления метрик: дни недели, время (МСК) и на сколько дней назад обновлять.'],
        ],
      },
      {
        type: 'note',
        text: 'Если метрики перестали обновляться несколько часов — первым делом проверьте токены Apify в настройках: возможно, закончился баланс или лимит токена.',
        variant: 'warning',
      },
    ],
  },

  {
    slug: 'creator-reports-overview',
    category: 'creator-reports',
    title: 'Отчёты создателей: полное руководство',
    description: 'Вкладки модуля, метрики публикаций, работа с создателями, импорт/экспорт, Apify',
    icon: '🎬',
    updatedAt: '2026-07-06',
    blocks: [
      {
        type: 'paragraph',
        text: 'Инструменты маркетинговой команды живут на верхних вкладках отдела «Маркетинг»: Интеграции, Креаторы, Блогеры и Бартеры. Штатные креаторы присылают ссылки на свои видео через Telegram-бота, система собирает метрики (просмотры, лайки, комментарии) через Apify и строит аналитику по каждому креатору; закупки у внешних блогеров ведутся через Интеграции с автоматической атрибуцией продаж WB по подменным артикулам.',
      },
      {
        type: 'image',
        src: '/screenshots/creator-reports-main.png',
        alt: 'Главная страница модуля Отчёты создателей',
        caption: 'Главная страница модуля с полосой статистики по платформам и вкладками',
      },

      { type: 'heading', text: 'Полоса статистики по платформам', level: 2 },
      {
        type: 'paragraph',
        text: 'В верхней части модуля расположены четыре карточки — по одной на каждую поддерживаемую платформу. Каждая карточка показывает количество публикаций за выбранный период.',
      },
      {
        type: 'table',
        headers: ['Платформа', 'Иконка', 'Что считается'],
        rows: [
          ['Instagram', '📸', 'Reels, посты и IGTV, отправленные через бота или импортированные'],
          ['TikTok', '🎵', 'Видео из TikTok всех форматов'],
          ['YouTube', '▶️', 'Видео и Shorts из YouTube'],
          ['ВКонтакте', '💙', 'Видео из VK'],
        ],
      },
      {
        type: 'paragraph',
        text: 'Нажмите на карточку платформы, чтобы отфильтровать таблицу и показать только публикации этой платформы. Повторное нажатие снимает фильтр.',
      },

      { type: 'heading', text: 'Структура вкладок отдела «Маркетинг»', level: 2 },
      {
        type: 'table',
        headers: ['Вкладка', 'Назначение'],
        rows: [
          ['Дашборд', 'Сводные показатели отдела: ROMI, публикации, динамика'],
          ['Интеграции', 'Закупки размещений у блогеров: план/факт воронки, экономика (CPV, CPC, ROMI), метрики WB по подменным артикулам'],
          ['Креаторы', 'Публикации штатных креаторов: рейтинг «Просмотры по креаторам», таблица публикаций с метриками, план на месяц'],
          ['Блогеры', 'Справочник внешних блогеров: База (все контакты и метрики аккаунтов) / Закупки / Подменные'],
          ['Бартеры', 'Аналогичный справочник бартерных партнёров'],
        ],
      },
      {
        type: 'note',
        text: 'Настройки (токены Apify, Telegram-бот, интервал синхронизации) открываются попапом по кнопке-шестерёнке и доступны только администраторам платформы.',
        variant: 'info',
      },

      { type: 'heading', text: 'Рейтинг креаторов', level: 2 },
      {
        type: 'paragraph',
        text: 'Над таблицей публикаций на вкладке «Креаторы» — рейтинг «Просмотры по креаторам» за период из фильтра дат: полоса каждого креатора разбита по площадкам, при наведении — карточка с просмотрами, лайками и комментариями. Если у креатора задан план (кнопка «План» в тулбаре), полоса показывает факт относительно плана.',
      },

      { type: 'heading', text: 'Инструменты вкладки «Креаторы»', level: 2 },
      {
        type: 'table',
        headers: ['Кнопка', 'Что делает'],
        rows: [
          ['Список креаторов', 'Справочник штатных креаторов: ФИО, Telegram, даты трудоустройства'],
          ['План', 'План просмотров/публикаций на месяц по каждому креатору'],
          ['Выплаты', 'Расчёт ЗП креаторов: оклад по числу видео + KPI по порогам просмотров, фиксация «Выплачено»'],
          ['Подменные', 'Пул подменных артикулов (ANS) и импорт XLSX'],
        ],
      },

      { type: 'heading', text: 'Таблица публикаций («Отчёты»)', level: 2 },
      {
        type: 'paragraph',
        text: 'Главный рабочий журнал всех публикаций. Каждая строка — одна публикация от одного создателя.',
      },
      {
        type: 'image',
        src: '/screenshots/creator-reports-table.png',
        alt: 'Таблица отчётов',
        caption: 'Основная таблица публикаций с метриками',
      },
      {
        type: 'table',
        headers: ['Колонка', 'Что показывает'],
        rows: [
          ['Дата', 'Дата публикации (или дата подачи через бота)'],
          ['Создатель', 'ФИО блогера из справочника'],
          ['Платформа', 'Иконка и название платформы'],
          ['Ссылка', 'Ссылка на видео — нажмите для перехода'],
          ['Артикул', 'Артикул WB, привязанный к публикации (если указан)'],
          ['Просмотры', 'Количество просмотров видео'],
          ['Лайки', 'Количество лайков'],
          ['Комментарии', 'Количество комментариев'],
          ['Сохранения', 'Количество сохранений (где доступно)'],
          ['ER%', 'Engagement Rate = (лайки + комментарии) / просмотры × 100%'],
          ['Статус метрик', 'Загружены ли метрики (✅ / ожидание)'],
        ],
      },
      {
        type: 'note',
        text: 'Метрики загружаются автоматически через Apify в фоне. Сразу после отправки публикации поле «Просмотры» может быть пустым — это нормально. Данные появятся в течение нескольких минут.',
        variant: 'info',
      },

      { type: 'heading', text: 'Импорт из XLSX', level: 3 },
      {
        type: 'paragraph',
        text: 'Кнопка «Импорт» позволяет загрузить публикации из файла Excel. Это удобно для массового добавления данных из Google Sheets или других источников.',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Подготовьте файл',
            description: 'Файл должен быть в формате XLSX. Убедитесь, что он содержит колонки с URL публикаций, ФИО создателя и датой.',
          },
          {
            title: 'Нажмите кнопку «Импорт»',
            description: 'Выберите файл на компьютере в появившемся диалоге.',
          },
          {
            title: 'Проверьте результат',
            description: 'После загрузки бот покажет сводку: создано записей, пропущено дубликатов, ошибки. Новые создатели добавляются автоматически.',
          },
        ],
      },

      { type: 'heading', text: 'Экспорт в XLSX', level: 3 },
      {
        type: 'paragraph',
        text: 'Кнопка «Экспорт» выгружает текущую отфильтрованную таблицу в Excel-файл. Это удобно для подготовки отчётов для внешних получателей.',
      },

      { type: 'heading', text: 'Подменные артикулы и атрибуция WB', level: 2 },
      {
        type: 'paragraph',
        text: 'К публикации креатора и к интеграции можно привязать **подменный артикул** (WW-код из отчётов ANS). После привязки в строке автоматически появляются метрики Wildberries: просмотры карточки, добавления в корзину, заказы и избранное — так факт продаж атрибуцируется конкретному видео или размещению.',
      },
      {
        type: 'note',
        text: 'Пул нераспределённых подменных артикулов и импорт отчётов ANS (XLSX) — по кнопке «Подменные» в тулбаре вкладки «Креаторы» и в разделе «Подменные» у Блогеров/Бартеров.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Справочник «Список креаторов»', level: 2 },
      {
        type: 'paragraph',
        text: 'Справочник всех зарегистрированных блогеров. Здесь хранятся их ФИО, Telegram-данные и статус активности.',
      },
      {
        type: 'table',
        headers: ['Поле', 'Описание'],
        rows: [
          ['ФИО', 'Полное имя создателя для отображения в таблицах и рейтингах'],
          ['Telegram username', 'Ник в Telegram (без @) для идентификации'],
          ['Telegram ID', 'Числовой ID аккаунта Telegram — необходим для работы бота'],
          ['Активен', 'Активные создатели могут отправлять публикации через бота'],
        ],
      },
      {
        type: 'note',
        text: 'Чтобы создатель мог использовать Telegram-бота, в его профиле должен быть указан числовой Telegram ID. Как его получить — описано в инструкции по боту.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Как добавить нового создателя', level: 3 },
      {
        type: 'steps',
        steps: [
          {
            title: 'Откройте «Список креаторов» в тулбаре вкладки «Креаторы»',
            description: 'Здесь хранится справочник всех штатных креаторов.',
          },
          {
            title: 'Нажмите «Добавить создателя»',
            description: 'Введите ФИО и контактные данные.',
          },
          {
            title: 'Укажите Telegram ID',
            description: 'Попросите создателя отправить команду /myid боту и передать вам полученный номер. Вставьте его в поле Telegram ID.',
          },
          {
            title: 'Сохраните запись',
            description: 'После сохранения создатель сможет отправлять публикации через бота. Предыдущие публикации не появятся автоматически.',
          },
        ],
      },

      { type: 'heading', text: 'Настройки (кнопка-шестерёнка, только админам)', level: 2 },
      {
        type: 'table',
        headers: ['Параметр', 'Описание'],
        rows: [
          ['Токен Apify', 'API-ключ для сбора метрик из социальных сетей. Получить в личном кабинете apify.com.'],
          ['Токен Telegram-бота', 'Токен бота из @BotFather. Необходим для приёма публикаций от создателей.'],
          ['Интервал синхронизации', 'Как часто обновлять метрики для уже загруженных публикаций.'],
        ],
      },
      {
        type: 'note',
        text: 'Без токена Apify метрики просмотров, лайков и комментариев не будут загружаться. Убедитесь, что на балансе Apify достаточно средств.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Часто задаваемые вопросы', level: 2 },
      {
        type: 'table',
        headers: ['Вопрос', 'Ответ'],
        rows: [
          ['Метрики не обновились уже несколько часов', 'Проверьте токены Apify в настройках (шестерёнка) — возможно, закончился баланс или истёк токен.'],
          ['Создатель говорит, что бот не принимает его сообщения', 'Проверьте, что в профиле создателя во вкладке «Создатели» указан правильный Telegram ID.'],
          ['Как привязать публикацию к артикулу после её загрузки?', 'Откройте запись в таблице «Отчёты» и отредактируйте поле «Артикул».'],
          ['Чем ER 3% отличается от ER 5%?', 'ER показывает вовлечённость аудитории. Выше — лучше. Нормой для Instagram считается 2–4%, для TikTok 5–8%.'],
        ],
      },
    ],
  },

  {
    slug: 'creator-bot-guide',
    category: 'creator-reports',
    title: 'Telegram-бот: инструкция для создателей',
    description: 'Как отправлять ссылки на публикации, добавлять артикулы WB, просматривать статистику',
    icon: '🤖',
    updatedAt: '2026-04-28',
    blocks: [
      {
        type: 'paragraph',
        text: 'Telegram-бот позволяет создателям отправлять ссылки на свои публикации прямо из Telegram — без входа в портал. Бот автоматически фиксирует публикацию и запускает сбор метрик.',
      },
      {
        type: 'note',
        text: 'Перед первым использованием администратор должен добавить вас в справочник создателей на портале и указать ваш Telegram ID. Без этого бот не будет принимать ваши сообщения.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Первый запуск: команда /start', level: 2 },
      {
        type: 'paragraph',
        text: 'Найдите бота в Telegram и отправьте команду `/start`. Если вы уже добавлены в систему, бот поприветствует вас по имени и покажет краткую справку.',
      },
      {
        type: 'note',
        text: 'Если бот отвечает «Вас нет в базе» — отправьте /myid и сообщите этот номер администратору для регистрации.',
        variant: 'info',
      },

      { type: 'heading', text: 'Как отправить публикацию', level: 2 },
      {
        type: 'paragraph',
        text: 'Просто отправьте ссылку на видео в чат с ботом. Артикул WB (если нужен) — через пробел или с новой строки:',
      },
      {
        type: 'ordered-list',
        items: [
          'Скопируйте ссылку на публикацию (Instagram Reels, TikTok, YouTube, ВКонтакте)',
          'Откройте чат с ботом',
          'Вставьте ссылку и при необходимости добавьте через пробел артикул товара WB',
          'Отправьте сообщение — бот подтвердит приём',
        ],
      },
      {
        type: 'note',
        text: 'Одно сообщение = одна публикация. Если нужно сдать несколько — отправляйте по одной.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Форматы сообщений', level: 2 },
      {
        type: 'table',
        headers: ['Формат', 'Пример'],
        rows: [
          ['Только ссылка', 'https://instagram.com/reel/ABC123'],
          ['Ссылка + артикул через пробел', 'https://instagram.com/reel/ABC123 987654321'],
          ['Ссылка + артикул с новой строки', 'https://tiktok.com/@user/video/123\n987654321'],
        ],
      },

      { type: 'heading', text: 'Поддерживаемые платформы', level: 2 },
      {
        type: 'table',
        headers: ['Платформа', 'Допустимые форматы ссылок'],
        rows: [
          ['📸 Instagram', 'instagram.com/reel/…  |  instagram.com/p/…  |  instagram.com/tv/…'],
          ['🎵 TikTok', 'tiktok.com/@user/video/…  |  vm.tiktok.com/…  |  vt.tiktok.com/…'],
          ['▶️ YouTube', 'youtube.com/watch?v=…  |  youtu.be/…  |  youtube.com/shorts/…'],
          ['💙 ВКонтакте', 'vk.com/video…  |  vk.ru/…'],
        ],
      },
      {
        type: 'note',
        text: 'Ссылки других платформ бот не принимает и попросит прислать поддерживаемую ссылку.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Артикул WB', level: 2 },
      {
        type: 'paragraph',
        text: 'Артикул Wildberries — числовой идентификатор товара (6 и более цифр). Добавлять его не обязательно, но он связывает публикацию с конкретным товаром для отслеживания конверсии.',
      },
      {
        type: 'list',
        items: [
          'Артикул можно найти в URL страницы товара на WB или в описании карточки',
          'Допустимые форматы: `987654321`, `1234567`, `90000000`',
          'Если артикул не нужен — просто отправьте только ссылку',
        ],
      },

      { type: 'heading', text: 'Подтверждение от бота', level: 2 },
      {
        type: 'paragraph',
        text: 'При успешном приёме публикации бот отправляет подтверждение с деталями: ваше имя, платформа, артикул (если указан) и ссылка на видео. Метрики загружаются автоматически в течение нескольких минут.',
      },

      { type: 'heading', text: 'Команда /stats — статистика за месяц', level: 2 },
      {
        type: 'paragraph',
        text: 'Команда `/stats` показывает вашу статистику по платформам за выбранный месяц: количество видео, просмотры, лайки, комментарии и показатель вовлечённости ER%.',
      },
      {
        type: 'table',
        headers: ['Команда', 'Что показывает'],
        rows: [
          ['/stats', 'Текущий месяц'],
          ['/stats 04.2026', 'Апрель 2026 (формат ММ.ГГГГ)'],
          ['/stats 2026-04', 'Апрель 2026 (формат ГГГГ-ММ)'],
          ['/stats апрель 2026', 'Апрель 2026 (русское название месяца)'],
          ['/stats апрель', 'Апрель текущего года'],
          ['/stats 4 2026', 'Апрель 2026 (число + год)'],
        ],
      },
      {
        type: 'paragraph',
        text: '**ER%** (Engagement Rate) считается как: (лайки + комментарии) / просмотры × 100%. Если метрики ещё не загружены, бот укажет это рядом с платформой.',
      },

      { type: 'heading', text: 'Команда /myid', level: 2 },
      {
        type: 'paragraph',
        text: 'Команда `/myid` возвращает ваш числовой Telegram ID. Сообщите его администратору при первичной регистрации — без этого бот не будет вас распознавать.',
      },

      { type: 'heading', text: 'Частые вопросы', level: 2 },
      {
        type: 'table',
        headers: ['Ситуация', 'Что делать'],
        rows: [
          ['Бот отвечает «Вас нет в базе»', 'Отправьте /myid и передайте этот ID администратору'],
          ['Бот не реагирует на ссылку', 'Проверьте формат — ссылка должна быть с поддерживаемой платформы'],
          ['Метрики не появились через час', 'Обычно загружаются за 10–30 минут. Если прошло больше суток — сообщите администратору'],
          ['Нужно исправить отправленную публикацию', 'Исправить через бота нельзя. Попросите администратора изменить запись в портале'],
          ['Можно ли отправить без артикула?', 'Да, артикул необязателен — просто пришлите только ссылку'],
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // WB ANALYTICS
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'wb-analytics-overview',
    category: 'wb-analytics',
    title: 'Продажи: полное руководство',
    description: 'Статистика, товары, финансы, склад — полный разбор всех разделов отдела продаж',
    icon: '📊',
    updatedAt: '2026-10-15',
    blocks: [
      { type: 'note', variant: 'warning', text: 'Временно скрыты в отделе «Продажи»: разделы «Оцифровка», «Сверка и чек-листы» и «Финансы» целиком, а в «Складе & Логистике» — подвкладка «Воронка» (там остались «Приход товара», «Ассортиментная матрица» и «Остатки по складам»). Разделы «Товары», «Все товары», «Платное хранение», «Остатки», «Возвраты» и «Поставки» сняты совсем. Всё это не доработано и в интерфейсе не показывается. Статья описывает их устройство и остаётся здесь, чтобы не потерять методику — вернём, когда доведём.' },
      {
        type: 'paragraph',
        text: 'Раздел «Продажи» — внутренний инструмент для анализа продаж на Wildberries. Модуль автоматически загружает данные из API Wildberries и позволяет отслеживать ключевые показатели: выручку, заказы, возвраты, рекламу, остатки и рентабельность в разбивке по неделям, дням и артикулам. Модуль поддерживает несколько кабинетов одновременно (ИП, ООО и т.д.).',
      },
      {
        type: 'image',
        src: '/screenshots/wb-dashboard.png',
        alt: 'Главный дашборд WB Аналитики',
        caption: 'Главная страница модуля — сводная картина по всем подключённым кабинетам',
      },
      { type: 'heading', text: 'Где что лежит сегодня', level: 2 },
      { type: 'paragraph', text: 'Отдел «Продажи» открывается из бокового меню. Наверху — вкладки: **Дашборд** (месяц целиком: план, факт, прогноз, разрыв до плана), **Отчёт** (день за днём по кабинетам), **Планирование YUC** (план по месяцам и по SKU), **Склад & Логистика**, **Отзывы** и **Боты**. У вкладок с галочкой раскрывается список экранов.' },
      { type: 'list', items: [
        '**Склад & Логистика** — «Приход товара», «Ассортиментная матрица», «Остатки по складам».',
        '**Настройки → Продажи · WB** (меню профиля, для администраторов) — там живут все аналитические и служебные экраны: налоги и параметры кабинетов, план ГПР на месяц, себестоимость и юнит-экономика, прибыль по артикулам, раздачи и внешний трафик, «Аналитик» (разбор месяца), ОПиУ, реклама, «Рука на пульсе», сводный чек-лист, воронка продаж, история справочников, оцифровка.',
      ] },
      { type: 'note', variant: 'info', text: 'Разделы «Аналитика» (Оцифровка, РНП, ОПиУ, Реклама), «Финансы» и «Товары» из отдела СКРЫТЫ — они дорабатываются и видны только через «Настройки → Продажи · WB». Поэтому если инструкция упоминает экран, которого нет на вкладках, ищите его там.' },
      { type: 'image', src: '/help-media/sales-nav.jpg', alt: 'Вкладки отдела «Продажи»: Дашборд, Отчёт, Планирование YUC, Склад & Логистика, Отзывы, Боты', caption: 'Так выглядит отдел «Продажи» сегодня: слева актуальные вкладки, справа — Отзывы и Боты' },
      { type: 'note', variant: 'info', text: 'Прежняя структура с разделами «Статистика → По неделям / Сравнение» и «Товары → ABC-анализ, Категории, Матрица, Цены» разобрана: вкладку «По неделям» сняли, а её загрузку файлом перенесли. Если вы искали эти экраны по старой инструкции — ищите нужные цифры на «Отчёте», «Дашборде» и во вкладках «Аналитики».' },
      {
        type: 'paragraph',
        text: 'Навигация по модулю состоит из четырёх разделов. Каждый содержит несколько вкладок с детализированными отчётами. Сводные цифры отдела — на дашборде «Продаж», внутри модуля своего дашборда нет.',
      },
      {
        type: 'table',
        headers: ['Раздел', 'Вкладки внутри', 'Назначение'],
        rows: [
          ['Статистика', 'Сводный чек-лист', 'Сводный чек-лист: сводная таблица по всем товарам, а клик по строке открывает разбор товара по дням. Динамика продаж — в «Отчёте» и на дашборде отдела. Раздел «РНП» с планом заказов и ДРР по товару снят 13.08.2026: план за всё время не завели ни разу, а те же заказы, воронку и рекламу показывает «Рука на пульсе» — там ещё и P&L по дням'],
          ['Финансы', 'ОПиУ, Юнит-экономика', 'Финансовые показатели и маржинальность. Себестоимость вводится там же, где считается юнит-экономика; отчёт о прибылях — «ОПиУ»: статьи по месяцам с расшифровкой до дня и артикула. 13.08.2026 в нём исправлены комиссия, себестоимость и налог, добавлен эквайринг и ввод постоянных расходов (ФОТ, аренда, связь, контент — карандаш в строке «Операционные расходы»), после чего второй отчёт о прибылях — снят: он показывал те же статьи по той же методике. 16.08.2026 поправлена строка «Реклама»: раньше она брала списания с рекламного счёта и складывала вместе с бонусами-кэшбэком (деньгами они не являются), да ещё и только за последние 90 дней — из-за чего у месяцев постарше реклама показывалась нулём. Теперь берётся удержание «WB Продвижение» из финансового отчёта — те же деньги, что WB реально удержал, на всю историю. 21.08.2026 по ответам отдела продаж исправлены ещё две статьи. «Плат. приёмка» перестала быть нулевой: WB с 28.07.2026 берёт 10 ₽ за обработку каждой штуки по схеме «со склада продавца», присылает это отдельной строкой, и портал раньше её не читал вовсе — мимо расчёта прошло 259 417 ₽ у Титковой и 504 723 ₽ у Бабаева за всё время. Появилась строка «Компенсация по штрафам»: когда WB отменяет ранее выставленный штраф, эта сумма теперь приходит доходом в месяц отмены, а не прибавляется к штрафам, как было раньше. Месяц, в котором штраф начислили, не переписывается'],
          ['Склад & Логистика', 'Воронка', 'Диагностика: сырьё воронки продаж. Остатки и оборачиваемость — на вкладке «Остатки по складам» в отделе; расходы на хранение — колонкой «Хранение» в «Прибыли по артикулам»'],
        ],
      },
      {
        type: 'image',
        src: '/screenshots/wb-menu-tabs.png',
        alt: 'Навигационные вкладки WB Аналитики',
        caption: 'Основные разделы и вкладки в верхней части страницы',
      },
      { type: 'heading', text: 'Выбор кабинета и периода', level: 2 },
      { type: 'heading', text: 'Кабинет', level: 3 },
      {
        type: 'paragraph',
        text: 'В верхнем левом углу расположен выпадающий список кабинетов. Переключение кабинета меняет данные во всех вкладках. Рядом с названием кабинета отображается иконка статуса синхронизации:',
      },
      {
        type: 'table',
        headers: ['Статус', 'Что означает'],
        rows: [
          ['✅ Готово', 'Данные актуальны, последняя синхронизация прошла успешно'],
          ['🔄 Синхронизация', 'Идёт загрузка данных из API Wildberries — подождите окончания'],
          ['❌ Ошибка', 'Проблема с токеном или API WB. Проверьте настройки кабинета'],
        ],
      },
      { type: 'heading', text: 'Период', level: 3 },
      {
        type: 'paragraph',
        text: 'Справа вверху расположен выбор временного диапазона. Доступны быстрые периоды для переключения одним кликом:',
      },
      {
        type: 'list',
        items: [
          '**4 недели** — последний месяц',
          '**8 недель** — последние 2 месяца',
          '**12 недель** — последние 3 месяца',
          '**26 недель** — последние полгода',
          '**52 недели** — последний год',
          '**Этот год** — с 1 января текущего года',
          '**Прошлый год** — весь предыдущий год',
          '**Произвольный период** — ввод вручную через иконку календаря',
        ],
      },
      {
        type: 'note',
        text: 'Данные WB обновляются с задержкой ~24 часа. Показатели «сегодня» могут быть неполными до конца дня.',
        variant: 'warning',
      },
      { type: 'heading', text: 'Раздел «Финансы»', level: 2 },
      { type: 'heading', text: 'Юнит-экономика', level: 3 },
      {
        type: 'paragraph',
        text: 'Расчёт прибыли и расходов на единицу товара — позволяет понять, сколько зарабатывается с каждой проданной единицы.',
      },
      {
        type: 'table',
        headers: ['Показатель', 'Описание'],
        rows: [
          ['Выручка', 'Розничная цена продажи единицы, уплаченная покупателем'],
          ['Комиссия WB', 'Процент, удерживаемый маркетплейсом (зависит от категории)'],
          ['Логистика', 'Стоимость доставки единицы до покупателя и обратно (при возврате)'],
          ['Хранение', 'Расходы на хранение единицы на складе WB'],
          ['Реклама на единицу', 'Распределённые рекламные расходы в расчёте на проданную единицу'],
          ['Себестоимость', 'Закупочная цена + упаковка (вводится вручную во вкладке «Себестоимость»)'],
          ['Налог', 'УСН 6% от суммы «К выплате» (настраивается в параметрах кабинета)'],
          ['Эквайринг', 'Фактическая сумма, удержанная Wildberries (из финансового отчёта). Руками не задаётся.'],
          ['Маржа', 'Выручка − все расходы выше'],
          ['ROI', 'Маржа / Себестоимость × 100% — эффективность вложений в закупку'],
        ],
      },
      {
        type: 'note',
        text: 'Без заполненной себестоимости (вкладка «Себестоимость») расчёт маржи и ROI невозможен — колонки будут пустыми.',
        variant: 'warning',
      },
      { type: 'heading', text: 'P&L по SKU («Прибыль по артикулам»)', level: 3 },
      {
        type: 'paragraph',
        text: 'P&L детализированный по каждому артикулу: наглядно видно, какой товар приносит прибыль, а какой работает в убыток. Период берётся из общего календаря «Период» в шапке — свой ряд кнопок («Текущий месяц», Q1–Q4, «Год») с 13.08.2026 убран: на экране получались два независимых периода и цифры таблицы не сходились с показателями сверху. Пока период не выбран, показывается текущий месяц. Таблица — обычное полотно портала, как «Себестоимость» и «Приход товара»: артикул закреплён слева, колонки собраны в группы (Товар, Выручка, Расходы, Итоги, Прибыль) — группу можно свернуть или перетащить, в каждом заголовке есть сортировка и фильтр (по артикулу — поиск, по тегу и предмету — список значений, по числам — диапазон «от и до»). Итог по видимым артикулам — в строке «Итого» внизу, она не уезжает при прокрутке; кнопка в углу разворачивает таблицу на весь экран. Переключатель «Активные / Архив / Все» — в шапке рядом с периодом.',
      },
      { type: 'heading', text: 'Себестоимость', level: 3 },
      {
        type: 'paragraph',
        text: 'Раздел для ввода и управления закупочными ценами по артикулам. Введите закупочную цену для каждого товара — эти данные используются во всех расчётах маржинальности. Поле «СПП %» оставляйте пустым: скидку постоянного покупателя портал считает сам из финансового отчёта — Wildberries не публикует её в кабинете продавца. Значение, вписанное руками, перебивает этот расчёт. У товаров, которые ещё не продавались, финансового отчёта нет, поэтому скидка берётся расчётной: сначала по прошлым продажам самого товара, потом по типичной скидке для такого же предмета, и лишь затем по кабинету. Такие значения помечены в таблице знаком «≈» — наведите на них, чтобы увидеть, откуда взята цифра.',
      },
      { type: 'heading', text: 'Реклама', level: 3 },
      {
        type: 'paragraph',
        text: 'Сводная статистика рекламных расходов по неделям: сумма расходов, ДРР%, выручка, приходящаяся на рекламные кампании.',
      },
      { type: 'heading', text: 'Расходы', level: 3 },
      {
        type: 'paragraph',
        text: 'Ввод прочих расходов (упаковка, сертификаты, зарплаты, аренда и т.д.) для включения в расчёт P&L. Расходы вносятся вручную по периодам.',
      },
      { type: 'heading', text: 'Раздел «Склад & Логистика»', level: 2 },
      { type: 'heading', text: 'Приход товара', level: 3 },
      {
        type: 'paragraph',
        text: 'Плоский список того, что реально едет со склада-фабрики (из R&D) и когда придёт. Таблица «В пути»: артикул, количество, плановая дата прихода, номер рейса и заказ. Ниже — «Остаток на складе ФФ» из последней выгрузки МойСклад: годные, заблокированные и брак. Артикулы — на языке Продаж (модель/цвет + размер), с фото карточки.',
      },
      {
        type: 'note',
        text: 'Древние недопоставки и брошенные заказы сюда НЕ попадают — показываем только живое: то, что физически в пути, и свежие приёмки. Плановая дата берётся из рейса, фактическая — из приёмки МойСклад.',
        variant: 'info',
      },
      { type: 'heading', text: 'Остатки по складам', level: 3 },
      {
        type: 'paragraph',
        text: 'Сколько товара лежит на каждом складе Wildberries. Строка — артикул, клик по ней разворачивает размеры; дальше «На ФФ» (склад фулфилмента из выгрузки МойСклад — считается отдельно и в «Итого WB» НЕ входит), «Итого WB» по учитываемым складам, данные карточки (бренд, предмет, артикул WB, баркод, объём), «в пути» к покупателям и возвраты и колонка на каждый склад; последняя строка — итог. Баркод и объём показываются у размера — у артикула они разные. Снимок остатков сохраняется КАЖДЫЙ ДЕНЬ, поэтому в календаре можно выбрать любой прошлый день и посмотреть, как было тогда — дни, за которые снимка нет, в календаре недоступны. Кнопка «Обновить с WB» запрашивает свежие остатки прямо сейчас: на самой кнопке видно, что происходит («ожидание отчёта», «лимит API, жду», «сохраняем»), а когда WB отдаст данные, таблица обновится сама — ждать и жать повторно не нужно.',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Выключите склады, которых больше нет',
            description: 'Кнопка «Склады» открывает список: название, остаток и тумблер. Выключенный склад пропадает из таблицы и из итогов — у ВСЕХ сотрудников, список общий. Так сгоревшие склады WB, которые продолжают отдавать остатки по API, перестают завышать запас.',
          },
          {
            title: 'Переключать может ответственный',
            description: 'Тумблеры доступны тем, кому выдано право на редактирование именно этой вкладки. У остальных список открывается на просмотр — видно, какие склады не учитываются и сколько на них лежит.',
          },
          {
            title: 'Выгрузите в Excel',
            description: 'Кнопка «Excel» скачивает файл с двумя листами: «Остатки» — бренд, предмет, артикул, артикул WB, баркод, размер, объём, «в пути», «Всего» и колонки складов (ровно то, что на экране), «Склады» — полный список складов с остатками и отметкой, учитывается склад или нет.',
          },
        ],
      },
      {
        type: 'note',
        text: 'Цифры берутся из отчёта Wildberries «Остатки на складах» и обновляются раз в сутки автоматически. Выключение склада влияет только на таблицу и расчёты — данные по нему продолжают собираться каждый день, их видно в списке складов и в выгрузке Excel. За дни, когда снимок не снимался, данных не будет: история начинается с того дня, как вкладка появилась.',
        variant: 'info',
      },
      {
        type: 'paragraph',
        text: 'Остаток склада фулфилмента загружается здесь кнопкой «Загрузить остаток ФФ»: xlsx-выгрузка «Товары на складе» из МойСклад как есть — вручную ничего готовить не надо. Размер портал берёт из наименования и сверяет со списком размеров этого артикула, лист «Брак» подхватывается автоматически. Ниже появляется таблица «На складе ФФ»: артикул → размеры, с колонками «Всего», «Годные», «Заблокировано» и «Брак» — сразу видно, сколько из остатка реально можно продать.',
      },
      {
        type: 'note',
        text: 'Загрузка ЗАМЕЩАЕТ предыдущий остаток целиком: чего нет в новом файле, того нет и на складе. Если файл не распознан (чужой формат, переименованные колонки), портал откажет и оставит прошлый остаток на месте. После загрузки показывается отчёт: сколько строк легло и что не легло — например, если в выгрузке у артикула размеры S/M/L, а в МойСклад заведены 42/44/46, такие строки перечисляются списком, чтобы остаток не потерялся молча.',
        variant: 'warning',
      },
      { type: 'heading', text: 'Ассортиментная матрица', level: 3 },
      {
        type: 'paragraph',
        text: 'Артикулы, отобранные в этот сезон продаж (во вкладке «Ассортимент» отдела R&D): план на сезон (штук), остаток на ФФ, и «паспорт товара» — категория, ТН ВЭД, состав, № декларации, упаковка. Артикулы сгруппированы (клик по артикулу — разбивка по размерам), с фото карточки. Сверху селектор сезона.',
      },
      {
        type: 'note',
        text: 'Паспорт товара здесь только для просмотра — его заполняет сезонист во вкладке «Ассортимент» отдела R&D. Что отобрано в сезон и с какими полями — тем и приходит в продажи.',
        variant: 'info',
      },
      {
        type: 'paragraph',
        text: 'Колонка «На ФФ, шт» показывает остаток на складе фулфилмента: ВСЁ, что физически лежит — годные плюс заблокированные плюс брак; брак и заблокированные подписаны под цифрой цветом. Сам файл грузится на соседней вкладке «Приход товара» (кнопка «Загрузить остаток ФФ»), там же есть таблица «На складе ФФ» со всеми позициями. Рядом с итогами видно, от какого числа выгрузка.',
      },
      {
        type: 'note',
        text: 'В этой таблице остаток виден только у артикулов, отобранных в сезон продаж (R&D → «Ассортимент» → «+ сезон»). Весь загруженный остаток целиком — в таблице «На складе ФФ» на вкладке «Приход товара».',
        variant: 'warning',
      },
      { type: 'heading', text: 'Остатки', level: 3 },
      {
        type: 'paragraph',
        text: 'Текущие и исторические остатки по складам WB. Показывает оборачиваемость в днях и риск out-of-stock — когда товар закончится при текущем темпе продаж.',
      },
      { type: 'heading', text: 'Возвраты', level: 3 },
      {
        type: 'paragraph',
        text: 'Детализация возвратов по артикулам: количество, периоды, причины (если переданы WB). Высокий % возврата сигнализирует о проблемах с качеством, описанием товара или упаковкой.',
      },
      { type: 'heading', text: 'Поставки', level: 3 },
      {
        type: 'paragraph',
        text: 'Планирование и история поставок. Рассчитывает, сколько единиц нужно довезти исходя из текущих продаж и остатков, чтобы не допустить дефицита.',
      },
      { type: 'heading', text: 'Воронка', level: 3 },
      {
        type: 'paragraph',
        text: 'Воронка конверсии от показа до покупки: показы → клики → добавления в корзину → заказы → выкупы. Данные из API рекламы WB. Помогает найти, на каком этапе теряются покупатели.',
      },
      { type: 'heading', text: 'Хранение', level: 3 },
      {
        type: 'paragraph',
        text: 'Расходы на платное хранение по складам WB. Показывает динамику затрат на хранение и помогает оценить их влияние на рентабельность.',
      },
      { type: 'heading', text: 'Синхронизация данных', level: 2 },
      { type: 'heading', text: 'Автоматическая синхронизация', level: 3 },
      {
        type: 'paragraph',
        text: 'Запускается по расписанию, настроенному в параметрах кабинета. Загружает данные за последние 30–90 дней. Рекомендуется включить автоматический запуск — данные будут всегда актуальны.',
      },
      { type: 'heading', text: 'Ручная синхронизация', level: 3 },
      {
        type: 'paragraph',
        text: 'Кнопка «Синхронизировать» в верхней панели кабинета. Запускает полную загрузку за настроенный период. Используйте её, если нужно обновить данные немедленно.',
      },
      { type: 'heading', text: 'Ограничения API Wildberries', level: 3 },
      {
        type: 'paragraph',
        text: 'Wildberries устанавливает ограничения на глубину и частоту запросов к своему API. Это влияет на то, какие исторические данные доступны:',
      },
      {
        type: 'table',
        headers: ['Данные', 'Глубина истории', 'Примечание'],
        rows: [
          ['Отчёт о продажах (выплаты)', 'Без ограничений', '1 запрос в минуту; исторический синк ~70 недель ≈ ~70 мин'],
          ['Заказы', '~6 месяцев', 'Заказы старше 6 мес. недоступны в API WB — это ограничение самого WB'],
          ['Реклама (статистика)', '~90 дней', 'API рекламы WB отдаёт данные только за последние 90 дней'],
          ['Остатки', 'Текущий снимок', 'Ежедневный снимок сохраняется в базу при каждой синхронизации'],
        ],
      },
      { type: 'heading', text: 'Почему данные могут расходиться', level: 3 },
      {
        type: 'list',
        items: [
          '**«По расчёту» vs «По заказу»** — разные даты одного события: дата заказа и дата выплаты могут отличаться на 2–4 недели',
          '**Заказы в старых периодах = 0** — WB не хранит заказы старше 6 месяцев в своём API',
          '**Реклама пустая в старых периодах** — API рекламы даёт только последние 90 дней',
          '**Данные за текущую неделю неполные** — синк обновляется периодически, не в реальном времени',
        ],
      },
      { type: 'heading', text: 'Настройки кабинета', level: 2 },
      {
        type: 'paragraph',
        text: 'Кнопка шестерёнки рядом с названием кабинета открывает настройки. Здесь задаются параметры подключения и расчётов:',
      },
      {
        type: 'table',
        headers: ['Параметр', 'Описание'],
        rows: [
          ['Название', 'Отображаемое название кабинета в интерфейсе'],
          ['API-токен WB', 'Токен из личного кабинета WB → Настройки → Доступ к API → Статистика'],
          ['Активен', 'Включить / выключить автоматическую синхронизацию кабинета'],
          ['Ставка налога, %', 'УСН ставка (обычно 6%). Используется в расчёте чистой маржи'],
          ['Эквайринг WB, %', 'Комиссия платёжной системы (по умолчанию ~1.5%)'],
          ['Расписание синка', 'Время запуска авто-синхронизации (UTC)'],
          ['Глубина, дней', 'За сколько дней назад загружать данные при авто-синхронизации'],
        ],
      },
      {
        type: 'note',
        text: 'Токен API для статистики отличается от обычного API-токена WB. При создании токена в кабинете WB отметьте две категории: «Статистика» (заказы, продажи, остатки) и «Финансы» (финансовый отчёт о реализации). Без «Финансов» финотчёт не загрузится и синхронизация покажет ошибку доступа 403.',
        variant: 'warning',
      },
      { type: 'heading', text: 'Часто задаваемые вопросы', level: 2 },
      { type: 'heading', text: 'Почему заказы = 0 за периоды старше 6 месяцев?', level: 3 },
      {
        type: 'paragraph',
        text: 'Wildberries хранит историю заказов только ~6 месяцев. Данные за более ранние периоды в API недоступны — это ограничение WB, а не портала.',
      },
      { type: 'heading', text: 'Почему «Продажи» и «Заказы» — разные числа?', level: 3 },
      {
        type: 'paragraph',
        text: 'Заказы — сколько покупатель оформил заказов. Продажи — сколько единиц реально выкуплено (доставлено и не возвращено). Разница = возвраты + заказы, которые ещё в пути.',
      },
      { type: 'heading', text: 'Почему выручка в «По расчёту» не равна «По заказу»?', level: 3 },
      {
        type: 'paragraph',
        text: '«По расчёту» — деньги, которые WB перечислил в конкретную расчётную неделю. «По заказу» — деньги, относящиеся к заказам этой недели, но которые WB мог выплатить через 2–4 недели.',
      },
      { type: 'heading', text: 'Что означает % выплаты?', level: 3 },
      {
        type: 'paragraph',
        text: 'Доля суммы «К выплате» от розничной выручки. Показывает, какой процент от цены товара возвращается продавцу после вычета комиссии WB, логистики, штрафов и хранения. Нормальное значение: 30–60% в зависимости от категории.',
      },
      { type: 'heading', text: 'Что такое ДРР?', level: 3 },
      {
        type: 'paragraph',
        text: 'ДРР — Доля Рекламных Расходов. Формула: Расходы на рекламу / Выручка × 100%. Показывает, сколько процентов выручки уходит на рекламу. Ориентир: держать ДРР ниже 10–15%.',
      },
      { type: 'heading', text: 'Синхронизация завершилась, но данные не обновились', level: 3 },
      {
        type: 'paragraph',
        text: 'Нажмите F5 (обновить страницу). Браузер мог показать сохранённую копию страницы.',
      },
      { type: 'heading', text: 'Глоссарий', level: 2 },
      {
        type: 'table',
        headers: ['Термин', 'Расшифровка'],
        rows: [
          ['К выплате', 'Сумма, которую WB перечислит продавцу по конкретной единице товара'],
          ['Выручка', 'Розничная цена, уплаченная покупателем (до всех удержаний WB)'],
          ['Расчётная неделя', 'Период, за который WB производит выплату (обычно раз в 2 недели)'],
          ['ДРР', 'Доля Рекламных Расходов = реклама / выручка × 100%'],
          ['Выкуп / % выкупа', 'Процент заказов, которые получены покупателем и не возвращены'],
          ['Себестоимость', 'Закупочная цена + упаковка + доставка до склада WB'],
          ['Маржа', 'Выручка − Себестоимость − Комиссия − Логистика − Реклама − Налог'],
          ['ROI', 'Return on Investment = Маржа / Себестоимость × 100%'],
          ['ABC-анализ', 'Разделение товаров на 3 группы по вкладу в выручку (A=80%, B=15%, C=5%)'],
          ['Исторический синк', 'Загрузка данных за весь период с нуля (медленно, ~1 мин/неделя)'],
          ['Out-of-stock', 'Ситуация, когда товар закончился на складе WB и не отображается в поиске'],
          ['CTR', 'Click-Through Rate = клики / показы × 100% — кликабельность рекламы'],
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // R&D
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'rd-overview',
    category: 'rd',
    title: 'Отдел R&D: обзор вкладок',
    description: 'Как устроена страница отдела: Ассортиментная матрица, Закуп/Доставка, Запуск продукта и ЧЗ',
    icon: '🔬',
    updatedAt: '2026-08-04',
    blocks: [
      {
        type: 'paragraph',
        text: 'Отдел R&D отвечает за путь товара от идеи до склада: отбор новинок, согласование, заказ на фабрике, доставку и расчёт себестоимости. Все инструменты собраны на странице отдела **Отделы → R&D** в виде верхних вкладок.',
      },
      {
        type: 'image',
        src: '/help-media/rd-dashboard.png',
        alt: 'Дашборд отдела R&D',
        caption: 'Дашборд отдела: этапы запусков, категории, прогресс приёмки заказов, «В производстве / в пути»',
      },
      {
        type: 'paragraph',
        text: 'Дашборд открывается первым и отвечает на вопрос «что сейчас происходит»: воронка этапов запуска (красным — где опаздываем), запуски по категориям, сколько получено от заказанного за год, объёмы в производстве и в пути, свежие заказы и брак по поставщикам. Клик по виджету ведёт в соответствующий инструмент.',
      },
      {
        type: 'table',
        headers: ['Вкладка', 'Назначение'],
        rows: [
          ['Дашборд', 'Сводка отдела: ключевые показатели и быстрые переходы к инструментам'],
          ['Запуск продукта', 'Пайплайн запуска новых продуктов: этапы-чекпоинты по каждой позиции. Заголовки этапов по умолчанию фиолетовые; администратор может перекрасить любой этап кружком-свотчем в его шапке (палитра, свой цвет, сброс) — как в таблицах-«тепловых картах» портала.'],
          ['Ассортиментная матрица', 'Отбор новинок: реестр кандидатов, воронка согласования, просчёт заказа'],
          ['Закуп/Доставка', 'Заказы на фабриках, логистика рейсов, приёмки и себестоимость'],
          ['ЧЗ', 'Печать этикеток «Честный знак»: товары и шаблоны'],
        ],
      },
      {
        type: 'note',
        text: 'Типовой путь товара: **Ассортиментная матрица** (нашли и согласовали новинку) → кнопка «Сформировать заказ» в Воронке → **Закуп/Доставка** (заказ, производство, рейс, приёмка) → **Себестоимость** (итоговая цена единицы).',
        variant: 'tip',
      },
      {
        type: 'paragraph',
        text: 'Подробные руководства: «Ассортиментная матрица: отбор новинок» и «Закуп/Доставка: заказы, логистика, себестоимость» — в этой же категории.',
      },
    ],
  },

  {
    slug: 'assortment-guide',
    category: 'rd',
    title: 'Ассортиментная матрица: отбор новинок',
    description: 'База ассортимента и сезоны продаж, автозаполнение паспорта из карточек WB, реестр кандидатов «Поиск новинок», юнит-экономика, воронка согласования и формирование заказа',
    icon: '🧩',
    updatedAt: '2026-07-31',
    blocks: [
      {
        type: 'paragraph',
        text: 'Модуль ведёт отбор новых товаров: от найденного на маркетплейсе кандидата до согласованной позиции, готовой к заказу, а также распределяет весь ассортимент по сезонам продаж.',
      },
      {
        type: 'table',
        headers: ['Вкладка', 'Назначение'],
        rows: [
          ['Ассортимент', 'Вся база товаров с МойСклад; выбор сезона(ов) продаж для каждого артикула'],
          ['Поиск новинок', 'Реестр всех кандидатов с рыночными данными, ценами закупки и решениями'],
          ['Воронка', 'Этапы согласования позиции; отсюда формируется заказ'],
          ['Просчёт заказа', 'Финальные параметры согласованных товаров перед заказом'],
        ],
      },

      { type: 'heading', text: 'Вкладка «Ассортимент»', level: 2 },
      {
        type: 'paragraph',
        text: 'Единая база всего ассортимента с МойСклад (весь каталог, а не один сезон) с фото. Напротив каждого артикула — выбор сезона(ов) продаж: нажмите «+ сезон» и отметьте, в каких сезонах товар продаётся. Отмеченный артикул попадает в отдел продаж (Склад → Ассортиментная матрица) выбранного сезона со всей информацией. Базовые вещи (например, белые рубашки) можно отметить сразу в несколько сезонов — выбор множественный.',
      },
      {
        type: 'note',
        text: 'База большая — работайте через фильтры прямо в шапке таблицы: у каждого столбца есть воронка. «Артикул» — поиск по артикулу и названию; «Сезоны» и «Категория» — списком галочек (можно выбрать сразу несколько); «ТН ВЭД», «Состав», «№ декл.», «Упаковка» — поиском по подстроке. Плюс чипы-сегменты сверху со счётчиками («Требуют сезона», «Без паспорта», «В сезоне») — один клик показывает только то, что осталось сделать. Категорию можно менять прямо в столбце «Категория» — это выпадающий список категорий из МойСклад.',
        variant: 'info',
      },
      {
        type: 'paragraph',
        text: 'Справа в панели фильтров — одна кнопка-глаз со счётчиком «Архив». По умолчанию она включена (архив скрыт из таблицы), нажатие показывает архивные обратно. В «Архив» попадают: товары с припиской «АРХИВ» в наименовании из МойСклад (так их помечают на складе — определяется автоматически); артикулы, удалённые из каталога МойСклад; служебные штрихкод-артикулы МойСклад (00006…) без нормального кода; а также помеченные вручную как «Устаревший»/«Неактуальный» в панели «Разметка».',
      },
      {
        type: 'note',
        text: 'Каталог синхронизируется с МойСклад автоматически (и по кнопке «Синхронизировать»): новые товары из МойСклад заводятся сами, названия обновляются, а то, что в МойСклад переименовали в «…АРХИВ» или удалили, само уезжает в «Архив». Поэтому список «активных» — это то, что реально живёт в МойСклад сейчас; вручную чистить его не нужно.',
        variant: 'info',
      },
      { type: 'heading', text: 'Кнопка «Заполнить из WB»', level: 3 },
      {
        type: 'paragraph',
        text: 'Номер декларации соответствия и размеры упаковки уже заведены в карточках Wildberries — вбивать их руками в сотни строк не нужно. Кнопка «Заполнить из WB» справа над таблицей читает карточки всех кабинетов WB, сопоставляет их с нашими артикулами по артикулу продавца (в WB он заведён как «модель/цвет» — так же, как у нас) и проставляет данные сразу на все размеры артикула.',
      },
      {
        type: 'paragraph',
        text: 'Сначала откроется предпросмотр: сколько карточек прочитано, сколько артикулов сопоставилось и сколько ячеек будет заполнено. Ничего не записывается, пока не нажмёте «Заполнить». Чипами сверху выбираются поля: по умолчанию «№ декларации» и «Упаковка», дополнительно можно взять «ТН ВЭД», «Состав» и «Категорию» — их обычно заполняют вручную, поэтому они выключены.',
      },
      {
        type: 'paragraph',
        text: 'Тот же импорт заполняет столбец «Кабинет» сразу после артикула — в каком кабинете WB нашлась карточка (ИП Титкова / ИП Бабаев). Он только для чтения: это факт, а не наше решение. По нему можно фильтровать — воронка в шапке столбца. Прочерк означает, что карточки на WB нет: товар для другого маркетплейса, служебный код или архив.',
      },
      {
        type: 'table',
        headers: ['Поле портала', 'Откуда в карточке WB'],
        rows: [
          ['№ декл.', 'характеристика «Номер декларации соответствия»'],
          ['Упаковка', 'габариты карточки: длина × ширина × высота и вес брутто («24×16×3 см, 0.18 кг»)'],
          ['ТН ВЭД', 'характеристика «ТНВЭД»'],
          ['Состав', 'характеристика «Состав»'],
          ['Категория', '«предмет» карточки (Блузки, Платья, Рубашки)'],
          ['Кабинет', 'в каком кабинете WB нашлась карточка — заполняется всегда, только для чтения'],
        ],
      },
      {
        type: 'note',
        text: 'По умолчанию заполняются ТОЛЬКО пустые ячейки — то, что вписано руками, импорт не трогает. Чтобы обновить и его, включите тумблер «Перезаписывать заполненное вручную». Строка «Нет карточки на WB» в предпросмотре — это нормально: так выглядят товары не для WB (артикулы для Ozon с приставками U_, Y_OZ_, M_), служебные штрихкод-коды и архив. Если WB ответил ошибкой и каталог прочитан не полностью, кнопка «Заполнить» блокируется: иначе часть артикулов выглядела бы как «нет на WB», хотя карточка просто не доехала.',
        variant: 'info',
      },
      {
        type: 'note',
        text: 'Категорию из WB берите осознанно: у Wildberries есть предметы, которых нет в списке МойСклад («Юбки», «Сарафаны»), и после импорта такая категория окажется вне выпадающего списка.',
        variant: 'warning',
      },
      {
        type: 'heading',
        text: 'Панель «Разметка»',
        level: 3,
      },
      {
        type: 'paragraph',
        text: 'Кнопка «Разметка» справа в панели фильтров открывает выезжающий список всего ассортимента. Вверху он делится на «Активные» и «Архив» (плюс «Все») — так же, как таблица. Найдите артикулы (поиск или фильтр), отметьте нужные переключателями слева — можно кнопкой «Выделить показанные» отметить сразу всё, что осталось после поиска, — и нажмите «В архив» или «Активный». Ставится сразу на все размеры артикула, отмечать можно пачками. Служебные barcode-коды (00006…) и товары с «АРХИВ» в наименовании МойСклад считаются архивными автоматически.',
      },
      {
        type: 'note',
        text: 'Разметка нужна только для чистоты таблицы: скрытые артикулы не пропадают из портала и статус ни на что не влияет — ни на заказы, ни на остатки, ни на матрицу отдела Продаж. Ошиблись — откройте «Разметку», найдите артикул и верните ему статус «Актуальный».',
        variant: 'info',
      },
      {
        type: 'note',
        text: 'В выборе доступны только активные сезоны. Паспорт товара: категория, ТН ВЭД и состав подтягиваются автоматически из МойСклад при синхронизации (ТН ВЭД — из поля товара, состав — из описания, категория — из атрибута «Категория товара»). № декларации и упаковку в МойСклад не ведут — их вносят прямо в ячейках таблицы (пунктирный «+» = можно ввести); правки сохраняются на карточку товара и уходят в продажи готовыми.',
        variant: 'info',
      },

      { type: 'heading', text: 'Вкладка «Поиск новинок»', level: 2 },
      {
        type: 'paragraph',
        text: 'Большая таблица-реестр: каждая строка — товар-кандидат с фото. Колонки собраны в цветные группы (Товар, Удержания площадки, Юнит-экономика, Решение). Таблицу можно настраивать под себя: перетаскивать колонки и группы, закреплять колонки, сворачивать группы — настройки запоминаются.',
      },
      {
        type: 'image',
        src: '/help-media/rd-as-registry.png',
        alt: 'Реестр «Поиск новинок»',
        caption: 'Реестр кандидатов: фото, поставщик, цена закупа, логистика, плановая РРЦ и удержания площадки по каждой позиции',
      },
      {
        type: 'list',
        items: [
          '**Клик по строке** открывает карточку товара со всеми полями и фото',
          '**Три страны производства** (Китай, Киргизия, Узбекистан) сравниваются прямо в строке: закупочная цена в валюте страны, логистика и итоговая цена — тремя строками в ячейке',
          '**Поставщик** выбирается из предложений фабрик со своими ценами',
          '**Фото** подтягиваются автоматически из карточки WB по артикулу',
          '**Статус** — «Согласовано» или «Отказ»; согласованные позиции идут дальше в воронку',
        ],
      },

      { type: 'heading', text: 'Юнит-экономика кандидата', level: 3 },
      {
        type: 'paragraph',
        text: 'Ячейка «Плановая РРЦ» открывает калькулятор прибыли: комиссия, эквайринг, логистика WB, реклама, хранение, налоги — до чистой прибыли, маржи и ROI. Снимок расчёта сохраняется на товаре, и колонки блока «Юнит-экономика» заполняются из него.',
      },
      {
        type: 'paragraph',
        text: 'Тот же калькулятор есть **отдельной страницей — `/calculator`**: посчитать экономику товара, которого ещё нет в реестре. Все числа вводятся руками, товар и поставщик не нужны. Расчёт можно назвать и сохранить — он появится в списке «Мои расчёты» справа и откроется позже со всеми значениями (свои расчёты видит только автор). Страницы нет в меню — открывается по прямой ссылке, вход в портал при этом нужен.',
      },

      { type: 'heading', text: 'Просчёт логистики', level: 3 },
      {
        type: 'paragraph',
        text: 'Логистика единицы считается автоматически: ставка за килограмм из справочника «Логистика» (модуль Закуп/Доставка → Справочники) умножается на вес единицы. Ставки зависят от страны и вида доставки.',
      },

      { type: 'heading', text: 'Размерная сетка', level: 3 },
      {
        type: 'heading', text: 'Сборка слайдов карточки', level: 3,
      },
      {
        type: 'paragraph',
        text: 'Во вкладке **«Рендеры ИИ»** (там же, где генерируются фото) есть блок «Размерная сетка», а под ним кнопка **«Собрать карточку — 10 слайдов»** — она отдаёт архив готовых PNG 1800×2400 (это ровно 3:4 под требования WB): обложка, выгоды, материал, акценты, размерная сетка, параметры модели, комплектация, сочетания, коллекция и правила возврата. Вёрстка повторяет мастер-шаблон ENMOONE — координаты, шрифты и цвета взяты из самого макета, поэтому слайды не надо доводить руками. Фото подставляются автоматически: в каждый слайд идёт готовый рендер нужного вида из вкладки «Рендеры ИИ» — обложка и размерная сетка берут hero-кадр, «Выгоды» лайфстайл, «Материал» макро ткани, «Акценты» детали, «Сочетай» разные образы. Поэтому **сначала во вкладке «Рендеры ИИ» нажмите «Весь комплект»** — иначе слайды соберутся из исходного фото поставщика. Если нужного рендера нет, слайд берёт фото из галереи товара. Тексты и цифры — из карточки, **сохраните её перед сборкой**. Сборка занимает около 15 секунд.',
      },
      {
        type: 'paragraph',
        text: 'В карточке товара есть свёрнутый блок **«Размерная сетка»** — он хранит то, чего больше нигде нет: таблицу мерок (размер × грудь/талия/рукав/длина), сноску о допуске, посадку, параметры модели (рост, размер, обхваты) и состав ткани. Свёрнутый блок показывает короткую сводку («3 размера · рост 169 см · 80% хлопок»), так что карточку он не удлиняет. Набор мерок подставляется по категории — «Верх», «Низ» или «Платье», — но колонки можно переименовать, добавить и убрать. Эти данные пойдут в слайды карточки: таблицу размеров, «Параметры модели» и «Материал».',
      },
      {
        type: 'paragraph',
        text: 'Там же — **выноски на фото**: подписи «Грудь», «Талия», «Рукав», «Длина» с линиями-указателями, как в макете. Редактор показан ровно как слайд: фон в правой части, слева призрак таблицы — ставьте выноски по изделию справа, где их не закроет сетка. Кадром выбирается тот же рендер, что пойдёт фоном (обычно hero), поэтому в списке «Кадр» есть и рендеры ИИ, и фото галереи. У каждой выноски три точки: два конца линии (растяните по краям мерки — например, по ширине груди) и подпись на линии. Две фиолетовые точки на линии гнут её дугой — в слайд они не попадают, это технические ручки. Две, а не одна: одной точкой линию не выгнуть по краям, а двумя получается и дуга, и S-образный изгиб. Кнопка **«Открыть крупно»** разворачивает фото на весь экран: в карточке оно небольшое, и попасть точкой в край рукава тяжело. Кнопка **«Расставить ИИ»** делает черновик: модель смотрит на фото и сама проводит линии — грудь и талия поперёк изделия, рукав вдоль руки, длина по вертикали, — а подписи ставит в правой части кадра, где их не закроет таблица. Занимает около минуты (модель делает набросок, потом сверяется по своим же линиям и правит края). Это именно черновик: проверьте и поправьте точки мышью, ничего не сохраняется само. Не расставили — слайд просто печатается без выносок.',
      },
      { type: 'heading', text: 'Рендеры ИИ', level: 3 },
      {
        type: 'paragraph',
        text: 'Вкладка **«Рендеры ИИ»** в карточке товара превращает сырое фото поставщика в готовый комплект кадров для карточки маркетплейса. Главная кнопка **«Весь комплект — 8 кадров»** ставит сразу всё, что нужно карточке, в порядке слайдов: **Hero-карточка** (студия, модель в полный рост), **Лайфстайл** (город, живой свет), **Вид сзади**, **Макро ткани** (слайд «Материал»), **Деталь: верх** (воротник и горловина) и **Деталь: край** (манжет и низ) — для слайда «Акценты», **Комплектация** (раскладка под углом с бирками) и **Для коллажа** (чистый фон под вырезку в слайд «Сочетай»). Каждый кадр можно переснять отдельно кнопкой ниже, а «Свой промпт» — описать любую подачу словами. Считай примерно минуту на кадр. На слайде «Коллекция» показываются другие товары — их фото подтягиваются **по артикулам WB**, которые вы укажете в блоке «Размерная сетка → Коллекция»: система находит товар с таким артикулом в ассортименте и берёт его фото (нет такого — подставит рендер этого товара). У кадра «Комплектация» вторым референсом идёт **эталон раскладки ENMOONE** — поэтому угол, разворот рукавов и положение бирок повторяют фирменную подачу, а не выдумываются заново.',
      },
      {
        type: 'paragraph',
        text: 'Важно: **на кадрах намеренно нет текста**. Нейросеть пишет кириллицу нечитаемо, поэтому во всех промтах текст запрещён, а бирки на слайде «Комплектация» генерируются чистыми — надписи, размерная сетка, состав и правила возврата добавляются в макете поверх. Референсом служит фото товара (если фото несколько — выбери миниатюру): фасон, цвет и детали переносятся на рендер точно. Готовый рендер открывается кликом на весь экран; кнопка **«В фото»** добавляет его в галерею товара (можно сделать главным), стрелка — скачивает PNG. Корзинка на карточке рендера удаляет его из очереди — работает и для неудачных попыток (карточка с ошибкой), и для готовых; уже добавленные «В фото товара» копии при этом остаются в галерее.',
      },
      { type: 'heading', text: 'Импорт из XLSX', level: 3 },
      {
        type: 'note',
        text: 'Кнопка «Импорт» загружает кандидатов из Excel. Дубликаты определяются по связке артикул + название + бренд; если у строки нет артикула — нужна уникальная категория, иначе строки склеятся.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Вкладка «Воронка»', level: 2 },
      {
        type: 'paragraph',
        text: 'Согласованные кандидаты проходят этапы подготовки к заказу: **Запросить и согласовать образец → Размерный ряд → Цвета → Формирование спецификации → Заключение договора → Оформление деклараций → Оформление и отправка поставщику**. У каждой позиции — ячейки-чекпоинты по этапам; в ячейках живут вложения, комментарии, размеры и цвета. Слева у артикула — карточка с фото и экономикой (маржа, ROI, прибыль на штуку), справа — количество и сумма заказа.',
      },
      {
        type: 'image',
        src: '/help-media/rd-as-funnel.png',
        alt: 'Воронка согласования',
        caption: 'Воронка: строка — артикул с экономикой, столбцы — этапы подготовки заказа с чекпоинтами',
      },
      {
        type: 'note',
        text: 'Кнопка **«Сформировать заказ»** становится активной только когда ВСЕ этапы позиции зелёные и указано количество — так в заказ не уходят несогласованные товары.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Вкладка «Просчёт заказа»', level: 2 },
      {
        type: 'paragraph',
        text: 'Финальная таблица по согласованным позициям: выбранная страна и поставщик, цена закупки, логистика, итоговая цена и комментарии — то, что уезжает в заказ модуля «Закуп/Доставка».',
      },
      {
        type: 'image',
        src: '/help-media/rd-as-final.png',
        alt: 'Просчёт заказа',
        caption: 'Просчёт заказа: финальные параметры согласованных позиций перед размещением',
      },
    ],
  },

  {
    slug: 'rd-collection-guide',
    category: 'rd',
    title: 'Закуп/Доставка: заказы, логистика, себестоимость',
    description: 'Заказы на фабриках, канбан логистики рейсов с упаковочными листами, приёмки МойСклад, дерево себестоимости и справочники',
    icon: '📦',
    updatedAt: '2026-07-29',
    blocks: [
      {
        type: 'paragraph',
        text: 'Модуль ведёт заказ от размещения на фабрике до приёмки на складе и расчёта себестоимости. Данные о заказах и приёмках синхронизируются с **МойСклад**.',
      },
      {
        type: 'table',
        headers: ['Вкладка', 'Назначение'],
        rows: [
          ['Заказы', 'Все заказы поставщикам: состав, суммы в валюте закупки, статусы, отгрузка'],
          ['В производстве / в пути', 'Сводная по товарам: что сейчас шьётся и что уже едет'],
          ['Логистика рейсов', 'Канбан доставки: рейсы двигаются по этапам от фабрики до склада'],
          ['Приёмки', 'Приёмки товара на склад из МойСклад'],
          ['Себестоимость', 'Итоговая себестоимость единицы: дерево Артикул → Цвет → Приёмки'],
          ['Справочники', 'Бренды, ИП, фабрики, страны, ставки логистики, транспортные компании'],
        ],
      },

      { type: 'heading', text: 'Заказы', level: 2 },
      { type: 'note', variant: 'tip', text: 'Вверху справа — компактный переключатель: слева год, справа сезон (Лето/Осень) с числом заказов. Вместе они задают, какой сезон показан (напр. 2026 + Лето). По умолчанию открывается активный сезон, в котором есть заказы, — новый пустой сезон (например, только что созданная «Осень 2026») их больше не перекрывает.' },
      {
        type: 'image',
        src: '/help-media/rd-c-orders.png',
        alt: 'Вкладка «Заказы»',
        caption: 'Заказы поставщикам: состав, суммы в валюте закупки, статусы и отгрузка',
      },
      {
        type: 'list',
        items: [
          'Заказы группируются по **сезонам, сезон = год** (нумерация заказов в МойСклад сбрасывается ежегодно)',
          'Суммы хранятся **в валюте закупки**; пересчёт в рубли — по курсу из МойСклад',
          'Итоги по заказам разворачиваются в разбивку по статусам; на размещённых без отгрузки стоит пометка «ждём отгрузки»',
        ],
      },

      { type: 'heading', text: 'В производстве / в пути', level: 2 },
      {
        type: 'image',
        src: '/help-media/rd-c-pivot.png',
        alt: 'Вкладка «В производстве / в пути»',
        caption: 'Сводная по товарам: что шьётся на фабриках и что уже едет на склад',
      },

      { type: 'heading', text: 'Логистика рейсов', level: 2 },
      {
        type: 'paragraph',
        text: 'Канбан-доска: каждая карточка — рейс с грузом. Колонки-этапы (фабрика, граница, транзит, склад…) настраиваются под ваш процесс. На компьютере карточка перетаскивается мышью, на планшете/телефоне — нажмите и подержите карточку ~0,3 сек (появится «поднятая» копия под пальцем) и, не отпуская, перенесите её в нужную колонку. Обычный свайп при этом просто прокручивает колонку. В карточке рейса — перевозчик из справочника транспортных компаний, вес, ставки и сроки.',
      },
      {
        type: 'image',
        src: '/help-media/rd-c-logistics.png',
        alt: 'Канбан «Логистика рейсов»',
        caption: 'Логистика рейсов: карточки-рейсы двигаются по этапам доставки',
      },
      {
        type: 'note',
        text: 'У рейса есть **упаковочный лист**: состав по коробам. При приёмке короба сканируются — так видно, что доехало и что расходится с листом.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Приёмки', level: 2 },
      {
        type: 'image',
        src: '/help-media/rd-c-receipts.png',
        alt: 'Вкладка «Приёмки»',
        caption: 'Приёмки товара на склад из МойСклад',
      },

      { type: 'heading', text: 'Себестоимость', level: 2 },
      {
        type: 'paragraph',
        text: 'Дерево **Артикул → Цвет → Приёмки**: по каждой приёмке видна закупочная цена, доля логистики и итоговая себестоимость единицы. Так одна и та же модель в разных партиях может иметь разную себестоимость — это нормально и видно явно.',
      },
      {
        type: 'image',
        src: '/help-media/rd-c-cost.png',
        alt: 'Вкладка «Себестоимость»',
        caption: 'Себестоимость: дерево Артикул → Цвет → Приёмки с закупом, логистикой и итогом за единицу',
      },
      {
        type: 'paragraph',
        text: 'Справа над таблицей — две кнопки выгрузки в Excel: **«Excel: сезон»** (только выбранный сезон) и **«Excel: все сезоны»** (все товары за все сезоны и годы одним файлом).',
      },
      {
        type: 'list',
        items: [
          'Лист «Себестоимость» — строка на артикул: сезон, модель, цвет, размер, SKU, баркод WB, принято шт, производство/шт, себестоимость/шт и суммы; внизу строка «Итого».',
          'Лист «По приёмкам» — та же детализация, что раскрывается в дереве: номер и дата приёмки, входящий номер, сколько принято и почём. Видно, что одна модель в разных партиях стоит по-разному.',
          'Лист «Свод по моделям» — строка на модель: сколько артикулов, сколько принято и сколько всего вложено.',
        ],
      },
      {
        type: 'note',
        variant: 'info',
        text: 'Числа в файле те же, что на экране: производство/шт = цена фабрики × курс МойСклад, себестоимость/шт = производство + распределённые логистика, таможня и доп. расходы рейса. Шапка закреплена, фильтры включены — можно сразу крутить сводные.',
      },
      {
        type: 'note',
        variant: 'warning',
        text: 'Баркод WB подтягивается из живых карточек кабинетов (артикул продавца + размер), поэтому у текущего сезона он есть почти везде, а у старых — только пока карточка не снята с WB: у удалённых карточек баркода уже нет нигде, и ячейка остаётся пустой. Штрихкод МойСклад сюда не подставляется — он стоит на карточке модели, а не на размере, и с баркодом WB не совпадает.',
      },

      { type: 'heading', text: 'Справочники', level: 2 },
      {
        type: 'table',
        headers: ['Справочник', 'Что хранит'],
        rows: [
          ['Бренды / ИП', 'Юрлица и бренды с логотипами'],
          ['Фабрики (контрагенты)', 'Карточка фабрики: страна, контакты, сводка по заказам'],
          ['Страны', 'Страны производства'],
          ['Логистика', 'Ставки за килограмм и сроки этапов по стране и виду доставки (земля / поезд / авиа) — используются и в Ассортиментной матрице'],
          ['Транспортные компании', 'Перевозчики: какие страны возят; выбираются в карточке рейса'],
        ],
      },
      {
        type: 'image',
        src: '/help-media/rd-c-refs.png',
        alt: 'Вкладка «Справочники»',
        caption: 'Справочники модуля: бренды, ИП, фабрики, страны, ставки логистики и перевозчики',
      },
      {
        type: 'note',
        text: 'Курс валют берётся из МойСклад (приоритетнее курса ЦБ). Основная валюта учёта в МойСклад — киргизский сом (KGS).',
        variant: 'info',
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // TELEGRAM
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'telegram-platform-overview',
    category: 'telegram',
    title: 'Telegram-платформа: полное руководство',
    description: 'Подключение ботов, заявки, тикеты поддержки, розыгрыши, промокоды, рассылки, пользователи',
    icon: '🤖',
    updatedAt: '2026-04-28',
    blocks: [
      {
        type: 'paragraph',
        text: 'Модуль «Боты» — инструмент для создания и управления Telegram-ботами компании. Через одну платформу можно подключить несколько ботов, строить сценарии диалогов, собирать заявки от пользователей, проводить розыгрыши, выдавать промокоды и делать рассылки.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-main.png',
        alt: 'Главная страница Telegram-платформы',
        caption: 'Интерфейс платформы с вкладками управления ботом',
      },
      {
        type: 'table',
        headers: ['Вкладка', 'Назначение'],
        rows: [
          ['Боты', 'Подключение и настройка Telegram-ботов через токен BotFather'],
          ['Сценарии', 'Создание и публикация диалоговых сценариев (цепочек сообщений)'],
          ['Заявки', 'Просмотр и модерация заявок, поданных пользователями через бота'],
          ['Тикеты', 'Обращения в поддержку от пользователей бота с возможностью ответить'],
          ['Розыгрыши', 'Записи участников розыгрышей и выдача призов победителям'],
          ['Промокоды', 'Пулы промокодов, отслеживание выдачи каждого кода'],
          ['Рассылки', 'Массовые сообщения пользователям с фильтрацией по тегам'],
          ['Пользователи', 'База Telegram-пользователей бота с тегами и контактами'],
        ],
      },

      { type: 'heading', text: 'Вкладка «Боты» — подключение бота', level: 2 },
      {
        type: 'paragraph',
        text: 'Перед началом работы необходимо подключить хотя бы одного Telegram-бота. Бот создаётся в Telegram через сервис @BotFather, а его токен вводится в платформе.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-bots.png',
        alt: 'Вкладка Боты',
        caption: 'Список подключённых ботов с их статусами',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Создайте бота в Telegram',
            description: 'Откройте Telegram, найдите @BotFather, отправьте /newbot. Следуйте инструкциям: укажите имя и username бота. BotFather вышлет токен вида «1234567890:AAF...».',
          },
          {
            title: 'Нажмите «Добавить бота» в платформе',
            description: 'Откроется форма. Введите отображаемое название бота, вставьте токен из BotFather и при необходимости добавьте описание.',
          },
          {
            title: 'Нажмите «Проверить токен»',
            description: 'Платформа обратится к Telegram API, убедится что токен действителен и автоматически заполнит поле username.',
          },
          {
            title: 'Настройте Webhook',
            description: 'Нажмите кнопку «Webhook» рядом с ботом. Webhook — это адрес, по которому Telegram будет отправлять все входящие сообщения. Без него бот не будет получать и обрабатывать сообщения пользователей.',
          },
          {
            title: 'Включите бота',
            description: 'Переключатель «Активен» включает/выключает обработку входящих сообщений. Новый бот создаётся активным по умолчанию.',
          },
        ],
      },
      {
        type: 'table',
        headers: ['Поле', 'Описание', 'Обязательное?'],
        rows: [
          ['Название', 'Отображаемое имя в интерфейсе платформы (до 128 символов)', 'Да'],
          ['Токен', 'Токен бота из @BotFather (до 256 символов)', 'Да'],
          ['Username', 'Заполняется автоматически после проверки токена', 'Авто'],
          ['Описание', 'Произвольные заметки о боте', 'Нет'],
          ['Активен', 'Включить / выключить обработку сообщений', 'Да'],
        ],
      },
      {
        type: 'note',
        text: 'Удаление бота необратимо — вместе с ним удаляются все сценарии, пользователи, заявки и история. Перед удалением убедитесь, что данные больше не нужны.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Вкладка «Сценарии»', level: 2 },
      {
        type: 'paragraph',
        text: 'Сценарий (Flow) — это дерево диалога бота с пользователем. Каждый бот может иметь несколько сценариев, но только один является основным (запускается при /start). Подробно о построении сценариев — в статье «Редактор сценариев».',
      },
      {
        type: 'table',
        headers: ['Поле', 'Описание'],
        rows: [
          ['Название', 'Имя сценария для внутренней навигации'],
          ['Описание', 'Заметки о назначении сценария'],
          ['Опубликован', 'Только опубликованный сценарий активен. Черновик пользователи не видят.'],
          ['По умолчанию', 'Этот сценарий запускается при /start. На каждого бота — один основной.'],
        ],
      },
      {
        type: 'note',
        text: 'Изменения в редакторе не применяются сразу. Нажмите «Опубликовать», чтобы сценарий стал активным для пользователей.',
        variant: 'info',
      },

      { type: 'heading', text: 'Вкладка «Заявки» — модерация', level: 2 },
      {
        type: 'paragraph',
        text: 'Заявки создаются, когда пользователь бота отвечает на вопрос (текст или медиа) в узле сценария с включённой модерацией. Пока заявка не рассмотрена, сценарий для этого пользователя приостановлен.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-applications.png',
        alt: 'Вкладка Заявки',
        caption: 'Список заявок сгруппирован по узлам сценария',
      },
      {
        type: 'paragraph',
        text: 'Заявки сгруппированы по узлам сценария. Нажмите на группу, чтобы увидеть все заявки из этого узла. Каждая заявка содержит ответ пользователя (текст или файл), который можно открыть кликом.',
      },
      {
        type: 'table',
        headers: ['Статус', 'Описание', 'Что происходит с пользователем'],
        rows: [
          ['Новая', 'Заявка поступила, ожидает проверки', 'Сценарий приостановлен, пользователь ждёт'],
          ['Одобрена', 'Ответ принят модератором', 'Сценарий продолжается по ветке «одобрено»'],
          ['Отклонена', 'Ответ не принят', 'Сценарий переходит по ветке «отклонено»'],
          ['Доработка', 'Пользователя просят переотправить', 'Сценарий возвращается к тому же вопросу'],
        ],
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Откройте заявку',
            description: 'Нажмите на строку заявки. Откроется модальное окно с ответом пользователя, метаданными и кнопками действий.',
          },
          {
            title: 'Изучите ответ',
            description: 'Текстовые ответы отображаются как есть. Фото и видео можно просмотреть прямо в окне. Документы — скачать.',
          },
          {
            title: 'При необходимости добавьте комментарий',
            description: 'Введите заметку модератора — она сохранится в записи заявки для истории.',
          },
          {
            title: 'Выберите решение',
            description: 'Нажмите «Одобрить», «Отклонить» или «На доработку». Решение мгновенно разблокирует сценарий пользователя.',
          },
        ],
      },
      {
        type: 'note',
        text: 'Заявки можно экспортировать в Excel кнопкой «Экспорт» — удобно для регулярной отчётности по поступающим заявкам.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Вкладка «Тикеты» — поддержка пользователей', level: 2 },
      {
        type: 'paragraph',
        text: 'Тикеты создаются из узла «Тикет поддержки» в сценарии. В отличие от заявок, тикеты не блокируют сценарий — пользователь сразу получает подтверждение, а оператор отвечает в удобное время.',
      },
      {
        type: 'table',
        headers: ['Статус', 'Описание'],
        rows: [
          ['Новый', 'Тикет поступил, ещё нет ответа оператора'],
          ['Отвечен', 'Оператор ответил, ответ отправлен пользователю в Telegram'],
        ],
      },
      {
        type: 'paragraph',
        text: 'Чтобы ответить на тикет, откройте его и введите текст ответа в поле внизу. После нажатия «Отправить ответ» сообщение немедленно доставляется пользователю в Telegram, а тикет переходит в статус «Отвечен».',
      },
      {
        type: 'list',
        items: [
          'Тикет может содержать несколько вложений (фото, видео, документы) — все отображаются в модальном окне',
          'Внутренний комментарий оператора не отправляется пользователю — только для внутренних заметок',
          'Тикеты можно экспортировать в Excel',
        ],
      },

      { type: 'heading', text: 'Вкладка «Розыгрыши»', level: 2 },
      {
        type: 'paragraph',
        text: 'Участники розыгрыша — пользователи, прошедшие через узел «Код розыгрыша» в сценарии. Каждому участнику автоматически генерируется уникальный код (8 символов). Оператор выбирает победителя и отправляет ему сообщение прямо из интерфейса.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-raffles.png',
        alt: 'Вкладка Розыгрыши',
        caption: 'Список участников розыгрыша с их уникальными кодами и статусами',
      },
      {
        type: 'table',
        headers: ['Статус', 'Описание'],
        rows: [
          ['Выдан', 'Пользователь получил код участника, победитель ещё не определён'],
          ['Награждён', 'Оператор отметил как победителя и отправил приз/сообщение'],
        ],
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Найдите победителя',
            description: 'Используйте поиск по имени пользователя или коду. Результаты обновляются в реальном времени.',
          },
          {
            title: 'Раскройте запись',
            description: 'Нажмите на строку участника — откроется поле для ввода сообщения.',
          },
          {
            title: 'Введите сообщение победителю',
            description: 'Напишите поздравление или инструкцию по получению приза.',
          },
          {
            title: 'Нажмите «Наградить»',
            description: 'Сообщение мгновенно отправится пользователю в Telegram. Статус записи изменится на «Награждён».',
          },
        ],
      },

      { type: 'heading', text: 'Вкладка «Промокоды»', level: 2 },
      {
        type: 'paragraph',
        text: 'Промокоды хранятся в пулах. Пул — это именованный набор кодов для определённого бота. Узел «Промокод» в сценарии автоматически берёт первый доступный код из пула и отправляет его пользователю.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-promos.png',
        alt: 'Вкладка Промокоды',
        caption: 'Список пулов промокодов с количеством доступных и выданных кодов',
      },

      { type: 'heading', text: 'Создание пула промокодов', level: 3 },
      {
        type: 'steps',
        steps: [
          {
            title: 'Нажмите «Создать пул»',
            description: 'Выберите бота, введите название пула (например «Акция лето 2026») и описание.',
          },
          {
            title: 'Добавьте коды',
            description: 'Вставьте промокоды в текстовое поле — по одному на строку. Можно вставить сразу несколько тысяч.',
          },
          {
            title: 'Укажите количество использований',
            description: 'Значение «1» — каждый код выдаётся одному пользователю и становится недоступным. «Без ограничений» — код можно выдавать неограниченное число раз.',
          },
          {
            title: 'Сохраните пул',
            description: 'Пул появится в списке. Теперь его можно указать в узле «Промокод» сценария.',
          },
        ],
      },
      {
        type: 'table',
        headers: ['Показатель пула', 'Что означает'],
        rows: [
          ['Всего', 'Общее количество кодов в пуле'],
          ['Выдано', 'Сколько кодов уже выдано пользователям'],
          ['Доступно', 'Сколько кодов ещё можно выдать (Всего − Выдано)'],
        ],
      },
      {
        type: 'note',
        text: 'Когда доступных кодов не остаётся, пользователи, попавшие на узел «Промокод», не получат код — убедитесь, что в пуле всегда есть запас.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Вкладка «Рассылки»', level: 2 },
      {
        type: 'paragraph',
        text: 'Рассылки позволяют отправить сообщение всем пользователям бота или только тем, у кого есть определённые теги. Теги назначаются пользователям через узел «Установить тег» в сценарии.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-broadcasts.png',
        alt: 'Вкладка Рассылки',
        caption: 'Список рассылок со статусами и счётчиками доставки',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Нажмите «Создать рассылку»',
            description: 'Выберите бота и введите заголовок рассылки (для внутренней навигации).',
          },
          {
            title: 'Напишите текст сообщения',
            description: 'Текст, который получат пользователи в Telegram. При необходимости прикрепите фото или видео.',
          },
          {
            title: 'Настройте фильтр по тегам (опционально)',
            description: 'Если нужно отправить только части аудитории — укажите теги. Например, тег «vip» отправит рассылку только пользователям с этим тегом. Без тегов рассылка уйдёт всем пользователям бота.',
          },
          {
            title: 'Запустите или запланируйте',
            description: 'Кнопка «Запустить» отправляет немедленно. Поле «Запланировать» позволяет указать дату и время — рассылка будет запущена вручную в нужный момент.',
          },
        ],
      },
      {
        type: 'table',
        headers: ['Статус рассылки', 'Описание'],
        rows: [
          ['Черновик', 'Создана, но ещё не отправлена'],
          ['Запланирована', 'Назначено время, ожидает ручного запуска'],
          ['Выполняется', 'Идёт отправка сообщений пользователям'],
          ['Завершена', 'Все сообщения отправлены'],
          ['Ошибка', 'Произошла ошибка при отправке — проверьте настройки бота'],
        ],
      },
      {
        type: 'note',
        text: 'После завершения рассылки отображаются счётчики: сколько сообщений доставлено успешно и сколько не удалось (пользователь заблокировал бота).',
        variant: 'info',
      },

      { type: 'heading', text: 'Вкладка «Пользователи»', level: 2 },
      {
        type: 'paragraph',
        text: 'База всех Telegram-пользователей, которые написали боту. Записи создаются автоматически при первом сообщении пользователя.',
      },
      {
        type: 'table',
        headers: ['Поле', 'Откуда берётся'],
        rows: [
          ['Имя', 'Имя и фамилия из профиля Telegram'],
          ['Username', '@ник пользователя в Telegram'],
          ['Telegram ID', 'Числовой идентификатор (уникален навсегда)'],
          ['Телефон', 'Заполняется, если пользователь поделился контактом через узел «Ожидание контакта»'],
          ['Теги', 'Метки, установленные узлом «Установить тег» в сценарии. Используются для фильтрации рассылок.'],
          ['Дата добавления', 'Когда пользователь впервые написал боту'],
        ],
      },
      {
        type: 'note',
        text: 'Удаление пользователя необратимо — удаляются все его заявки, ответы и история прохождения сценариев.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Часто задаваемые вопросы', level: 2 },
      {
        type: 'table',
        headers: ['Вопрос', 'Ответ'],
        rows: [
          ['Бот не отвечает на сообщения пользователей', 'Проверьте: 1) настроен ли Webhook, 2) активен ли бот, 3) опубликован ли сценарий'],
          ['Пользователь написал /start, но бот молчит', 'Убедитесь что есть опубликованный сценарий с флагом «По умолчанию»'],
          ['Заявка висит в статусе «Новая» уже несколько дней', 'Пользователь ждёт решения модератора — его сценарий заморожен. Обработайте заявку.'],
          ['Промокоды закончились', 'Перейдите в пул и добавьте новые коды через «Добавить коды»'],
          ['Рассылка не дошла до части пользователей', 'Пользователи могли заблокировать бота. Это нормально — они попадают в счётчик «Ошибки».'],
          ['Как посмотреть, какой код получил конкретный пользователь?', 'Откройте пул промокодов и найдите строку с нужным пользователем — там указан код и дата выдачи.'],
        ],
      },
    ],
  },

  {
    slug: 'telegram-flow-builder',
    category: 'telegram',
    title: 'Редактор сценариев: построение диалогов',
    description: 'Как работает визуальный редактор, все типы узлов, соединение блоков, публикация сценария',
    icon: '🔀',
    updatedAt: '2026-04-28',
    blocks: [
      {
        type: 'paragraph',
        text: 'Редактор сценариев — визуальный конструктор диалогов бота. Вы строите дерево из блоков (узлов), соединяете их стрелками, и бот проводит пользователя по этому пути: отправляет сообщения, задаёт вопросы, выдаёт коды и принимает заявки.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-flow-builder.png',
        alt: 'Редактор сценариев',
        caption: 'Визуальный редактор с узлами на холсте, панелью типов слева и настройками справа',
      },

      { type: 'heading', text: 'Интерфейс редактора', level: 2 },
      {
        type: 'table',
        headers: ['Область', 'Назначение'],
        rows: [
          ['Левая панель', 'Список типов узлов — перетащите на холст чтобы добавить'],
          ['Холст (центр)', 'Рабочая область: узлы, соединения, навигация по сценарию'],
          ['Правая панель', 'Настройки выбранного узла — текст, кнопки, медиа и другие параметры'],
          ['Верхняя панель', 'Кнопки «Сохранить черновик» и «Опубликовать»'],
        ],
      },
      {
        type: 'list',
        items: [
          '**Масштаб** — колесо мыши (от 30% до 200%)',
          '**Перемещение по холсту** — Alt + зажатая левая кнопка мыши, или колёсико нажатием',
          '**Выделить узел** — кликнуть по нему (откроются настройки в правой панели)',
          '**Удалить узел** — выделить и нажать Delete или кнопку корзины в правой панели',
          '**Удалить соединение** — кликнуть по стрелке между узлами',
        ],
      },

      { type: 'heading', text: 'Типы узлов', level: 2 },
      {
        type: 'paragraph',
        text: 'Каждый узел выполняет одно конкретное действие. Цвет рамки узла указывает на его тип:',
      },
      {
        type: 'table',
        headers: ['Узел', 'Цвет', 'Что делает'],
        rows: [
          ['Старт', '🟢 Зелёный', 'Точка входа. Выполняется при /start или при переходе из другого сценария. Один на сценарий.'],
          ['Сообщение', '🔵 Синий', 'Отправляет пользователю текст, медиафайл и/или кнопки. Основной рабочий блок.'],
          ['Ожидание текста', '🟣 Фиолетовый', 'Ждёт текстовый ввод от пользователя и сохраняет его.'],
          ['Ожидание медиа', '🟣 Фиолетовый', 'Ждёт фото, видео или документ от пользователя.'],
          ['Ожидание контакта', '🟣 Фиолетовый', 'Просит пользователя поделиться номером телефона через кнопку в Telegram.'],
          ['Тикет поддержки', '🟠 Оранжевый', 'Принимает сообщение (текст + медиа) и создаёт тикет для оператора.'],
          ['Задержка', '⚫ Серый', 'Пауза в N секунд перед переходом к следующему узлу.'],
          ['Проверка подписки', '🔵 Голубой', 'Проверяет, подписан ли пользователь на указанный Telegram-канал. Ветвится: подписан / не подписан.'],
          ['Установить тег', '🟢 Зелёный', 'Присваивает пользователю один или несколько тегов (используются в рассылках).'],
          ['Промокод', '🟠 Оранжевый', 'Выдаёт уникальный промокод из указанного пула и показывает его пользователю.'],
          ['Код розыгрыша', '🩷 Розовый', 'Генерирует уникальный код участника розыгрыша и создаёт запись в таблице розыгрышей.'],
          ['Конец', '⚫ Серый', 'Завершает сценарий. После этого узла пользователь переходит в ожидание.'],
        ],
      },

      { type: 'heading', text: 'Узел «Сообщение» — подробно', level: 2 },
      {
        type: 'paragraph',
        text: 'Самый часто используемый узел. Позволяет отправить текст с форматированием, прикрепить медиафайл и добавить кнопки.',
      },
      {
        type: 'image',
        src: '/screenshots/tg-node-message.png',
        alt: 'Настройки узла Сообщение',
        caption: 'Правая панель с настройками текста, медиа и кнопок',
      },
      {
        type: 'table',
        headers: ['Параметр', 'Описание'],
        rows: [
          ['Текст', 'Текст сообщения. Поддерживает **жирный** (`**текст**`), `моноширинный` (`` `код` ``) и [кликабельный] (`[текст]`) форматы.'],
          ['Медиа', 'URL фото, видео, GIF или документа. Прикрепляется к сообщению.'],
          ['Задержка (сек)', 'Пауза перед отправкой этого сообщения (имитирует «печатает...»).'],
          ['Кнопки', 'Inline-кнопки под сообщением. Каждая кнопка: текст, куда ведёт, и опционально URL.'],
        ],
      },
      {
        type: 'note',
        text: 'Если добавить кнопки, сценарий остановится на этом узле и будет ждать нажатия. Без кнопок — автоматически перейдёт к следующему узлу.',
        variant: 'info',
      },

      { type: 'heading', text: 'Узлы ожидания — подробно', level: 2 },
      {
        type: 'paragraph',
        text: 'Узлы «Ожидание текста», «Ожидание медиа» и «Ожидание контакта» останавливают выполнение сценария и ждут ввода от пользователя.',
      },
      {
        type: 'table',
        headers: ['Параметр', 'Описание'],
        rows: [
          ['Ключ ответа', 'Имя переменной, в которую сохраняется ответ пользователя (например: «phone», «name»)'],
          ['Требует модерации', 'Если включено — создаётся заявка, сценарий ждёт решения оператора'],
          ['Ветка «одобрено»', 'Куда перейти, если модератор одобрил заявку'],
          ['Ветка «отклонено»', 'Куда перейти, если модератор отклонил заявку'],
        ],
      },
      {
        type: 'note',
        text: 'Без включённой модерации ответ пользователя сохраняется автоматически и сценарий сразу переходит дальше.',
        variant: 'tip',
      },

      { type: 'heading', text: 'Узел «Проверка подписки» — подробно', level: 2 },
      {
        type: 'paragraph',
        text: 'Этот узел проверяет, подписан ли пользователь на указанный Telegram-канал. Используется для закрытых активностей — «подпишись и участвуй».',
      },
      {
        type: 'list',
        items: [
          'Укажите username канала (например `@mychannel`) в настройках узла',
          'Ветка «Подписан» — куда перейти, если пользователь подписан',
          'Ветка «Не подписан» — можно показать сообщение с ссылкой на канал и снова проверить',
          'Бот должен быть администратором канала для проверки подписок',
        ],
      },

      { type: 'heading', text: 'Узел «Промокод» — подробно', level: 2 },
      {
        type: 'paragraph',
        text: 'Автоматически берёт первый доступный код из выбранного пула и отправляет его пользователю.',
      },
      {
        type: 'list',
        items: [
          'Выберите один или несколько пулов промокодов в настройках узла',
          'В тексте сообщения используйте плейсхолдер `{{promo_code}}` — он заменится реальным кодом',
          'После выдачи код помечается как использованный и больше не выдаётся (при лимите 1 использование)',
          'Если кодов в пуле нет — пользователь получит сообщение о том, что коды закончились',
        ],
      },

      { type: 'heading', text: 'Как соединять узлы', level: 2 },
      {
        type: 'paragraph',
        text: 'Узлы соединяются стрелками — они определяют, куда переходит пользователь после каждого шага.',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Наведите на узел-источник',
            description: 'На правом крае узла появится маленький кружок (порт выхода).',
          },
          {
            title: 'Потяните за порт',
            description: 'Зажмите левую кнопку мыши и потяните — появится линия соединения.',
          },
          {
            title: 'Отпустите на узле-цели',
            description: 'Наведите на следующий узел и отпустите. Соединение создано.',
          },
          {
            title: 'Удаление соединения',
            description: 'Кликните по стрелке соединения — она подсветится. Нажмите Delete или кнопку удаления.',
          },
        ],
      },
      {
        type: 'note',
        text: 'Некоторые узлы имеют несколько выходов (например «Проверка подписки» — «Подписан» и «Не подписан»). У каждого выхода свой порт.',
        variant: 'info',
      },

      { type: 'heading', text: 'Пример простого сценария', level: 2 },
      {
        type: 'paragraph',
        text: 'Вот пример типичного сценария «Подпишись и получи промокод»:',
      },
      {
        type: 'steps',
        steps: [
          {
            title: 'Старт',
            description: 'Пользователь отправил /start',
          },
          {
            title: 'Сообщение',
            description: '«Привет! Подпишись на наш канал @mychannel и получи промокод на скидку 20%»',
          },
          {
            title: 'Проверка подписки',
            description: 'Проверяем подписку на @mychannel. Если не подписан → возвращаемся к сообщению выше.',
          },
          {
            title: 'Промокод',
            description: 'Пользователь подписан → выдаём код из пула. Сообщение: «Ваш промокод: {{promo_code}}»',
          },
          {
            title: 'Конец',
            description: 'Сценарий завершён.',
          },
        ],
      },

      { type: 'heading', text: 'Сохранение и публикация', level: 2 },
      {
        type: 'table',
        headers: ['Действие', 'Что происходит'],
        rows: [
          ['Сохранить черновик', 'Изменения сохраняются, но сценарий для пользователей не меняется'],
          ['Опубликовать', 'Черновик становится активной версией — все новые пользователи пойдут по новому пути'],
        ],
      },
      {
        type: 'note',
        text: 'Пользователи, уже находящиеся в середине сценария, после публикации переключатся на новую версию при следующем сообщении. Не публикуйте незаконченные сценарии.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Часто задаваемые вопросы', level: 2 },
      {
        type: 'table',
        headers: ['Вопрос', 'Ответ'],
        rows: [
          ['Можно ли иметь несколько сценариев для одного бота?', 'Да. Но только один может быть «По умолчанию» (запускается на /start). Остальные можно вызвать из сценария переходом.'],
          ['Пользователь случайно застрял — как сбросить прогресс?', 'Найдите пользователя во вкладке «Пользователи» и удалите его запись. После этого /start начнёт сценарий с нуля.'],
          ['Как передать данные из одного узла в другой?', 'Ответы пользователя сохраняются по ключу. Используйте плейсхолдер {{ключ}} в тексте последующих сообщений.'],
          ['Сколько узлов можно добавить в сценарий?', 'Технических ограничений нет, но рекомендуется не превышать 50–100 узлов для удобства редактирования.'],
          ['Почему кнопка «Опубликовать» неактивна?', 'Убедитесь, что в сценарии есть хотя бы один узел «Старт» и все соединения замкнуты.'],
        ],
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────────
  // ADMIN
  // ─────────────────────────────────────────────────────────────────────────────
  {
    slug: 'settings-sales-wb',
    category: 'admin',
    title: 'Настройки → Продажи · WB',
    description: 'Кабинеты WB и токены, налоги кабинета, ручные синхронизации, план ГПР, себестоимость, ОПиУ — всё, что раньше жило во вкладках отдела',
    icon: '🔑',
    updatedAt: '2026-08-15',
    blocks: [
      {
        type: 'paragraph',
        text: 'Раздел открывается из меню профиля → «Настройки» → пункт «Продажи · WB» в списке слева. Он появился потому, что во вкладках отдела «Продажи» скрыли недоделанные разделы («Оцифровка», «Сверка и чек-листы», «Финансы», «Товары» и часть подвкладок Склада), а вместе с ними из навигации пропал единственный вход в служебные экраны WB-Аналитики: кабинеты с API-токенами, налоги, расписание синхронизации, себестоимость, расходы. Теперь они собраны здесь.',
      },
      {
        type: 'note',
        text: 'ВЕСЬ раздел «Настройки» доступен только администратору платформы — и в интерфейсе, и на сервере. Раньше расходилось: кнопки прятали, а эндпоинты оставались открытыми праву модуля, поэтому руководитель отдела мог прямым запросом завести кабинет, подменить API-токен WB или сменить налоговую модель. Теперь кабинеты, токены, налоги, расписания синхронизаций, план ГПР и настройки отзывов требуют роль администратора на бэкенде. Синхронизация данных и публикация ответов остались рабочими операциями сотрудников.',
        variant: 'warning',
      },
      { type: 'heading', text: 'Три группы разделов', level: 2 },
      {
        type: 'table',
        headers: ['Группа', 'Что внутри'],
        rows: [
          ['Подключение и данные', '«Налоги и параметры кабинетов» — СНО, ставка налога и НДС по каждому кабинету плюс его активность. Эквайринг руками не задаётся: с 05.08.2026 берётся фактической суммой из финансового отчёта WB (за свежие дни, пока отчёт не доехал, оценивается по ставке за 90 дней). Владелец и API-токены — в справочнике «Клиенты, кабинеты и токены». Расписание ночного автосинка (включён, час, глубина, тянуть ли рекламу) переехало в «Настройки модулей → Синхронизации»: это настройка, а не повседневная кнопка. Здесь же меню «Данные» (ручные синхронизации и их отмена) и «Справочники» (автозаполнение комиссий WB и логистики МП). Рядом — «План ГПР на месяц». Настройки отзывов сюда НЕ входят: это настройка модуля, а не отдела, и она живёт в разделе «Настройки модулей».'],
          ['Финансы и себестоимость', '«Себестоимость и юнит-экономика» — один экран: ввод себестоимости и юнит-затрат, импорт XLS, архивация и тут же расчёт маржи с ROI на единицу (07.08.2026 две вкладки объединены — они правили одни и те же поля); «Прибыль по артикулам» — тот же отчёт о прибылях, но в разрезе SKU; ОПиУ — единственный отчёт о прибылях по месяцам с 13.08.2026 (прежний отчёт о прибылях снят, статьи и ручные расходы переехали сюда), «Реклама» — кампании WB с конверсиями, и «Рука на пульсе» — показатели по дням (последние два до 07.08.2026 существовали, но открыть их было нельзя: в меню модуля на них не было ни одной ссылки). Разделов «Расходы по месяцам» и «Цены и скидки» больше нет (06.08.2026): фотосъёмка, фулфилмент и прочие расходы месяца вводятся в ОПиУ — там же, где остальные, и сразу видно, куда сумма попадает. Раздачи и внешний трафик с 08.08.2026 ведутся в отдельном разделе «Раздачи и внешний трафик» — по товару и дате: только так они попадают в «Цену заказа» и «Цену корзины» чек-листа за нужный день. Месяц, где заведены такие строки, вытесняет одноимённую ручную строку ОПиУ, поэтому дважды деньги не считаются. Цены ведутся в кабинете WB, в портал они приезжают ночной синхронизацией и участвуют в юнит-экономике. Последний помечен «в доработке»: он лежит здесь временно, пока дорабатывается, и потом вернётся в отдел «Продажи».'],
          ['Товары и планы продаж', '«Сводный чек-лист» и «Рука на пульсе» (раздел «Все товары» снят 13.08.2026 — архивация артикулов и их показатели живут в «Себестоимости и юнит-экономике»). Чек-лист с 08.08.2026 один: открывается сводной таблицей по всем артикулам, а нажатие на строку разворачивает разбор этого товара по дням. Сам разбор пересобран: карточка товара с главными цифрами и предельным ДРР (21.08.2026 потолки цены клика, корзины и заказа убраны: отдел продаж ориентируется только на ДРР, эти цифры при необходимости считает подбор в юнит-экономике), свёрнутые секции «Локализация» и «Поисковые запросы», таблица по дням на общем стандарте с итогами за период. По умолчанию показан рабочий набор колонок, остальные блоки включаются кнопкой «Колонки», а колонки, пустые за весь период, скрываются сами. Выкупы и проценты выкупа считаются так: «Выкупов» — по заказам выбранного периода, «Выкуп % мес» — за 30 дней от конца периода (им прогнозируются заказы, статус которых ещё неизвестен, поэтому на нём построены «Выручка» и «Прибыль до ДРР»), «Выкуп % период» — по завершённым заказам самого периода. Проценты считаются только по завершённым заказам: товары в пути не учитываются, как и в самом WB. Рядом «Завершено» показывает, какая доля заказов уже получила статус, а «Отмен» и «Возвратов» — сколько заказов сорвалось до и после выкупа. Прогноз выручки и прибыли собран по формулам методики: заказы делятся на уже известные (выкупы и раздачи) и на те, чей статус ещё неясен — вторые домножаются на «% выкупа мес». Раздачи и внешний трафик вычитаются из прибыли с ДРР, поэтому их суммы видно отдельной колонкой. У товаров без закупочной цены прибыль и маржа не показываются вовсе — иначе расходы считались бы нулевыми и товар выглядел бы стопроцентно прибыльным. Над таблицей — быстрые отборы со счётчиками: «Убыточные», «Кончается запас», «Реклама не окупается», «Локализация ниже нормы». В разборе есть график по дням: клики, заказы, выкупы, ДРР, цена заказа, остаток и расход на рекламу — любые из них линиями, хоть все сразу. Так видно, оплачен всплеск рекламой или пришёл сам. Показателей можно включить несколько сразу; они разного масштаба, поэтому линии приводятся к общей шкале 0–100, а настоящие значения показываются в подсказке. Нажатие на график раскрывает день целиком — воронка, реклама, заказы, выкупы, цена и прибыль в одной панели поверх графика, как расшифровка месяца в ОПиУ; стрелки листают соседние дни, Esc закрывает. Дни, выбивающиеся из периода, помечены в таблице стрелкой с подсказкой, во сколько раз они отличаются от обычного. Период задаётся в одном месте — календарём в шапке экрана, разбор берёт его же. Стрелки рядом с выбором товара листают отобранные позиции подряд, возвращаться в таблицу не нужно. Выбранный отбор и линия графика запоминаются до следующего захода. Раньше это были два раздела с почти одинаковыми названиями, и чтобы разобрать товар, приходилось уходить в соседний экран и искать его в выпадающем списке. «Остатки» и «Хватит на» показываются НА КОНЕЦ выбранного периода — по суточному снимку склада, а не по сегодняшнему: сравнивать прошлый месяц с нынешним складом бессмысленно. «Хватит на» считается так: итого в остатках (склад плюс план возврата: то, что едет обратно, и та часть едущего к клиентам, которую не выкупят) делится на скорость заказов за последнюю неделю периода и на процент выкупа. ДРР считается от СУММЫ ЗАКАЗОВ — «всегда от того, на что можем повлиять в рекламе», — и в него входят раздачи и внешний трафик; так же он считается и в разборе по дням. «Потенциал поставок» показывает, на сколько процентов вырастут заказы, если довести локализацию до 70 %: при локализации 25 % это +180 %, а не «45 пунктов недобрано», как считалось раньше. «Запас, ₽» в разборе по дням считается по закупочной цене, а не по цене продажи — это стоимость товара на складе, а не выручка, которую он принесёт.'],
          ['Проверка данных', 'Воронка продаж и платное хранение — сырьё, из которого считаются «Отчёт», чек-листы и ОПиУ. Сюда смотрят, когда цифры вызывают вопросы.'],
        ],
      },
      {
        type: 'note',
        text: 'Выбор кабинета, шестерёнка его настроек, меню «Данные» и «Справочники» есть в ЛЮБОМ разделе — это общая шапка модуля. То есть запустить синхронизацию можно, не возвращаясь в «Кабинеты».',
        variant: 'info',
      },
      { type: 'heading', text: 'Себестоимость и юнит-экономика', level: 2 },
      {
        type: 'paragraph',
        text: 'Строка таблицы — артикул продавца с цветом. Размеры разворачиваются под ним стрелкой слева: у каждого свои продажи и остаток, а поля ручного ввода (себестоимость, маркировка, приёмка, прочие расходы) хранятся на артикуле и правятся прямо в его строке. Клик по названию открывает карточку товара, значок архива справа убирает артикул из всех отчётов.',
      },
      {
        type: 'list',
        items: [
          'Колонки собраны в цветные группы — «Цена и СПП», «Затраты на единицу», «Без рекламы», «С рекламой». Группу можно свернуть, перекрасить, а колонки внутри — перетащить или закрепить слева. Порядок запоминается: в следующий раз таблица откроется такой, какой вы её оставили.',
          'В заголовках — сортировка и фильтры (воронка): по категории, меткам, цене, продажам, остатку, оборачиваемости, прибыли, марже и ROI. Внизу строка «Итого» по продажам, возвратам, выручке, перечислениям и остатку — она считается по тому, что осталось после фильтров.',
          'В группе «Склад» стоят две колонки остатка, и это разные вещи. «Остаток, шт» — учётный: всё, что Wildberries числит за нами, включая товар на сгоревших складах (мы его не списываем — официального акта нет, часть позиций возвращается в продажу). «Доступно, шт» — то же самое за вычетом складов, выключенных на вкладке «Остатки по складам». Оборачиваемость и хранение считаются по ДОСТУПНОМУ остатку: то, что нельзя продать, не должно улучшать оборачиваемость. Красная точка у остатка означает, что часть товара лежит на исключённых складах — наведите курсор, чтобы увидеть сколько. Разрез по складам ведётся с 3 августа 2026; если его нет, в колонке стоит «нет данных» и расчёт идёт по учётному остатку.',
          'Поля, которые заполняются вручную (себестоимость, маркировка, приёмка за литр, коррекция комиссии, процент выкупа ниши, прочие расходы), залиты лёгким тоном — их видно сразу, без наведения курсора. У строк размеров такой заливки нет: эти значения хранятся на артикуле и правятся в его строке.',
          'Цвет в таблице — сигнал, а не оформление. Красным и зелёным выделены только те значения, которые сами по себе хорошие или плохие: знак прибыли, маржи и ROI, запас до точки нуля, предельный ДРР против ДРР кабинета, нулевой остаток. Затраты, ставки и поступления печатаются обычным цветом.',
          'Метка ассортимента стоит на артикуле целиком: «Ликвид» — есть остаток и продажи за 90 дней, «Неликвид» — остаток есть, а продаж нет, «Действующие» и «Недействующие» — про продажи за 30 дней. Если завис только край размерного ряда, размеры перечислены в подсказке к метке.',
          'Период берётся из календаря в шапке экрана — того же, что и на остальных экранах раздела. Кнопка «Колонки» добавляет блоки: рекламные конверсии за 14 дней, поисковую аналитику за 7 дней, дополнительные показатели и подбор цены под желаемую маржу или ДРР. Кнопка «Цели» задаёт целевую маржу и желаемый ДРР — под них появляются свои блоки колонок, и там же выбирается, как считать блок «С рекламой»: по факту (списания WB) или по цели (что будет при желаемом ДРР — такие значения помечаются знаком ≈). Реклама на заказ всегда дешевле рекламы на проданную штуку: часть заказов не выкупают, и их реклама ложится на те, что доехали. Переключатель «Активные / Архив / Все» стоит в шапке, под выбором периода.',
        ],
      },
      { type: 'heading', text: 'План ГПР на месяц', level: 2 },
      {
        type: 'paragraph',
        text: 'Раньше плана не было ни на одном экране: файл правили на сервере руками. Теперь это таблица — строки-показатели (заказы ₽ и шт, продажи ₽ и шт, маржа %), колонки-кабинеты и «Все кабинеты». Именно с этими цифрами сверяются светофоры вкладки «Отчёт» и первого ряда карточек дашборда: «план 236.8 млн ₽», «не хватает 74 млн ₽ до плана».',
      },
      {
        type: 'list',
        items: [
          'Пустая ячейка — это «плана нет»: карточка честно напишет «ориентира нет». Ноль читается так же — дашборд не отличает нулевой план от незаданного, поэтому заполнять нулями нечего. Десятичный разделитель — запятая или точка (12,16 и 12.16 равнозначны).',
          'Колонка «Все кабинеты» появляется, только когда показатель заполнен у ВСЕХ кабинетов — иначе процент выполнения был бы занижен на кабинет без плана.',
          'Месяц выбирается вверху: доступны 12 прошлых и 6 будущих — план на следующий месяц можно задать заранее, а прошлый поправить задним числом. Несохранённые правки при смене месяца сбрасываются: иначе июльские цифры молча уехали бы в августовский план.',
          'Заполняйте строку кабинета целиком: если часть показателей пустая, вкладка «Отчёт» подставит вместо них ноль. Редактор предупредит об этом жёлтой плашкой.',
        ],
      },
      {
        type: 'note',
        text: 'Маржа % в колонке «Все кабинеты» — взвешенная по плановым продажам, а не среднее ставок: у кабинетов разный объём. Так же её считают вкладка «Отчёт» и дашборд — цифра плана везде одна.',
        variant: 'info',
      },
    ],
  },

  {
    slug: 'user-management',
    category: 'admin',
    title: 'Управление пользователями и правами',
    description: 'Создание сотрудников, роли, матрица прав доступа, право «Реальная себестоимость», назначение проектов таск-менеджера',
    icon: '👥',
    updatedAt: '2026-08-10',
    blocks: [
      {
        type: 'paragraph',
        text: 'Раздел управления пользователями доступен только администраторам платформы и открывается из меню профиля (карточка с именем внизу боковой панели). Здесь создаются учётные записи сотрудников, назначаются роли и настраиваются права доступа по вкладкам отделов.',
      },
      {
        type: 'note',
        text: 'Если вы видите страницу пользователей, значит у вас есть право «Управление пользователями». Если нет — обратитесь к действующему администратору.',
        variant: 'info',
      },
      {
        type: 'image',
        src: '/screenshots/user-management.png',
        alt: 'Страница управления пользователями',
        caption: 'Список сотрудников с ролями и кнопкой добавления нового пользователя',
      },

      { type: 'heading', text: 'Роли пользователей', level: 2 },
      {
        type: 'table',
        headers: ['Роль (код)', 'Название', 'Что включает'],
        rows: [
          ['employee', 'Сотрудник', 'Базовая роль. Видит только те вкладки, которые ему явно отмечены.'],
          ['department_head', 'Руководитель отдела', 'Та же выдача по вкладкам — роль сама по себе доступа не добавляет.'],
          ['platform_admin', 'Администратор платформы', 'Полный доступ ко всем разделам и настройкам портала в обход прав.'],
        ],
      },
      {
        type: 'note',
        text: 'Роль больше не раздаёт права заранее: новый сотрудник заводится пустым, и вкладки ему отмечает администратор. Раньше портал наливал каждому набор разделов по шаблону роли — из-за этого у людей оставался доступ к данным разделов, которых они в интерфейсе даже не видели.',
        variant: 'info',
      },
      {
        type: 'note',
        text: 'Роль «Администратор платформы» автоматически даёт доступ ко всем модулям без дополнительных настроек прав. Назначайте эту роль только проверенным сотрудникам.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Создание нового сотрудника', level: 2 },
      {
        type: 'steps',
        steps: [
          {
            title: 'Перейдите в раздел «Пользователи»',
            description: 'Ссылка доступна в боковой панели для администраторов.',
          },
          {
            title: 'Нажмите «Добавить пользователя»',
            description: 'Откроется форма создания нового сотрудника.',
          },
          {
            title: 'Заполните основные данные',
            description: 'Укажите email, полное имя и роль. Email будет использоваться для входа в систему.',
          },
          {
            title: 'Задайте пароль',
            description: 'Введите временный пароль для первого входа. Сотрудник сможет сменить его в настройках.',
          },
          {
            title: 'Настройте права доступа',
            description: 'В блоке «Права» отметьте вкладки отделов, доступные сотруднику, и уровень (просмотр / редактирование / удаление). Тумблер отдела включает все его вкладки разом.',
          },
          {
            title: 'Назначьте проекты таск-менеджера (опционально)',
            description: 'Если сотруднику нужен доступ к конкретным проектам в таск-менеджере — выберите их в блоке «Проекты ТМ». При единственном проекте сотрудник будет автоматически попадать в него при открытии таск-менеджера.',
          },
          {
            title: 'Сохраните',
            description: 'Нажмите «Создать». Сотрудник появится в списке и сможет войти в систему с указанными данными.',
          },
        ],
      },

      { type: 'heading', text: 'Поля карточки пользователя', level: 2 },
      {
        type: 'table',
        headers: ['Поле', 'Описание', 'Обязательное?'],
        rows: [
          ['Email', 'Логин для входа в систему. Должен быть уникальным.', 'Да'],
          ['Полное имя', 'Отображаемое имя в интерфейсе и в отчётах', 'Да'],
          ['Роль', 'employee / department_head / platform_admin', 'Да'],
          ['Отдел', 'Подразделение сотрудника (влияет на отображение в структуре)', 'Нет'],
          ['Пароль', 'При создании — задаётся администратором. При редактировании — только если нужно сменить.', 'При создании: Да'],
          ['Права доступа', 'Дерево вкладок отделов с уровнями просмотр/редактирование/удаление', 'Нет'],
          ['Проекты ТМ', 'Список проектов таск-менеджера, доступных сотруднику', 'Нет'],
          ['Активен', 'Включить/выключить учётную запись без удаления', 'Да'],
        ],
      },

      { type: 'heading', text: 'Права по вкладкам отделов', level: 2 },
      {
        type: 'paragraph',
        text: 'Права выдаются не на модули целиком, а на **вкладки отделов** — дерево «отдел → верхняя вкладка → подвкладка» с раскрытием. На каждую вкладку назначается уровень доступа. Отсутствие права означает, что вкладка скрыта из навигации и недоступна пользователю.',
      },
      {
        type: 'table',
        headers: ['Уровень', 'Что даёт'],
        rows: [
          ['Просмотр (view)', 'Вкладка видна, данные только для чтения'],
          ['Редактирование (edit)', 'Можно менять данные на вкладке — включая то, что раньше было отдельными правами: публиковать (например, ответы на отзывы), настраивать раздел и создавать записи'],
          ['Удаление (delete)', 'Можно удалять записи'],
        ],
      },
      {
        type: 'list',
        items: [
          'Тумблер на уровне отдела включает/выключает доступ ко всем его вкладкам разом',
          'Подвкладке нужно своё право: отметка на верхней вкладке ставит галочки детям каскадом, но доступ проверяется по каждой отдельно',
          'Роль **«Администратор платформы»** видит всё без настройки прав',
        ],
      },

      { type: 'heading', text: 'Особое право: «Реальная себестоимость»', level: 2 },
      {
        type: 'paragraph',
        text: 'В группе прав **«Системные»** есть отдельное право **«Реальная себестоимость»**. Оно управляет тем, какую себестоимость сотрудник видит в расчётах маржи и прибыли по всему порталу (Продажи, ОПиУ, Планирование и др.).',
      },
      {
        type: 'table',
        headers: ['Себестоимость', 'Кто видит', 'Источник'],
        rows: [
          ['Публичная (по умолчанию)', 'Все, у кого есть доступ к отчёту', '«Как в кабинете WB» (данные синхронизации)'],
          ['Реальная', 'Только с правом «Реальная себестоимость» + роль «Администратор платформы»', 'Истинная закупка владельца'],
        ],
      },
      {
        type: 'paragraph',
        text: 'У кого право выдано — в шапке cost-отчётов появляется тумблер **«Реальная себестоимость»**: выключен — публичная (по умолчанию), включён — реальная. Настройка запоминается по кабинету. У кого права нет — тумблера нет, и реальная себестоимость не отдаётся сервером ни при каких параметрах запроса (защита на уровне данных, а не только интерфейса).',
      },
      {
        type: 'note',
        text: 'Отделу продаж право «Реальная себестоимость» обычно НЕ выдают — они работают на публичной себестоимости. Реальную оставляют владельцам и поимённо отмеченным сотрудникам.',
        variant: 'info',
      },

      { type: 'heading', text: 'Деактивация и удаление', level: 2 },
      {
        type: 'paragraph',
        text: 'Если сотрудник уволился или временно не должен иметь доступ — деактивируйте его учётную запись (переключатель «Активен» → выкл.). Это сохраняет всю историю задач и данных, но блокирует вход.',
      },
      {
        type: 'paragraph',
        text: 'Полное удаление убирает запись из системы безвозвратно. Используйте только если уверены, что данные сотрудника больше не нужны.',
      },
      {
        type: 'note',
        text: 'Нельзя удалить самого себя или единственного активного администратора платформы — система выдаст предупреждение.',
        variant: 'warning',
      },

      { type: 'heading', text: 'Часто задаваемые вопросы', level: 2 },
      {
        type: 'table',
        headers: ['Вопрос', 'Ответ'],
        rows: [
          ['Сотрудник не видит нужный модуль', 'Убедитесь, что в его профиле добавлено право view для этого модуля.'],
          ['Сотрудник видит модуль, но не может ничего редактировать', 'Добавьте право manage или edit для этого модуля.'],
          ['Как сбросить пароль сотруднику?', 'Откройте редактирование пользователя и введите новый пароль в поле «Новый пароль».'],
          ['Как назначить сотруднику только один проект в ТМ?', 'В блоке «Проекты ТМ» выберите нужный проект. При входе в таск-менеджер он автоматически попадёт в этот проект.'],
          ['Сотрудник уволился — что делать?', 'Деактивируйте учётную запись (переключатель «Активен»). Удалять не рекомендуется — чтобы сохранить историю задач.'],
        ],
      },
    ],
  },
  {
    slug: 'digitization-dashboard',
    category: 'wb-analytics',
    title: '«Оцифровка», РНП и детали по SKU',
    description: 'Плитки-метрики оперативного дашборда, разбивка метрики по артикулам, период по умолчанию, тумблер реальной себестоимости',
    icon: '📟',
    updatedAt: '2026-07-29',
    blocks: [
      { type: 'note', variant: 'warning', text: 'Раздел «Оцифровка» временно скрыт в отделе «Продажи» (не доработан) — вкладки в интерфейсе нет. Статья сохранена как описание методики: вернём раздел, когда доведём.' },
      {
        type: 'paragraph',
        text: 'Раздел **«Оцифровка»** — оперативный дашборд юнит-экономики: вкладки **Оцифровка** (плитки-метрики за период), **РНП** («Рука на пульсе» — метрики × дни), **ОПиУ** и **Реклама**. По умолчанию открывается за **текущий месяц** (с 1-го числа по сегодня).',
      },
      { type: 'heading', text: 'Плитки «Оцифровки»', level: 2 },
      {
        type: 'paragraph',
        text: 'Каждая плитка — итог метрики за период: Чистая прибыль, Реализация, Сумма продаж, Сумма заказов, Выкуп %, Комиссия, Логистика, Хранение, Реклама/ДРР, Налог, Себестоимость, ROI, Возвраты, Прибыль/Логистика на 1 шт, Штрафы, Удержания и др. Наведите на «?» у названия — подсказка объясняет, как метрика считается.',
      },
      { type: 'heading', text: 'Детали по SKU (клик по плитке)', level: 2 },
      {
        type: 'paragraph',
        text: 'Клик по плитке открывает попап **«X — детали»**: разбивку ИМЕННО этой метрики по артикулам. Сумма значений по SKU равна итогу плитки. Для денежных метрик показывается ₽ и доля SKU в метрике (или ₽ и штуки для Реализации/Продаж/Заказов).',
      },
      {
        type: 'note',
        text: 'Часть метрик считается на уровне кабинета (Хранение, Штрафы, Удержания, Налог, Реклама) или является производной (ROI, на 1 шт, Капитализация, Потенц. прибыль, Выкуп %) — по SKU они не раскрываются, попап покажет пояснение. Чистая прибыль по SKU: прямые расходы минус кабинет-расходы, распределённые по доле реализации артикула.',
        variant: 'info',
      },
      { type: 'heading', text: 'Реальная себестоимость', level: 2 },
      {
        type: 'paragraph',
        text: 'Если у вас есть право «Реальная себестоимость», в шапке появляется тумблер: выключен — публичная себестоимость (как в кабинете WB), включён — реальная (закупка владельца). Влияет на маржу, прибыль и все производные показатели. Подробнее — в статье «Управление пользователями и правами».',
      },
    ],
  },
]
